// The new sale page (`/sell`): an address typed, and the office's own lookups
// answering it while the person watches — the property, its zones, overlays
// and bushfire question, the owners corporation, the conveyancer, the
// searches. Then their details, and a matter opened for staff to review.
//
// **Everything shown is the middleware's.** Which property, what the plan
// says about owners corporations, which searches are queued, who the
// conveyancer is and how long it takes all arrive from `/sale/*` and are
// drawn here, never worked out. See the middleware's docs/decisions/new-sale.md.
//
// **Every step is a real answer.** A step ticks when its request has come
// back and says what came back; one that could not be checked says so, and
// "could not check" is never drawn as "no" — a page telling a vendor their
// land is not bushfire prone because a query failed is a false statement
// about their land.
//
// **Live where the work is.** The planning check asks dozens of map layers,
// so it is streamed (`{stream: true}` → one JSON object a line): the lot's
// outline is drawn the moment the parcel is found and the layer count climbs
// as they are asked. Every number on screen is a count of something done.
//
// **Afterwards, a link to follow it** (`/sell#t={ref}.{token}`): the matter's
// own record, re-read while the tab is open. The token rides in the fragment
// so it never reaches a server log or a referrer, and this device remembers
// the matters opened on it.
//
// Public: no token, no session, and nothing imported from App.js.

import { Fragment, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T, SELL_BRAND, MIDDLEWARE_URL, LOGO_BLACK_SRC } from "./shared";
import { paginate, fillPages, SHEET_CSS } from "./contractPages";

const saleFetch = (path, options = {}) => fetch(`${MIDDLEWARE_URL}${path}`, {
  ...options,
  headers: { "Content-Type": "application/json", ...(options.headers || {}) },
});

async function ask(path, options) {
  try {
    const res = await saleFetch(path, options);
    const body = await res.json().catch(() => null);
    return { status: res.status, body: body || { ok: false } };
  } catch (e) {
    return { status: 0, body: { ok: false, error: "We could not reach Provey just now. Please check your connection and try again." } };
  }
}

// One JSON object a line, handed over as each line completes. A line that
// does not parse is skipped rather than ending the stream.
export async function readNdjson(body, onLine) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let held = "";
  const emit = (text) => {
    const line = text.trim();
    if (!line) return;
    try { onLine(JSON.parse(line)); } catch (e) { /* a torn line is not an answer */ }
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    held += decoder.decode(value, { stream: true });
    const parts = held.split("\n");
    held = parts.pop();
    parts.forEach(emit);
  }
  emit(held + decoder.decode());
}

// The planning check, streamed where the browser can read a stream and asked
// plainly where it cannot. Resolves with the same shape `ask` does.
async function askPlanningLive(where, onLine) {
  try {
    const res = await saleFetch("/sale/planning", { method: "POST", body: JSON.stringify({ ...where, stream: true }) });
    const streamed = (res.headers.get("content-type") || "").includes("ndjson");
    if (!streamed || !res.body || typeof res.body.getReader !== "function") {
      return { status: res.status, body: await res.json().catch(() => ({ ok: false })) };
    }
    let result = null;
    await readNdjson(res.body, (line) => {
      if (line.type === "result") result = line;
      else onLine(line);
    });
    return { status: res.status, body: result || { ok: false } };
  } catch (e) {
    return ask("/sale/planning", { method: "POST", body: JSON.stringify(where) });
  }
}

// ── The pieces worth testing ────────────────────────────────────────────────

// The steps, in the order they are drawn, and which answer settles each.
export const STEPS = [
  { key: "property", title: "Finding your property", from: "property" },
  { key: "planning", title: "Checking zones and overlays", from: "planning" },
  { key: "bushfire", title: "Checking for a bushfire prone area", from: "planning" },
  { key: "oc", title: "Checking for an owners corporation", from: "property" },
  { key: "conveyancer", title: "Matching you with a conveyancer", from: "conveyancer" },
  { key: "searches", title: "Queueing your title and property searches", from: "property" },
  { key: "draft", title: "Setting up your file and drafting your first contract", from: "draft" },
];

// A line icon per check, drawn in its status ring: faint while it waits,
// circled by the spinner while it runs, and in the accent once it ticks.
const STEP_ICONS = {
  property: <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0z" /><circle cx="12" cy="10" r="3" /></>,
  planning: <><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></>,
  bushfire: <path d="M12 22c4 0 7-3 7-7 0-4-3-6-4-9-1 2-2 3-3 3 0-3-1-5-3-7 0 4-4 7-4 13 0 4 3 7 7 7z" />,
  oc: <><rect x="4" y="2" width="16" height="20" rx="1" /><path d="M9 22v-4h6v4M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" /></>,
  conveyancer: <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  searches: <><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></>,
  draft: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></>,
};

// What a step says once its answer is in. `null` while it is still running.
const STILL_SEARCHING = "Still searching — it goes on your contract when the state's map answers.";
export function stepResult(key, answers = {}) {
  const { property, planning, conveyancer } = answers;
  switch (key) {
    case "property":
      if (!property) return null;
      if (!property.ok) return { tone: "bad", line: property.error || "We could not find that property." };
      if (property.unlisted) return { tone: "flag", line: "Not on the map yet — your conveyancer confirms the land from your rates notice" };
      return { tone: "ok", line: [property.property.lots.join(", "), property.property.council && `${property.property.council} council`].filter(Boolean).join(" · ") };
    case "planning":
      if (!planning) return null;
      if (planning.unlisted) return { tone: "unknown", line: "Checked once your conveyancer confirms the land." };
      if (!planning.ok) return { tone: "unknown", line: "Could not be checked just now — your conveyancer will confirm it." };
      // The map was slow: the page moves on, and the answer still lands on the contract.
      if (planning.planning.stillSearching) return { tone: "unknown", line: STILL_SEARCHING };
      return {
        tone: "ok",
        line: [
          planning.planning.zones.length ? planning.planning.zones.join(", ") : "Zone to be confirmed",
          planning.planning.overlays.length ? `${planning.planning.overlays.length} overlay${planning.planning.overlays.length === 1 ? "" : "s"}` : (planning.planning.overlaysComplete ? "no overlays" : "overlays to be confirmed"),
        ].join(" · "),
      };
    case "bushfire":
      if (!planning) return null;
      if (planning.unlisted) return { tone: "unknown", line: "Checked once your conveyancer confirms the land." };
      if (planning.ok && planning.planning.stillSearching) return { tone: "unknown", line: STILL_SEARCHING };
      if (!planning.ok || planning.planning.bushfireProneArea === null) {
        return { tone: "unknown", line: "Could not be checked just now — your conveyancer will confirm it." };
      }
      return { tone: planning.planning.bushfireProneArea ? "flag" : "ok", line: planning.planning.bushfireLine };
    case "oc":
      if (!property) return null;
      if (!property.ok) return { tone: "unknown", line: "Waiting on the property." };
      return { tone: property.ownersCorporation.status === "likely" ? "flag" : "ok", line: property.ownersCorporation.line };
    case "conveyancer":
      if (!conveyancer) return null;
      if (!conveyancer.ok || !conveyancer.conveyancer) return { tone: "ok", line: "Our team will introduce your conveyancer shortly." };
      return { tone: "ok", line: `${conveyancer.conveyancer.name}${conveyancer.conveyancer.yearsExperience ? ` · ${conveyancer.conveyancer.yearsExperience} years' experience` : ""}` };
    case "searches":
      if (!property) return null;
      if (!property.ok) return { tone: "unknown", line: "Waiting on the property." };
      return { tone: "ok", line: `${property.searches.length} search${property.searches.length === 1 ? "" : "es"} ready for review` };
    case "draft":
      if (!answers.draft) return null;
      return answers.draft.ok
        ? { tone: "ok", line: "Your first draft is ready to look at below." }
        : { tone: "unknown", line: "Your conveyancer will prepare the first draft." };
    default:
      return null;
  }
}

// The body `/sale/start` takes: the picked property and what the person typed,
// nothing else. The honeypot rides along as typed.
export function startPayload(form = {}, pick = {}, conveyancerId = null) {
  const agent = form.role === "agent";
  return {
    address: pick.address || "",
    propertyPfi: pick.propertyPfi || "",
    ...(pick.unlisted ? { unlisted: true } : {}),
    role: form.role,
    name: (form.name || "").trim(),
    email: (form.email || "").trim(),
    phone: (form.phone || "").trim(),
    ...(agent ? { agency: (form.agency || "").trim() } : {}),
    ...(!agent && (form.agentEmail || "").trim() ? { agentEmail: form.agentEmail.trim() } : {}),
    ...(conveyancerId ? { conveyancerId } : {}),
    // An agent's or a helper's start is a proposal the vendor accepts: no tick.
    consent: form.role === "agent" || form.role === "other" || form.consent === true,
    website: form.website || "",
    // The page to come back to from Stripe.
    page: salePath() === "/sellv2" ? "sellv2" : "sell",
  };
}

// Dollars from the middleware's cents, as an Australian price.
export const money = (cents) => `$${(Math.round(cents || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// **Back from Stripe**: `?paid=<ref>` (or `?unpaid=`) names the matter, and
// this device's own saved link opens it — the link itself never went to
// Stripe. Another device has no saved link, and is told to use the email.
export function returnedFromStripe(search = "", tracked = [], paying = null) {
  const m = /[?&](paid|unpaid)=([A-Za-z0-9-]{1,40})/.exec(search);
  if (!m) return null;
  const t = (paying && paying.ref === m[2] && paying.token ? paying : null) || tracked.find(x => x.ref === m[2]);
  return { paid: m[1] === "paid", ref: m[2], track: t ? { ref: t.ref, token: t.token } : null };
}

// Who the visitor is meeting: the one they picked while that one may still be
// picked (the middleware's `available`, the next in line), else the
// allocation. The middleware decides again on start; this is only the face.
export function chosenConveyancer(answer, id) {
  if (!answer || !answer.ok) return null;
  const picked = id && (answer.options || []).find(o => o.id === id && o.available);
  return picked || answer.conveyancer || null;
}

// What a running step says while it works, from what has actually been heard
// so far — nothing here is a guess at how long is left.
export function runningLine(key, live = {}) {
  switch (key) {
    case "property":
      return "Searching Vicmap's property records…";
    case "planning": {
      const o = live.overlays;
      const zone = live.zones && live.zones.length ? `${live.zones.join(", ")} · ` : "";
      if (o && o.total) {
        const parcel = o.parcels > 1 ? ` (lot ${o.parcel} of ${o.parcels})` : "";
        return `${zone}checked ${o.done} of ${o.total} overlay layers${parcel}${o.found ? ` · ${o.found} found so far` : ""}`;
      }
      return live.shape ? "Lot found — asking the planning map…" : "Finding the lot on the map…";
    }
    case "bushfire":
      return live.bushfire ? live.bushfire.bushfireLine : "Asking the bushfire prone area map…";
    case "conveyancer":
      return "Looking at who has room for a new sale…";
    default:
      return "";
  }
}

// `#t={ref}.{token}`, or null. The ref is the matter's own and the token the
// middleware's (base64url, 32 characters); anything else is not a link.
export function trackFromHash(hash = "") {
  const m = String(hash).match(/^#t=([A-Za-z0-9-]{1,40})\.([A-Za-z0-9_-]{32})$/);
  return m ? { ref: m[1], token: m[2] } : null;
}

// `#lease={ref}.{token}` — the link a property manager is emailed to upload
// the lease (the middleware's `/sale/lease/:ref/:token`). Not a tracker: it
// opens nothing else on the matter.
export function leaseFromHash(hash = "") {
  const m = String(hash).match(/^#lease=([A-Za-z0-9-]{1,40})\.([A-Za-z0-9_-]{32})$/);
  return m ? { ref: m[1], token: m[2] } : null;
}

// Vicmap's addresses arrive in capitals; shown in title case, with the state
// and anything that is not a word (a unit like 2/10, a postcode) as it came.
const KEEP_UPPER = new Set(["VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"]);
export const displayAddress = (address = "") => (/[a-z]/.test(address) ? address
  : String(address).replace(/[A-Za-z][A-Za-z']*/g, (w) => (KEEP_UPPER.has(w) ? w
    : /\d/.test(w) ? w : w[0] + w.slice(1).toLowerCase()))
    .replace(/\b([OD])'([a-z])/g, (m, a, b) => `${a}'${b.toUpperCase()}`));

// Contracts the firm has prepared nearby — "recently", because the portal
// holds only its own matters, not the firm's history — said as one line — the street where
// there is one, the suburb as well where it adds to it. Null says nothing.
export function localLine(local) {
  if (!local) return null;
  const street = local.street && local.street.count ? local.street : null;
  const suburb = local.suburb && local.suburb.count ? local.suburb : null;
  const place = (n) => displayAddress(n.name);
  if (street) {
    const head = street.count === 1 ? `We've recently prepared a contract on ${place(street)}` : `We've recently prepared ${street.count} contracts on ${place(street)}`;
    return suburb && suburb.count > street.count ? `${head}, and ${suburb.count} in ${place(suburb)}` : head;
  }
  if (suburb && suburb.count >= 2) return `We've recently prepared ${suburb.count} contracts in ${place(suburb)}`;
  return suburb ? `We've recently prepared a contract in ${place(suburb)}` : null;
}

// A tracker link stays on the page it was made on (/sell or /sellv2).
const salePath = () => (typeof window !== "undefined" && /^\/sellv2(\/|$)/i.test(window.location.pathname) ? "/sellv2" : "/sell");
export const trackHref = (t) => `${salePath()}#t=${t.ref}.${t.token}`;

// The matters opened on this device, newest first. Only a convenience: a
// browser that will not store anything still has the link on screen.
const TRACK_STORE = "provey.sell.tracked";
export function readTracked() {
  try {
    const list = JSON.parse(localStorage.getItem(TRACK_STORE) || "[]");
    return Array.isArray(list) ? list.filter(t => trackFromHash(`#t=${t.ref}.${t.token}`)) : [];
  } catch (e) { return []; }
}
// The link of the matter being paid for, kept for this tab while it is on
// Stripe's page: the return address carries no token (the token never goes
// to Stripe), so this is how the tab finds its way back to the invoice —
// whatever link the visitor came in on.
const PAYING_STORE = "provey.sell.paying";
function rememberPaying(track) {
  try { sessionStorage.setItem(PAYING_STORE, JSON.stringify({ ref: track.ref, token: track.token })); } catch (e) { /* the emailed link still works */ }
}
export function readPaying() {
  try { const t = JSON.parse(sessionStorage.getItem(PAYING_STORE) || "null"); return t && trackFromHash(`#t=${t.ref}.${t.token}`) ? t : null; } catch (e) { return null; }
}
function rememberTracked(entry) {
  try {
    const rest = readTracked().filter(t => t.ref !== entry.ref);
    localStorage.setItem(TRACK_STORE, JSON.stringify([entry, ...rest].slice(0, 5)));
  } catch (e) { /* storage blocked — the link on screen still works */ }
}

// How long ago, for the live badge.
export function agoText(seconds) {
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${Math.floor(seconds / 10) * 10}s ago`;
  const m = Math.floor(seconds / 60);
  return m === 1 ? "1 min ago" : `${m} mins ago`;
}

// **When the matter last changed**, not when the page last asked: the page
// asks every few seconds, so the time since then only ever read "just now"
// or "10s ago" whatever had happened. The newest update on the timeline is
// the real thing — a search back, a question answered, the invoice paid.
export function updatedText(updates = [], now = Date.now()) {
  const times = (updates || []).map(u => Date.parse(u && u.at)).filter(t => Number.isFinite(t));
  if (!times.length) return "";
  const latest = Math.max(...times);
  const seconds = Math.max(0, Math.round((now - latest) / 1000));
  if (seconds < 60) return "updated just now";
  const m = Math.floor(seconds / 60);
  if (m < 60) return `updated ${m === 1 ? "1 min" : `${m} mins`} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `updated ${h === 1 ? "1 hr" : `${h} hrs`} ago`;
  return `updated ${new Date(latest).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`;
}

// ── Motion ──────────────────────────────────────────────────────────────────
//
// Everything that moves is decoration on a real answer — a number counting up
// to the figure the middleware sent, a card rising as it scrolls in — and
// every bit of it stands still for somebody who has asked for less motion.

// Desktop width, where the page opens into two columns once it has checked.
function useWide(min = 1100) {
  const query = `(min-width: ${min}px)`;
  const [wide, setWide] = useState(() => { try { return window.matchMedia(query).matches; } catch (e) { return false; } });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(query); } catch (e) { return undefined; }
    const on = () => setWide(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", on); else if (mq.addListener) mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else if (mq.removeListener) mq.removeListener(on); };
  }, [query]);
  return wide;
}

const prefersLessMotion = () => {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return true; }
};

// Example addresses typed into the empty box, one character at a time.
export const EXAMPLE_ADDRESSES = [
  "14 Elm Grove, Richmond 3121",
  "2/45 Beach Road, Sandringham 3191",
  "8 Station Street, Ballarat 3350",
  "301/9 Commercial Road, Prahran 3181",
];

// Where the typing is at `tick` (one tick per character, a pause at each end):
// what the placeholder shows. Pure, so it is testable without timers.
export function typedAt(tick, examples = EXAMPLE_ADDRESSES, hold = 18) {
  let t = tick;
  for (let round = 0; ; round += 1) {
    const text = examples[round % examples.length];
    const span = text.length * 2 + hold;
    if (t < span) {
      if (t < text.length) return text.slice(0, t + 1);
      if (t < text.length + hold) return text;
      return text.slice(0, text.length - (t - text.length - hold) - 1);
    }
    t -= span;
  }
}

function useTypewriter(active) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!active || prefersLessMotion()) return undefined;
    const timer = setInterval(() => setTick(n => n + 1), 70);
    return () => clearInterval(timer);
  }, [active]);
  return active && !prefersLessMotion() ? `${typedAt(tick)}` : "e.g. 12 Smith Street, Richmond 3121";
}

// A real number, counted up to.
// `always` counts even for somebody who asked for less motion — for a figure
// the page is showing being found (the lot's area), like the walk-through.
function CountUp({ value, ms = 900, always = false }) {
  const still = () => !always && prefersLessMotion();
  const [shown, setShown] = useState(() => (still() ? value : 0));
  useEffect(() => {
    if (still() || typeof requestAnimationFrame !== "function") { setShown(value); return undefined; }
    let frame;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / ms);
      setShown(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, ms, always]);
  return <>{Number(shown).toLocaleString("en-AU")}</>;
}

// Rises into view when scrolled to. Without IntersectionObserver it is simply
// there.
function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(() => typeof IntersectionObserver === "undefined" || prefersLessMotion());
  useEffect(() => {
    if (inView || !ref.current) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView]);
  return <div ref={ref} className={`ns-rise${inView ? " in" : ""}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

// A card that leans towards the pointer, a few degrees at most.
function useTilt() {
  const ref = useRef(null);
  const onMouseMove = (e) => {
    const el = ref.current;
    if (!el || prefersLessMotion()) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${(-y * 4).toFixed(2)}deg) rotateY(${(x * 5).toFixed(2)}deg)`;
    el.style.setProperty("--gx", `${((x + 0.5) * 100).toFixed(1)}%`);
    el.style.setProperty("--gy", `${((y + 0.5) * 100).toFixed(1)}%`);
  };
  const onMouseLeave = () => { if (ref.current) ref.current.style.transform = ""; };
  return { ref, onMouseMove, onMouseLeave };
}

// The page's weather: three slow blobs of the palette behind everything, and
// a soft light that follows the pointer on a desktop.
function Atmosphere() {
  useEffect(() => {
    if (prefersLessMotion()) return undefined;
    const move = (e) => {
      document.documentElement.style.setProperty("--mx", `${e.clientX}px`);
      document.documentElement.style.setProperty("--my", `${e.clientY}px`);
    };
    window.addEventListener("pointermove", move, { passive: true });
    return () => window.removeEventListener("pointermove", move);
  }, []);
  return (
    <div className="ns-aurora" aria-hidden="true">
      <span className="b1" /><span className="b2" /><span className="b3" />
      <span className="ns-spot" />
    </div>
  );
}

// A burst of paper in the palette's colours when the matter opens. Positions
// are worked out from the index, so it looks the same every time.
const CONFETTI = ["var(--sellLavender)", "var(--sellGlow)", "var(--sellAccent)", "var(--sellLavenderDeep)", "var(--sellCharcoal)", "var(--sellCream)"];
function Confetti() {
  if (prefersLessMotion()) return null;
  return (
    <div className="ns-confetti" aria-hidden="true">
      {Array.from({ length: 36 }, (_, i) => {
        const angle = (i / 36) * Math.PI * 2;
        const reach = 120 + ((i * 47) % 140);
        return (
          <i key={i} style={{
            background: CONFETTI[i % CONFETTI.length],
            "--dx": `${Math.cos(angle) * reach}px`,
            "--dy": `${Math.sin(angle) * reach * 0.7 - 60}px`,
            "--rot": `${(i * 73) % 360}deg`,
            animationDelay: `${(i % 6) * 18}ms`,
          }} />
        );
      })}
    </div>
  );
}

// An issuer's initials, for the badge beside a search.
export const initialsOf = (name = "") => String(name).split(/\s+/).filter(w => /^[A-Za-z]/.test(w) && !/^(of|and|the)$/i.test(w))
  .slice(0, 2).map(w => w[0].toUpperCase()).join("") || "·";

// ── The walk through ────────────────────────────────────────────────────────
//
// When the checks are done the page shows what they found one section at a
// time — the property, the conveyancer, the contract, the searches, what
// happens next — each on its own, then everything at once with the form to
// start the matter. It moves on by itself, and the dots, Next and Skip are
// always there; somebody who asked for less motion gets everything at once.
// Property and planning, then the contract, then the searches, then who and
// what next — each slides up under the checks, which stay open above.
export const TOUR = [
  { key: "property", label: "Property", ms: 4200 },
  { key: "draft", label: "Contract", ms: 6000 },
  { key: "searches", label: "Searches", ms: 3800 },
  { key: "conveyancer", label: "Conveyancer", ms: 3500 },
  { key: "next", label: "Next", ms: 4000 },
  { key: "start", label: "Start", ms: 0 },
];

// How long each check stays on screen working before it ticks, however fast
// its answer came: the weekly copy answers the map checks in a fraction of a
// second, and a list that ticks itself instantly reads as nothing happening.
export const STEP_MS = 1100;
// A panel leaving, before it is unmounted.
const LEAVE_MS = 180;

// The stops for this property: no conveyancer stop when nobody is offered.
// On a wide screen the draft has its own column, so it is not a stop.
export const tourFor = ({ conveyancer, wide = false } = {}) => TOUR.filter(t => (t.key !== "conveyancer" || conveyancer) && (t.key !== "draft" || !wide));

// ── Small pieces ────────────────────────────────────────────────────────────

const TONE = {
  ok: { ring: "var(--sellAccent)", fill: "var(--sellAccent)", ink: "var(--sellSoft)" },
  flag: { ring: "var(--sellGlow)", fill: "var(--sellInk)", ink: "var(--sellInk)" },
  unknown: { ring: "var(--sellFaint)", fill: "var(--sellFaint)", ink: "var(--sellSoft)" },
  bad: { ring: T.danger, fill: T.danger, ink: T.danger },
};

function StepIcon({ state, tone, icon, portrait }) {
  const line = icon ? STEP_ICONS[icon] : null;
  // The conveyancer's check shows their face once it ticks, where there is one.
  const face = state === "done" && portrait ? portraitSrc(portrait) : null;
  if (face) {
    return (
      <span className="ns-step-ico done ns-step-face" aria-hidden="true">
        <img src={face} alt="" />
        <span className="ns-step-mark" style={{ background: (TONE[tone] || TONE.ok).fill }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
        </span>
      </span>
    );
  }
  if (!line) {
    if (state === "waiting") return <span className="ns-dot" aria-hidden="true" />;
    if (state === "running") return <span className="ns-spin" aria-hidden="true" />;
  }
  const c = TONE[tone] || TONE.ok;
  const mark = tone === "unknown" ? <path d="M12 8v4m0 4h.01" /> : tone === "bad" ? <path d="M15 9l-6 6M9 9l6 6" /> : <path d="M20 6L9 17l-5-5" />;
  if (line) {
    const done = state === "done";
    return (
      <span className={`ns-step-ico ${state}`} style={done ? { borderColor: c.ring, color: c.fill } : undefined} aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{line}</svg>
        {done && <span className="ns-step-mark" style={{ background: c.fill }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">{mark}</svg>
        </span>}
      </span>
    );
  }
  return (
    <span className="ns-done" style={{ borderColor: c.ring, color: c.fill }} aria-hidden="true">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{mark}</svg>
    </span>
  );
}

function Card({ title, children, style, className = "" }) {
  return (
    <section className={`ns-card ${className}`} style={style}>
      {title && <h3 className="ns-card-title">{title}</h3>}
      {children}
    </section>
  );
}

function TiltCard({ title, children }) {
  const tilt = useTilt();
  return (
    <section className="ns-card ns-tilt" ref={tilt.ref} onMouseMove={tilt.onMouseMove} onMouseLeave={tilt.onMouseLeave}>
      {title && <h3 className="ns-card-title">{title}</h3>}
      {children}
    </section>
  );
}

const Field = ({ label, children, hint }) => (
  <label className="ns-field">
    <span className="ns-label">{label}</span>
    {children}
    {hint && <span className="ns-hint">{hint}</span>}
  </label>
);

// **A box that types ahead from the agent directory** — the salesperson or
// the agency, each on its own, as Form 27's picker asks. Picking a row sets
// its id; typing again clears it, so a name typed over a pick is sent as
// typed. The rows carry a name and where — the middleware never sends an
// email or phone to this page.
// The agent's email is asked only where the directory holds no work address
// for them: typed rather than picked, or picked without one (the search says
// yes or no, never the address).
export const agentNeedsEmail = (a = {}) => Boolean((a.agentName || "").trim()) && !(a.agentPickedId && a.agentHasWorkEmail);
export const directoryRow = (kind, r) => (kind === "agent"
  ? { id: r.agentId, name: r.name, where: [r.officeName, r.suburb].filter(Boolean).join(", "), hasWorkEmail: r.hasWorkEmail === true }
  : { id: r.officeId, name: r.tradingName, where: r.suburb || "" });
function DirectoryPicker({ kind, label, value, onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);
  const search = (q) => {
    const n = ++seq.current;
    if (q.trim().length < 2) { setRows([]); return; }
    setTimeout(async () => {
      if (n !== seq.current) return;
      const { body } = await ask(`/sale/${kind}-search?q=${encodeURIComponent(q)}`);
      if (n === seq.current) setRows(body && body.ok ? (body.results || []).map(r => directoryRow(kind, r)) : []);
    }, 220);
  };
  return (
    <div className="ns-field ns-picker">
      <span className="ns-label">{label}</span>
      <input className="ns-input" value={value.name || ""} autoComplete="off" role="combobox" aria-controls={`ns-picker-${kind}`} aria-expanded={open && rows.length > 0}
        onChange={(e) => { onChange({ id: "", name: e.target.value }); setOpen(true); search(e.target.value); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {value.id && <span className="ns-hint">From our directory</span>}
      {open && rows.length > 0 && (
        <ul className="ns-picker-list" id={`ns-picker-${kind}`} role="listbox">
          {rows.map(r => (
            <li key={r.id} role="option" aria-selected={r.id === value.id}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange({ id: r.id, name: r.name, hasWorkEmail: r.hasWorkEmail }); setOpen(false); }}>
                <b>{r.name}</b>{r.where && <span>{r.where}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The conveyancer's photo, from the middleware's own copy (Form 78's portrait,
// stored by its content), or their initial where there is none or it fails.
export const portraitSrc = (path) => (/^\/sale\/portrait\/[0-9a-f]{64}\.(png|jpg|webp)$/.test(path || "") ? `${MIDDLEWARE_URL}${path}` : null);

// How soon: the middleware's headline (as soon as a few hours without an
// owners corporation; the business-day range with one) and what it depends on.
function TimeEstimate({ timeframe, bare = false }) {
  const body = (
    <div className="ns-eta">
      <span className="ns-eta-ico" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg></span>
      <div>
        <div className="ns-eta-label">Section 32 ready</div>
        <div className="ns-eta-head">{timeframe.headline || timeframe.line}</div>
        {timeframe.headline && <div className="ns-eta-detail">{timeframe.detail || timeframe.line}</div>}
      </div>
    </div>
  );
  return bare ? body : <section className="ns-card ns-card-eta">{body}</section>;
}

// The initial in a small round, for an agent the directory named.
function Portraitless({ name }) {
  return <span className="ns-agent-dot" aria-hidden="true">{String(name || "?").slice(0, 1)}</span>;
}

// Someone else, if the visitor would rather: the next in line may be picked,
// and the rest of the team scrolls on to the right, at capacity right now.
function ConveyancerChooser({ options, chosen, onChoose }) {
  const strip = useRef(null);
  const [more, setMore] = useState(false);
  const [less, setLess] = useState(false);
  // The strip hides its scrollbar, so a mouse with no sideways wheel moves
  // only by these arrows: one each way, shown while there is somewhere to go.
  const measure = () => {
    const el = strip.current;
    setMore(Boolean(el) && el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    setLess(Boolean(el) && el.scrollLeft > 4);
  };
  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);
  const along = (dir) => strip.current && strip.current.scrollBy({ left: dir * strip.current.clientWidth * 0.8, behavior: "smooth" });
  return (
    <div className="ns-choose">
      <div className="ns-choose-head">Prefer someone else?</div>
      <div className="ns-choose-wrap">
        <div className="ns-choose-strip" ref={strip} onScroll={measure} role="radiogroup" aria-label="Your conveyancer">
          {options.map((o) => {
            const on = chosen && o.id === chosen.id;
            return (
              <button key={o.id} type="button" role="radio" aria-checked={on} disabled={!o.available}
                className={`ns-choose-card${on ? " ns-on" : ""}${o.available ? "" : " ns-full"}`}
                onClick={() => o.available && onChoose(o.id)}>
                <Portrait person={o} size={44} />
                <span className="ns-choose-name">{o.name}</span>
                {o.role && <span className="ns-choose-line">{o.role}</span>}
                <span className="ns-choose-line">{!o.available ? "At capacity right now"
                  : on ? "Your conveyancer"
                    : o.contractsPrepared != null ? `${o.contractsPrepared} contracts in 3 months`
                      : o.yearsExperience != null ? `${o.yearsExperience} years' experience` : "Available"}</span>
              </button>
            );
          })}
        </div>
        {less && <button type="button" className="ns-choose-more ns-back" onClick={() => along(-1)} aria-label="Earlier conveyancers">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
        </button>}
        {more && <button type="button" className="ns-choose-more" onClick={() => along(1)} aria-label="More conveyancers">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
        </button>}
      </div>
      <p className="ns-choose-note">We spread new files across our team so each conveyancer has the time to give your sale the care it deserves.</p>
    </div>
  );
}

// **Who will do it, beside the button that starts it**: the conveyancer
// matched or picked, one line, so the step reads as handing the sale to a
// person. Nothing when nobody is taking files.
function ConveyancerLine({ person, agent = false }) {
  return (
    <div className="ns-conv-line">
      <Portrait person={person} size={36} />
      <span><b>{person.name}</b> will prepare {agent ? "the" : "your"} contract{person.role ? <span className="ns-conv-role"> · {person.role}</span> : null}</span>
    </div>
  );
}

function Portrait({ person, size }) {
  const [broken, setBroken] = useState(false);
  const src = portraitSrc(person.portrait);
  const style = size ? { width: size, height: size } : undefined;
  if (src && !broken) return <img className="ns-avatar" src={src} alt="" style={style} onError={() => setBroken(true)} />;
  return <span className="ns-avatar" style={style} aria-hidden="true">{person.firstName.slice(0, 1)}</span>;
}

// The lot, traced from Vicmap's own outline (`parcelShape` in the middleware:
// true to scale, the area approximate). Drawn once and left on screen.
// Drawn and counted even for somebody who asked for less motion: the
// outline being traced is the page finding their land, as the walk-through is.
function ParcelOutline({ shape, lots }) {
  const pad = 8;
  const d = shape.rings.map(r => `M${r.map(p => p.join(" ")).join("L")}Z`).join("");
  return (
    <figure className="ns-parcel">
      <svg viewBox={`${-pad} ${-pad} ${shape.width + pad * 2} ${shape.height + pad * 2}`} role="img"
        aria-label={`Outline of ${lots || "the lot"}, about ${shape.areaM2} square metres`}>
        <path d={d} className="ns-parcel-fill" />
        <path d={d} pathLength="1" className="ns-parcel-line" />
      </svg>
      <figcaption>
        <b>about <CountUp value={shape.areaM2} ms={1400} always /> m²</b>
        <span>{shape.widthM} × {shape.heightM} m · from Vicmap, indicative</span>
      </figcaption>
    </figure>
  );
}

// The search states, as the person reads them.
export const SEARCH_STATE = {
  queued: "Waiting for review",
  ordered: "Ordered",
  received: "Received",
  not_required: "Not needed",
};

// Which searches changed state since the last read, so the page can show
// them moving.
export function changedSearches(before = [], after = []) {
  const key = (s, i) => `${i}:${s.name}`;
  const was = new Map(before.map((s, i) => [key(s, i), s.state]));
  return new Set(after.map((s, i) => [key(s, i), s.state]).filter(([k, st]) => was.has(k) && was.get(k) !== st).map(([k]) => k));
}

// The searches that have just landed, by name, for the arrival notice: only a
// move to received, and only from a state the page had already seen.
export function arrivals(before = [], after = []) {
  const was = new Map(before.map((s, i) => [`${i}:${s.name}`, s.state]));
  return after.filter((s, i) => s.state === "received" && was.has(`${i}:${s.name}`) && was.get(`${i}:${s.name}`) !== "received").map(s => s.name);
}

// Where a search is in the queue: 0 waiting for review, 1 ordered, 2 received.
export const QUEUE_STAGES = ["Review", "Ordered", "Received"];
// The bar fills from the left: what is in, then what is ordered, then the rest
// — whatever order the list below happens to be in.
const SEG_RANK = { received: 0, ordered: 1 };
export const barSegments = (searches = []) => searches.filter(s => s.state !== "not_required")
  .map(s => s.state).sort((a, b) => (SEG_RANK[a] ?? 2) - (SEG_RANK[b] ?? 2));
// **The list in the bar's order**: what has come back first, then what is
// ordered, then what waits; a search not needed last. Stable, and each row
// keeps its place in the middleware's list (`at`) for its key, so a row that
// moves up when it lands is the same row.
export const queueRows = (searches = []) => searches
  .map((s, at) => ({ s, at }))
  .sort((a, b) => (SEG_RANK[a.s.state] ?? (a.s.state === "not_required" ? 3 : 2)) - (SEG_RANK[b.s.state] ?? (b.s.state === "not_required" ? 3 : 2)));
export const queueStage = (state) => (state === "received" ? 2 : state === "ordered" ? 1 : 0);

const TRACK_POLL_MS = 20000;

// One search's place in the queue: three stops on a line, the line filled to
// where it is, the stop it is at pulsing until it is in.
function QueueTrack({ state }) {
  if (state === "not_required") return <span className="ns-chip not_required">Not needed</span>;
  const at = queueStage(state);
  return (
    <span className={`ns-queue at-${at}`} role="img" aria-label={SEARCH_STATE[state] || state}>
      <span className="ns-queue-line"><span className="ns-queue-fill" style={{ width: `${at * 50}%` }} /></span>
      {QUEUE_STAGES.map((label, i) => (
        <span key={label} className={`ns-queue-stop${i < at ? " past" : i === at ? " now" : ""}`} style={{ left: `calc((100% - 15px) * ${i / 2})` }}>
          {i < at || at === 2
            ? <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
            : null}
        </span>
      ))}
      <span className="ns-queue-label">{SEARCH_STATE[state] || state}</span>
    </span>
  );
}

// Sending a view-only copy of the tracker — to the agent, most often. The
// middleware mints the copy's own link and emails it; where mail is not set
// up the link comes back to copy.
// **Sharing a link.** A phone hands it to the system's share sheet (text,
// WhatsApp, mail — whatever they use); a desktop copies it or emails it from
// here. No SMS from a desktop. The link is minted on the first press, not on
// load — each is a token the middleware keeps (as a hash) and caps — and a
// share sheet or clipboard the browser refuses after that wait leaves the
// link on screen, so a second press works from it at once.
const nativeShare = () => {
  try { return typeof navigator !== "undefined" && typeof navigator.share === "function" && window.matchMedia("(pointer: coarse)").matches; } catch (e) { return false; }
};
// **The phone's own mail and messages, addressed by URL**: a share sheet
// hands the words to whichever app is picked, and Gmail's takes its first
// line for the subject and runs the lines together. A mailto: link sets the
// subject and keeps the lines, and the app adds the sender's signature; an
// sms: link puts the whole message in the text. CRLF, as mail URLs expect.
export const mailtoHref = (subject, body) => `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(String(body).replace(/\r?\n/g, "\r\n"))}`;
export const smsHref = (body) => `sms:?&body=${encodeURIComponent(body)}`;
const MailIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 6l-10 7L2 6" /></svg>;
const MessageIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>;
const ShareIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" /></svg>;
const CopyIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>;

function ShareSheet({ label, shareTitle, shareText, sheetText, note, getLink, sendEmail, initialLink = null, already = [], sentLabel = "Sent to", collapsed = false }) {
  // Collapsed, it is one button until pressed; then the ways to send it.
  const [open, setOpen] = useState(!collapsed);
  const [link, setLink] = useState(initialLink);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [error, setError] = useState("");
  const [to, setTo] = useState({ email: "", name: "" });
  const [sending, setSending] = useState(false);
  // Only the people it was emailed to are named; a copied link has nobody.
  const [sent, setSent] = useState(() => already.filter(x => x && (x.name || x.email)));
  const [native] = useState(nativeShare);
  const linkNow = async () => {
    if (link) return link;
    setBusy(true); setError("");
    const made = await getLink();
    setBusy(false);
    if (!made.link) { setError(made.error || "The link could not be made just now. Please try again."); return null; }
    setLink(made.link);
    return made.link;
  };
  const share = async () => {
    setPressed(true);
    const url = await linkNow();
    if (!url) return;
    // **The link inside the words, not beside them**: Messages drops the text
    // when a URL comes separately, and Mail takes the title as the subject
    // and adds the sender's own signature — so this is the whole message.
    // Gmail's share takes the first line for the subject and runs the rest
    // together, so the sheet's message opens on a line made to be a subject
    // and stays short (`sheetText`).
    try { await navigator.share({ title: shareTitle, text: `${sheetText || shareText.replace(/^Hi,\s*/, "")}\n\n${url}` }); } catch (e) { /* closed, or refused: the link is on screen */ }
  };
  const mail = async () => {
    setPressed(true);
    const url = await linkNow();
    if (!url) return;
    window.location.href = mailtoHref(shareTitle, `${shareText}\n\n${url}`);
  };
  const text = async () => {
    setPressed(true);
    const url = await linkNow();
    if (!url) return;
    // A text message has no greeting line and no subject: the words and the link.
    window.location.href = smsHref(`${shareText.replace(/^Hi,\s*/, "")}\n\n${url}`);
  };
  const copy = async () => {
    setPressed(true);
    const url = await linkNow();
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch (e) { /* the link is on screen to copy by hand */ }
  };
  const email = async (e) => {
    e.preventDefault();
    setPressed(true); setSending(true); setError("");
    const r = await sendEmail(to);
    setSending(false);
    if (!r.ok) { setError(r.error || "It could not be sent just now."); return; }
    setSent(list => [...list.filter(x => x.email !== to.email), { email: to.email, name: to.name }]);
    if (!r.sent) { if (r.link) setLink(r.link); setError("We couldn't email it just now — copy the link instead."); }
    setTo({ email: "", name: "" });
  };
  const sentLine = sent.length > 0 && (
    <p className="ns-share-sent">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
      {sentLabel}: {sent.map(x => x.name || x.email).join(", ")}
    </p>
  );
  if (!open) {
    return (
      <div className="ns-share">
        {sentLine}
        <button type="button" className="ns-btn soft" onClick={() => setOpen(true)}><ShareIcon />{label}</button>
      </div>
    );
  }
  return (
    <div className="ns-share">
      {sent.length > 0 && (
        <p className="ns-share-sent">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
          {sentLabel}: {sent.map(x => x.name || x.email).join(", ")}
        </p>
      )}
      <div className="ns-share-row">
        <button type="button" className="ns-btn soft" onClick={mail} disabled={busy}><MailIcon />{busy ? "Making the link…" : "Email"}</button>
        {native && <button type="button" className="ns-btn soft" onClick={text} disabled={busy}><MessageIcon />Message</button>}
        <button type="button" className="ns-btn soft" onClick={copy} disabled={busy}><CopyIcon />{copied ? "Copied" : "Copy link"}</button>
        {native && <button type="button" className="ns-btn soft ns-share-more" onClick={share} disabled={busy} aria-label="More ways to share"><ShareIcon /></button>}
        {collapsed && <button type="button" className="ns-link ns-share-hide" onClick={() => setOpen(false)}>Hide</button>}
      </div>
      {pressed && link && <input className="ns-input ns-share-url" readOnly value={link} aria-label="The link" onFocus={(e) => e.target.select()} />}
      {!native && sendEmail && <p className="ns-hint ns-share-or">Or we'll email it for you:</p>}
      {!native && sendEmail && (
        <form className="ns-share-form" onSubmit={email}>
          <input className="ns-input" placeholder="Their name" value={to.name} onChange={e => setTo(t => ({ ...t, name: e.target.value }))} autoComplete="off" />
          <input className="ns-input" type="email" required placeholder="Their email" value={to.email} onChange={e => setTo(t => ({ ...t, email: e.target.value }))} autoComplete="off" />
          <button type="submit" className="ns-btn soft" disabled={sending}>{sending ? "Sending…" : "Email it"}</button>
        </form>
      )}
      {pressed && note && <p className="ns-hint ns-share-note">{note}</p>}
      {error && <p className="ns-error" role="alert">{error}</p>}
    </div>
  );
}

const trackPath = (track, tail) => `/sale/track/${encodeURIComponent(track.ref)}/${encodeURIComponent(track.token)}/${tail}`;

// A view-only copy of the tracker, for anyone: follows, never answers or pays.
function TrackerShare({ track, address = "", already = [] }) {
  const place = displayAddress(address);
  return (
    <ShareSheet label="Share this matter" already={already} sentLabel="Status shared with" collapsed
      shareTitle={place ? `Conveyancing for ${place}` : "Conveyancing for the sale"}
      shareText={`Hi,\n\nHere's a link to follow the Section 32 and contract of sale${place ? ` for ${place}` : ""} as it comes together: every search as it comes back, and the draft contract.\n\nIt's view only, so there's nothing to fill in.`}
      sheetText={`Follow the sale${place ? ` of ${place}` : ""}\n\nView only: every search as it comes back, and the draft contract.`}
      note="View only — they can follow the searches and see the draft, but can't answer questions or pay."
      getLink={async () => {
        const { status, body } = await ask(trackPath(track, "share"), { method: "POST", body: JSON.stringify({ linkOnly: true }) });
        return status === 429 ? { error: "Too many from here — please try again in a little while." } : { link: body.ok ? body.link : null, error: body.error };
      }}
      sendEmail={async (to) => {
        const { status, body } = await ask(trackPath(track, "share"), { method: "POST", body: JSON.stringify(to) });
        if (status === 429) return { ok: false, error: "Too many from here — please try again in a little while." };
        return { ok: body.ok, error: body.error, sent: body.sent, link: body.share ? `${window.location.origin}${trackHref(body.share)}` : null };
      }} />
  );
}

// The proposal's link for the vendor, from the agent: theirs to accept,
// answer and pay. Emailed from here, replies go to the agent.
function ProposalShare({ track, address = "", initialLink = null }) {
  const place = displayAddress(address);
  return (
    <ShareSheet label="Send to the vendor" initialLink={initialLink}
      shareTitle={place ? `Conveyancing for ${place}` : "Conveyancing for your sale"}
      shareText={`Hi,\n\nHere's the proposal for your Section 32 and contract of sale${place ? ` for ${place}` : ""}.\n\nOpen the link to see what's included, answer a few quick questions about the property and accept. Once you do, the searches are ordered and the contract comes together in days.`}
      sheetText={`Conveyancing quote${place ? ` for ${place}` : ""} is ready\n\nOpen the link to see the quote, answer a few questions and accept.`}
      getLink={async () => {
        const { status, body } = await ask(trackPath(track, "vendor-link"), { method: "POST", body: "{}" });
        return status === 429 ? { error: "Too many from here — please try again in a little while." } : { link: body.ok ? body.link : null, error: body.error };
      }}
      sendEmail={async (to) => {
        const { status, body } = await ask(trackPath(track, "vendor-link"), { method: "POST", body: JSON.stringify(to) });
        if (status === 429) return { ok: false, error: "Too many from here — please try again in a little while." };
        return { ok: body.ok, error: body.error, sent: body.sent, link: body.link };
      }} />
  );
}

function TrackerDraft({ track, view }) {
  const base = `${MIDDLEWARE_URL}/sale/track/${encodeURIComponent(track.ref)}/${encodeURIComponent(track.token)}`;
  // Re-drawn when a search arrives, since that is what changes it.
  const { html, failed } = useDraftHtml(() => fetch(`${base}/draft`), [base, view.counts.received]);
  if (failed) return null;
  return (
    <DraftPanel html={html} title="Your draft contract"
      intro="Drafted from your matter as it stands — it fills in as the searches come back."
      footer={(
        <div className="ns-follow" style={{ marginTop: 16 }}>
          {view.draft.pdf
            ? <a className="ns-btn" href={`${base}/draft.pdf`}>Download draft PDF</a>
            : <span className="ns-hint">A PDF of the draft is ready once your conveyancer has reviewed the matter.</span>}
        </div>
      )} />
  );
}

// **The lease, from the tracker** (`view.lease`, the middleware's
// `trackerLease`): whenever the property is let, a card with its own upload
// button — the lease can come later without reopening the questions.
export function leaseCardLine(lease = {}) {
  if (lease.state === "received") return `We have the lease${lease.files > 1 ? ` (${lease.files} files)` : ""} — it goes in the Section 32. Add a renewal or another page here.`;
  if (lease.state === "asked") return `We've asked ${lease.role === "agent" ? "the selling agent's office" : "your property manager"} for it. If you have a copy, upload it here.`;
  return "It goes in the Section 32. Upload it here whenever you have it — a PDF, or a photo of each page.";
}
function TrackerLease({ track, lease }) {
  const [value, setValue] = useState(null);
  const shown = value ? { state: "received", files: (lease.state === "received" ? lease.files : 0) + 1 } : lease;
  return (
    <Card title="The lease">
      <p className="ns-msg" style={{ marginTop: 0 }}>{leaseCardLine(shown)}</p>
      <UploadButton url={`${MIDDLEWARE_URL}/sale/track/${encodeURIComponent(track.ref)}/${encodeURIComponent(track.token)}/lease`}
        label="Upload the lease" value={value} onChange={setValue}
        first={shown.state === "received" ? "Add another file or page" : "Upload the lease"} again="Add another file or page" />
    </Card>
  );
}

// The matter afterwards. Re-read every 20 seconds while the tab is in view
// and at once when it comes back into view; never while hidden.
// ── The questions ───────────────────────────────────────────────────────────
//
// Form 27's disclosure questions, asked on the page. The questions, their
// options and which follow-up each answer opens are the middleware's
// (lib/sale-questions.js); this only draws them and sends the answers back.
// One group at a time, saved as each is finished. An agent sees the questions
// an agent can answer; the vendor's own link asks the rest.
export function questionApplies(q, answers = {}) {
  if (!q.when) return true;
  return Object.entries(q.when).some(([key, values]) => {
    const a = answers[key];
    return Array.isArray(a) ? a.some(v => values.includes(v)) : values.includes(a);
  });
}

export function groupsToAsk(view = {}) {
  const asked = (view.questions || []).filter(q => q.askable);
  return (view.groups || [])
    .map(g => ({ ...g, questions: asked.filter(q => q.group === g.key) }))
    .filter(g => g.questions.length);
}

// **Questions drawn together** (the middleware's `row`): consecutive
// questions sharing a row are one block, side by side where there is room;
// `attach` joins a block to the question above it, with no line between.
export function questionBlocks(questions = []) {
  const blocks = [];
  for (const q of questions) {
    const last = blocks[blocks.length - 1];
    if (q.row && last && last.row === q.row) last.questions.push(q);
    else blocks.push({ key: q.row ? `row:${q.row}` : q.key, row: q.row || null, attach: Boolean(q.attach), questions: [q] });
  }
  return blocks;
}

export const contactComplete = (v) => Boolean(v && typeof v === "object"
  && String(v.name || "").trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v.email || "").trim()) && String(v.mobile || "").replace(/\D/g, "").length >= 8);

export function groupMissing(group, answers = {}) {
  return group.questions.filter(q => q.required && questionApplies(q, answers)).filter(q => {
    const v = answers[q.key];
    if (q.type === "works") return !Array.isArray(v) || !v.some(r => r.what);
    if (q.type === "multi") return !Array.isArray(v);
    if (q.type === "oc") return !Array.isArray(v) || !v.length;
    if (q.type === "contact") return !contactComplete(v);
    return v === undefined || v === null || v === "";
  }).map(q => q.key);
}

// A file the question asks for — the rates notice, the lease — uploaded the
// moment it is chosen, on the route the middleware names (`q.upload`), not
// with the answers: a PDF or a photo, filed on the matter.
export const uploadPathFor = (q = {}) => (q.upload === "lease" ? "lease" : "rates-notice");
export function UploadButton({ url, label, value, onChange, again = "Add another page", first = "Upload a photo or PDF" }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(url, {
        method: "POST", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file,
      });
      const body = await res.json().catch(() => ({ ok: false }));
      if (!body.ok) setError(body.error || "The upload failed — please try again."); else onChange({ name: body.file.name });
    } catch (err) {
      setError("The upload failed — please check your connection and try again.");
    }
    setBusy(false);
  };
  return (
    <div className="ns-upload">
      {value && value.name && <p className="ns-share-sent">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
        Uploaded — {value.name}</p>}
      <label className="ns-btn soft ns-upload-btn">
        <input type="file" accept="application/pdf,image/*" onChange={pick} disabled={busy} aria-label={label} />
        {busy ? "Uploading…" : value && value.name ? again : first}
      </label>
      {error && <p className="ns-error" role="alert">{error}</p>}
    </div>
  );
}
function UploadInput({ q, track, value, onChange }) {
  const lease = q.upload === "lease";
  return <UploadButton url={`${MIDDLEWARE_URL}/sale/track/${encodeURIComponent(track.ref)}/${encodeURIComponent(track.token)}/${uploadPathFor(q)}`}
    label={q.label} value={value} onChange={onChange}
    first={lease ? "Upload the lease" : "Upload a photo or PDF"} again={lease ? "Add another file or page" : "Add another page"} />;
}

// **A long list shows its first few**, the one picked if it is further down,
// and the rest behind "More": thirteen pills read as a wall. The order is the
// middleware's, which puts the common answers first.
export const CHOICES_SHOWN = 5;
export const shownChoices = (options = [], value, all = false) => {
  if (all || options.length <= CHOICES_SHOWN + 2) return options;
  return options.filter((o, i) => i < CHOICES_SHOWN || o === value);
};

function ChoiceInput({ q, value, onChange }) {
  const [all, setAll] = useState(false);
  const shown = shownChoices(q.options, value, all);
  return (
    <>
    <div className="ns-choices" role="radiogroup" aria-label={q.label}>
      {shown.map(o => (
        <button type="button" key={o} role="radio" aria-checked={value === o}
          className={`ns-choice${value === o ? " on" : ""}`} onClick={() => onChange(o)}>{o}</button>
      ))}
      {shown.length < q.options.length && (
        <button type="button" className="ns-link ns-choice-more" onClick={() => setAll(true)}>More options</button>
      )}
    </div>
    {/* What the middleware says to someone who picks this answer (`q.notes`). */}
    {q.notes && value && q.notes[value] && <p className="ns-choice-note" role="status">{q.notes[value]}</p>}
    </>
  );
}

function QuestionInput({ q, track, value, onChange }) {
  if (q.type === "file") return <UploadInput q={q} track={track} value={value} onChange={onChange} />;
  if (q.type === "choice") return <ChoiceInput q={q} value={value} onChange={onChange} />;
  if (q.type === "multi") {
    const list = Array.isArray(value) ? value : null;
    const toggle = (o) => onChange(list && list.includes(o) ? list.filter(v => v !== o) : [...(list || []), o]);
    return (
      <div className="ns-choices">
        {q.options.map(o => (
          <button type="button" key={o} aria-pressed={Boolean(list && list.includes(o))}
            className={`ns-choice${list && list.includes(o) ? " on" : ""}`} onClick={() => toggle(o)}>{o}</button>
        ))}
        {q.noneLabel && (
          <button type="button" aria-pressed={Boolean(list && !list.length)}
            className={`ns-choice${list && !list.length ? " on" : ""}`} onClick={() => onChange([])}>{q.noneLabel}</button>
        )}
      </div>
    );
  }
  // The plan's own owners corporations: tick every one the lot is in.
  if (q.type === "oc") {
    const list = Array.isArray(value) ? value : [];
    const toggle = (k) => onChange(list.includes(k) ? list.filter(v => v !== k) : [...list, k]);
    return (
      <div className="ns-choices">
        {q.options.map(o => (
          <button type="button" key={o.key} aria-pressed={list.includes(o.key)}
            className={`ns-choice${list.includes(o.key) ? " on" : ""}`} onClick={() => toggle(o.key)}>
            {o.label}{o.manager ? <span className="ns-choice-sub"> · {o.manager}</span> : null}
          </button>
        ))}
      </div>
    );
  }
  // The agent and their agency, each typed ahead from the directory.
  if (q.type === "agent") {
    const a = value && typeof value === "object" ? value : {};
    return (
      <div className="ns-form-grid">
        <DirectoryPicker kind="agent" label="Agent" value={{ id: a.agentPickedId || "", name: a.agentName || "" }}
          onChange={(v) => onChange({ ...a, agentPickedId: v.id, agentName: v.name, agentHasWorkEmail: v.hasWorkEmail === true })} />
        <DirectoryPicker kind="office" label="Agency" value={{ id: a.officePickedId || "", name: a.agencyName || "" }}
          onChange={(v) => onChange({ ...a, officePickedId: v.id, agencyName: v.name })} />
        {agentNeedsEmail(a) && (
          <label className="ns-field">
            <span className="ns-label">Agent's email</span>
            <input className="ns-input" type="email" value={a.agentEmail || ""} autoComplete="off"
              onChange={(e) => onChange({ ...a, agentEmail: e.target.value })} />
            <span className="ns-hint">We don't have it on file — so we can send them the contract.</span>
          </label>
        )}
      </div>
    );
  }
  // The vendor's own details, on a proposal their agent prepared.
  if (q.type === "contact") {
    const c = value && typeof value === "object" ? value : {};
    const put = (key) => (e) => onChange({ ...c, [key]: e.target.value });
    const email = String(c.email || "").trim();
    const mobile = String(c.mobile || "").trim();
    const badEmail = email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const badMobile = mobile && mobile.replace(/\D/g, "").length < 8;
    return (
      <div className="ns-form-grid ns-contact">
        <div className="ns-contact-name"><Field label="Your full name"><input className="ns-input" value={c.name || ""} onChange={put("name")} autoComplete="name" /></Field></div>
        <Field label="Email"><input className="ns-input" type="email" value={c.email || ""} onChange={put("email")} autoComplete="email" inputMode="email" />
          {badEmail && <span className="ns-field-error">That doesn't look like an email address.</span>}</Field>
        <Field label="Mobile"><input className="ns-input" type="tel" value={c.mobile || ""} onChange={put("mobile")} autoComplete="tel" inputMode="tel" />
          {badMobile && <span className="ns-field-error">Enter a mobile number, like 0400 000 000.</span>}</Field>
      </div>
    );
  }
  if (q.type === "works") {
    const rows = Array.isArray(value) && value.length ? value : [{ what: "", by: "", value: "" }];
    const put = (i, key) => (e) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)));
    return (
      <div className="ns-works">
        {rows.map((r, i) => (
          <div key={i} className="ns-works-row">
            <input className="ns-input" placeholder="What was done — e.g. new deck" value={r.what} onChange={put(i, "what")} aria-label="What was done" />
            <select className="ns-input" value={r.by} onChange={put(i, "by")} aria-label="Who did it">
              <option value="">Who did it?</option>
              {q.byOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <input className="ns-input" placeholder="Rough cost" value={r.value} onChange={put(i, "value")} aria-label="Rough cost" inputMode="decimal" />
          </div>
        ))}
        {rows.length < 10 && <button type="button" className="ns-link" onClick={() => onChange([...rows, { what: "", by: "", value: "" }])}>Add another</button>}
      </div>
    );
  }
  if (q.type === "longtext") return <textarea className="ns-input" rows={3} value={value || ""} onChange={e => onChange(e.target.value)} aria-label={q.label} />;
  return <input className="ns-input" type={q.type === "date" ? "date" : q.type === "email" ? "email" : "text"} {...(q.type === "email" ? { autoComplete: "off", inputMode: "email" } : {})}
    value={value || ""} onChange={e => onChange(e.target.value)} aria-label={q.label} />;
}

// `onClose`: only when the questions were reopened from "Change answers" —
// shut them again without changing anything. Each page is saved on Next, so
// closing keeps what was saved and drops only the page open now.
export function SaleQuestions({ track, view, onDone, intro, onClose = null }) {
  const groups = groupsToAsk(view);
  const [answers, setAnswers] = useState(() => ({ ...(view.answers || {}) }));
  const [at, setAt] = useState(() => {
    const first = groups.findIndex(g => groupMissing(g, view.answers || {}).length);
    return first < 0 ? 0 : first;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tried, setTried] = useState(false);
  const card = useRef(null);
  if (!groups.length) return null;
  const group = groups[Math.min(at, groups.length - 1)];
  const left = groupMissing(group, answers);
  const last = at >= groups.length - 1;
  const lockedByVendor = (key) => view.who === "agent" && (view.answeredBy || {})[key] === "vendor";
  const fromAgent = (key) => view.who === "vendor" && (view.answeredBy || {})[key] === "agent";
  const shownHere = group.questions.filter(q => questionApplies(q, answers));

  const save = async (next) => {
    setTried(true);
    if (left.length) {
      // Show them which: the first question still to answer.
      setTimeout(() => {
        const el = card.current && card.current.querySelector(".ns-question-missing");
        if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      return;
    }
    setSaving(true); setError("");
    const mine = {};
    for (const q of group.questions) if (answers[q.key] !== undefined && !lockedByVendor(q.key)) mine[q.key] = answers[q.key];
    const { status, body } = await ask(`/sale/track/${encodeURIComponent(track.ref)}/${encodeURIComponent(track.token)}/answers`,
      { method: "POST", body: JSON.stringify({ answers: mine }) });
    setSaving(false);
    if (status === 429) { setError("Too many saves from here — please wait a moment and try again."); return; }
    if (!body.ok) { setError(body.error || "Your answers could not be saved. Please try again."); return; }
    setAnswers(a => ({ ...a, ...body.answers }));
    setTried(false);
    if (next) setAt(n => n + 1);
    else if (onDone) onDone(body);
  };

  return (
    <div ref={card} id="ns-questions" className={onClose ? "ns-questions-closable" : undefined}><Card title="Questions for the Section 32" className="ns-card-cta">
      {onClose && <button type="button" className="ns-questions-close" onClick={onClose} aria-label="Close without changing">×</button>}
      {intro && at === 0 && <p className="ns-msg" style={{ marginTop: 0 }}>{intro}</p>}
      <div className="ns-progress">
        <span>{group.title}</span>
        <span className="ns-progress-count"><b>{at + 1}</b> of {groups.length}</span>
      </div>
      <div className="ns-progress-bar" aria-hidden="true"><span style={{ width: `${((at + 1) / groups.length) * 100}%` }} /></div>
      {shownHere.some(q => fromAgent(q.key)) && (
        <p className="ns-from-agent-note">Your agent has answered some of these. Change anything that isn't right.</p>
      )}
      <div className="ns-questions" key={group.key}>
        {questionBlocks(shownHere).map(block => {
          const missing = (q) => (tried && left.includes(q.key) ? " ns-question-missing" : "");
          const body = (q) => (
            <>
              <div className="ns-label">{q.label}{!q.required && <span className="ns-optional"> · optional</span>}{fromAgent(q.key) && <span className="ns-from-agent"> · from your agent</span>}</div>
              {q.help && <div className="ns-hint">{q.help}</div>}
              {lockedByVendor(q.key)
                ? <div className="ns-hint">Answered by the vendor.</div>
                : <QuestionInput q={q} track={track} value={answers[q.key]} onChange={v => setAnswers(a => ({ ...a, [q.key]: v }))} />}
            </>
          );
          const attached = block.attach ? " ns-question-attached" : "";
          if (block.questions.length === 1) {
            const q = block.questions[0];
            return <div key={q.key} className={`ns-question${attached}${missing(q)}`}>{body(q)}</div>;
          }
          return (
            <div key={block.key} className={`ns-question${attached}`}>
              <div className="ns-question-row">
                {block.questions.map(q => <div key={q.key} className={`ns-question-cell${missing(q)}`}>{body(q)}</div>)}
              </div>
            </div>
          );
        })}
      </div>
      {tried && left.length > 0 && <p className="ns-error" role="alert">Please answer the questions marked above — "Not sure" is fine.</p>}
      {error && <p className="ns-error" role="alert">{error}</p>}
      <div className="ns-question-nav">
        {/* Each page is saved on Next, so a person can stop and come back. */}
        <span className="ns-saved-note">Saved as you go — come back to this link any time.</span>
        {onClose && <button type="button" className="ns-link" onClick={onClose}>Close without changing</button>}
        {at > 0 && <button type="button" className="ns-link" onClick={() => setAt(n => n - 1)}>Back</button>}
        <button type="button" className="ns-btn" disabled={saving} onClick={() => save(!last)}>
          {saving ? "Saving…" : last ? "Finish" : "Next"}<span className="ns-btn-arrow" aria-hidden="true">→</span>
        </button>
      </div>
    </Card></div>
  );
}

// **The answers read back**, in the middleware's words and groups: a quiet
// list under the step, opened and closed in place.
function AnswersSummary({ groups }) {
  return (
    <div className="ns-answers">
      {groups.map(g => (
        <div key={g.title} className="ns-answers-group">
          <div className="ns-answers-title">{g.title}</div>
          <dl className="ns-answers-list">
            {g.items.map(i => (
              <Fragment key={i.key}><dt>{i.label}</dt><dd>{i.answer}{i.by === "agent" && <span className="ns-answers-by"> · from your agent</span>}</dd></Fragment>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

// What it costs: the fee (and its usual price while the promotion runs) and
// the searches at cost, held on the card until review.
// The quote terms, as the middleware words them, and the box that accepts
// them. The version shown is what is sent, so a change of wording since the
// page loaded is asked again rather than taken as agreed.
function QuoteTerms({ terms, accepted, onAccept, attention = false, inputRef = null }) {
  if (!terms) return null;
  return (
    <div className="ns-terms" data-quote-terms="">
      <div className="ns-terms-head">Quote terms</div>
      <ul className="ns-terms-list">{terms.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
      {/* The box to tick is its own row, so it reads as something asked of
          the reader rather than one more line of the terms. */}
      <label className={`ns-accept${accepted ? " on" : ""}${attention && !accepted ? " ns-accept-attn" : ""}`}>
        <input ref={inputRef} type="checkbox" checked={accepted === true} onChange={(e) => onAccept(e.target.checked)} />
        <span><b>I accept the quote terms</b>{attention && !accepted && <span className="ns-accept-why">Tick this to pay.</span>}</span>
      </label>
    </div>
  );
}

export function withPromo(price, promo) {
  if (!promo) return price;
  return { ...price, feeCents: promo.feeCents, listCents: promo.usualFeeCents, totalCents: price.totalCents - price.feeCents + promo.feeCents, promo: promo.promo };
}

// **Who is doing the work**, beside every place somebody decides to go ahead
// — the start, the walkthrough's quote, the invoice — so a phone shows it
// with the button rather than somewhere below it.
export const FIRM_TRUST = { rating: 4.8, source: "Google", since: 2009 };
function TrustLine() {
  return (
    <p className="ns-trust" data-trust="">
      <span className="ns-trust-stars" aria-hidden="true">{"★★★★★"}</span>
      <span><b>{FIRM_TRUST.rating}</b> on {FIRM_TRUST.source} reviews · Established {FIRM_TRUST.since}</span>
    </p>
  );
}

function PriceSummary({ price: given, promo, agent = false }) {
  const price = withPromo(given, promo);
  return (
    <div className="ns-price" data-price="">
      <div className="ns-price-row"><span>Section 32 and contract of sale <span className="ns-hint">inc. GST</span>{price.promo && <span className="ns-promo-tag">Promo applied</span>}</span>
        <span>{price.listCents && price.listCents > price.feeCents && <s className="ns-price-was">{money(price.listCents)}</s>}{money(price.feeCents)}</span></div>
      <div className="ns-price-row"><span>Property searches, at cost{price.unpriced ? <span className="ns-hint"> · {price.unpriced} priced when ordered</span> : null}</span><span>{money(price.searchesCents)}</span></div>
      <div className="ns-price-row ns-price-total"><span>Estimate</span><span>{money(price.totalCents)}</span></div>
      {!agent && <p className="ns-hint" style={{ margin: 0 }}>You'll see the invoice and pay on your matter's page, once you've answered a few questions.</p>}
    </div>
  );
}

// **The bill, and paying it — the last step.** The middleware reads it off
// the order as it stands (fee with GST, then each search at cost, so the
// owners corporations the vendor ticked and no others); the page draws it.
// A promotion code changes the fee; the terms are accepted here; paying
// opens Stripe's page, which holds the amount and brings them back.
function BillLines({ payment }) {
  return (
    <div className="ns-price" data-bill="">
      <div className="ns-price-row"><span>Section 32 and contract of sale <span className="ns-hint">inc. GST</span>{payment.promo && <span className="ns-promo-tag">Promo applied</span>}</span>
        <span>{payment.usualFeeCents != null && payment.usualFeeCents > payment.feeCents
          ? <s className="ns-price-was">{money(payment.usualFeeCents)}</s>
          : payment.listCents && payment.listCents > payment.feeCents && <s className="ns-price-was">{money(payment.listCents)}</s>}{money(payment.feeCents)}</span></div>
      {(payment.lines || []).length > 0 && <div className="ns-bill-sub">Searches, at cost</div>}
      {(payment.lines || []).map((l, i) => (
        <div key={i} className="ns-price-row ns-bill-line"><span>{l.name}</span><span>{l.cents == null ? <span className="ns-hint">priced when ordered</span> : money(l.cents)}</span></div>
      ))}
      {!payment.lines && <div className="ns-price-row"><span>Property searches, at cost</span><span>{money(payment.searchesCents)}</span></div>}
      <div className="ns-price-row ns-price-total"><span>Total</span><span>{money(payment.totalCents)}</span></div>
    </div>
  );
}

function PromoCode({ track, onApplied }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!open) return <button type="button" className="ns-link ns-promo-open" onClick={() => setOpen(true)}>Have a promo code?</button>;
  const apply = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const { body } = await ask(`/sale/track/${encodeURIComponent(track.ref)}/${encodeURIComponent(track.token)}/promo`, { method: "POST", body: JSON.stringify({ code }) });
    setBusy(false);
    if (body.ok) { onApplied(body.payment); setOpen(false); setCode(""); } else setError(body.error || "That code could not be applied.");
  };
  return (
    <form className="ns-promo" onSubmit={apply}>
      <input className="ns-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Promo code" aria-label="Promo code" autoComplete="off" />
      <button type="submit" className="ns-btn soft" disabled={busy || !code.trim()}>{busy ? "Checking…" : "Apply"}</button>
      <button type="button" className="ns-link ns-promo-hide" onClick={() => { setOpen(false); setCode(""); setError(""); }}>Hide</button>
      {error && <p className="ns-error" role="alert" style={{ flexBasis: "100%" }}>{error}</p>}
    </form>
  );
}

// The start card's promo field — checked at once, so the estimate shows it;
// sent with the start (an agent can add their client's code here).
function StartPromo({ promo, onChange }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (promo) {
    return <p className="ns-hint" style={{ margin: 0 }}>Promo code applied. <button type="button" className="ns-link" onClick={() => onChange(null)}>Remove</button></p>;
  }
  if (!open) return <button type="button" className="ns-link ns-promo-open" onClick={() => setOpen(true)}>Have a promo code?</button>;
  const apply = async () => {
    setBusy(true); setError("");
    const { body } = await ask("/sale/promo", { method: "POST", body: JSON.stringify({ code }) });
    setBusy(false);
    if (body.ok) { onChange({ ...body, promo: body.promo }); setOpen(false); setCode(""); } else setError(body.error || "That code could not be applied.");
  };
  return (
    <div className="ns-promo">
      <input className="ns-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (code.trim()) apply(); } }} placeholder="Promo code" aria-label="Promo code" autoComplete="off" />
      <button type="button" className="ns-btn soft" disabled={busy || !code.trim()} onClick={apply}>{busy ? "Checking…" : "Apply"}</button>
      <button type="button" className="ns-link ns-promo-hide" onClick={() => { setOpen(false); setCode(""); setError(""); }}>Hide</button>
      {error && <p className="ns-error" role="alert" style={{ flexBasis: "100%" }}>{error}</p>}
    </div>
  );
}

export function payLabel(payment, busy) {
  if (busy) return "Opening…";
  return payment.totalCents > 0 ? `Pay ${money(payment.totalCents)}` : "Confirm — nothing to pay";
}

function PaymentCard({ track, payment: given, onPaid, title = "Invoice" }) {
  const [local, setLocal] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const acceptBox = useRef(null);
  useEffect(() => { setLocal(null); }, [given]);
  const payment = local || given;
  if (!payment || payment.status === "waived") return null;
  const pay = async () => {
    // The button always looks like a button; pressed before the terms are
    // accepted, it points at the box rather than doing nothing.
    if (payment.terms && !accepted) {
      setNudge(true);
      const el = acceptBox.current;
      if (el) { if (typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: prefersLessMotion() ? "auto" : "smooth", block: "center" }); el.focus({ preventScroll: true }); }
      return;
    }
    setBusy(true);
    setError("");
    const termsVersion = payment.terms ? payment.terms.version : undefined;
    const { body } = await ask(`/sale/track/${encodeURIComponent(track.ref)}/${encodeURIComponent(track.token)}/pay`, { method: "POST", body: JSON.stringify({ termsVersion }) });
    if (body.ok && body.url) { rememberPaying(track); window.location.assign(body.url); return; }
    setBusy(false);
    if (body.ok && body.payment) { setLocal(body.payment); if (onPaid) onPaid(body.payment); return; }
    if (body.payment) setLocal(body.payment);
    setError(body.error || "The payment page could not be opened. Please try again.");
  };
  return (
    <Card title={title}>
      {payment.status === "captured" && <p className="ns-msg">Paid {money(payment.capturedCents || payment.totalCents)}. Thank you.</p>}
      {payment.status === "authorised" && <p className="ns-msg">Invoice paid — thank you. Your conveyancer will take it from here.</p>}
      {payment.status === "not_required" && <p className="ns-msg">Nothing to pay{payment.promo ? ` — ${payment.promo.label || payment.promo.code}` : ""}. Your searches are being ordered.</p>}
      {payment.canPay && (
        <div className="ns-pay">
          <p className="ns-msg">{payment.status === "expired" ? "This invoice needs paying again." : "Your invoice for the Section 32. Nothing is ordered until it's paid."}</p>
          <div className="ns-pay-step"><span className="ns-pay-n">1</span>Check your invoice</div>
          <BillLines payment={payment} />
          <PromoCode track={track} onApplied={setLocal} />
          {payment.ocPending && <p className="ns-error" role="alert">Tick your owners corporations in the questions first — the invoice depends on them.</p>}
          {payment.terms && <div className="ns-pay-step"><span className="ns-pay-n">2</span>Accept the quote terms</div>}
          {payment.terms && <QuoteTerms terms={payment.terms} accepted={accepted} onAccept={(v) => { setAccepted(v); if (v) setNudge(false); }} attention={nudge} inputRef={acceptBox} />}
          <div className="ns-pay-step"><span className="ns-pay-n">{payment.terms ? 3 : 2}</span>{payment.totalCents > 0 ? "Pay by card" : "Confirm"}</div>
          {error && <p className="ns-error" role="alert">{error}</p>}
          <button type="button" className="ns-btn" onClick={pay} disabled={busy || payment.ocPending}>{payLabel(payment, busy)}<span className="ns-btn-arrow" aria-hidden="true">→</span></button>
          {/* Under the button, with Stripe's: reassurance where the card is asked for. */}
          <TrustLine />
        </div>
      )}
    </Card>
  );
}

// Where the agent's link stands on the bill: the vendor pays, never the agent.
function VendorPays({ payment }) {
  const line = payment.status === "authorised" || payment.status === "captured" ? "The vendor has paid the invoice."
    : payment.status === "not_required" || payment.status === "waived" ? "Nothing to pay on this one."
    : `Awaiting the vendor's payment${payment.totalCents ? ` — ${money(payment.totalCents)}` : ""}.`;
  return <Card title="Invoice"><p className="ns-msg" style={{ margin: 0 }}>{line}</p></Card>;
}

// A proposal's walkthrough plays once a visit by itself; the tracker offers it
// again after that.
const REPLAYED_STORE = "provey.sell.replayed";
const replayedHere = (ref) => { try { return (sessionStorage.getItem(REPLAYED_STORE) || "").split(",").includes(String(ref)); } catch (e) { return true; } };
const markReplayed = (ref) => { try { sessionStorage.setItem(REPLAYED_STORE, [...(sessionStorage.getItem(REPLAYED_STORE) || "").split(",").filter(Boolean), String(ref)].join(",")); } catch (e) { /* plays again next time */ } };

function Tracker({ track, returned = null, opened = null, onReplay = null }) {
  // The agent's optional answers, opened on request.
  const [answering, setAnswering] = useState(false);
  // Finished questions fold away; "Change answers" opens them again.
  const [editingAnswers, setEditingAnswers] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [savedAnswers, setSavedAnswers] = useState(false);
  useEffect(() => {
    if (!editingAnswers) return;
    const el = document.getElementById("ns-questions");
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [editingAnswers]);
  // **Two columns on a desktop**: the work on the left, and on the right the
  // conveyancer and sharing, which stay in view as the page scrolls.
  const wide = useWide(1024);
  const [view, setView] = useState(null);
  const [failed, setFailed] = useState(false);
  const [missing, setMissing] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [fresh, setFresh] = useState(new Set());
  const [landed, setLanded] = useState([]);
  // Read again at once after the questions: the bill follows the answers.
  const [reread, setReread] = useState(0);
  // A returned search open in the viewer.
  const [docOpen, setDocOpen] = useState(null);
  const last = useRef(null);

  useEffect(() => {
    let stopped = false;
    let timer = null;
    const read = async () => {
      clearTimeout(timer);
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const { status, body } = await ask(`/sale/track/${encodeURIComponent(track.ref)}/${encodeURIComponent(track.token)}`);
      if (stopped) return;
      if (status === 404) { setMissing(true); return; }
      if (!body.ok) { setFailed(true); } else {
        // A link that opened a matter is kept on this device, so a return
        // from Stripe (or a later visit) finds it whatever it came in on.
        if (!last.current) rememberTracked({ ref: track.ref, token: track.token, address: body.address || "" });
        setFailed(false);
        if (last.current) {
          const moved = changedSearches(last.current.searches, body.searches);
          if (moved.size) {
            setFresh(moved);
            setTimeout(() => !stopped && setFresh(new Set()), 4000);
          }
          const arrived = arrivals(last.current.searches, body.searches);
          if (arrived.length) {
            setLanded(arrived);
            setTimeout(() => !stopped && setLanded([]), 6000);
          }
        }
        // A proposal opens on its walkthrough for the vendor, once a visit —
        // not on a return from Stripe, never on a view-only link (the
        // middleware sends none for one), and never for the agent, who
        // watched it when they made the proposal ("Watch the walkthrough"
        // is still there).
        const agentHere = Boolean(body.questions && body.questions.who === "agent");
        if (!last.current && body.walkthrough && onReplay && !returned && !agentHere && !replayedHere(track.ref)) {
          markReplayed(track.ref);
          onReplay({ ...body.walkthrough, payment: body.payment && !body.payment.vendorPays ? body.payment : null, agent: Boolean(body.questions && body.questions.who === "agent") || Boolean(body.payment && body.payment.vendorPays) });
          return;
        }
        last.current = body;
        setView(body);
      }
      timer = setTimeout(read, TRACK_POLL_MS);
    };
    const onVisible = () => { if (document.visibilityState === "visible") read(); };
    read();
    document.addEventListener("visibilitychange", onVisible);
    const tick = setInterval(() => setNow(Date.now()), 5000);
    return () => { stopped = true; clearTimeout(timer); clearInterval(tick); document.removeEventListener("visibilitychange", onVisible); };
    // onReplay and returned are fixed for a mount; the read is per link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.ref, track.token, reread]);

  if (missing) {
    return <div className="ns-flow"><Card><h1 className="ns-h2">This link is not recognised</h1><p className="ns-msg">Check you have the whole link from when you opened the matter, or <a className="ns-link" href="/sell">start again</a>.</p></Card></div>;
  }
  if (!view) {
    return <div className="ns-flow"><Card><div className="ns-step ns-step-running"><span className="ns-spin" aria-hidden="true" /><div className="ns-step-title">Opening your matter…</div></div></Card></div>;
  }
  // **A lapsed proposal**: the vendor's link says so, and who to ask.
  if (view.expired) {
    const agentName = view.agent && view.agent.name ? `${view.agent.name}${view.agent.agency ? ` at ${view.agent.agency}` : ""}` : "your agent";
    return (
      <div className="ns-flow ns-reveal"><Card>
        <p className="ns-eyebrow">{displayAddress(view.address)}</p>
        <h1 className="ns-h2">This proposal has expired</h1>
        <p className="ns-msg">It was open for 60 days. If you'd still like us to prepare your Section 32 and contract of sale, ask {agentName} for a new one — or <a className="ns-link" href={salePath()}>start it here yourself</a>.</p>
      </Card></div>
    );
  }
  const { counts } = view;
  const agent = view.questions ? view.questions.who === "agent" : Boolean(view.payment && view.payment.vendorPays);
  const proposal = view.proposal && view.proposal.status === "awaiting_vendor" ? view.proposal : null;
  const until = proposal && proposal.expiresAt ? new Date(proposal.expiresAt).toLocaleDateString("en-AU", { day: "numeric", month: "long" }) : "";
  const questionsOpen = view.questions && ((!view.questions.complete && view.questions.missing.length > 0) || editingAnswers);
  const questionsDone = view.questions && !questionsOpen && (view.questions.complete || savedAnswers);
  // The conveyancer, and sharing the matter — with the agent, a co-vendor, or
  // themselves on another device. One card.
  // **One share on the agent's page while the proposal waits**: the vendor's
  // link, in the proposal card. A view-only share beside it reads as a second
  // way to send the same thing; it comes back once the vendor has accepted.
  const viewOnlyShare = !view.viewer && !(agent && proposal);
  const side = (view.conveyancer || viewOnlyShare) ? (
    <Card className="ns-side-card">
      {view.conveyancer && (
        <>
          <div className="ns-label">Your conveyancer</div>
          <div className="ns-person ns-person-side">
            <Portrait person={view.conveyancer} size={56} />
            <div>
              <div className="ns-person-name">{view.conveyancer.name}</div>
              <div className="ns-stats">{[view.conveyancer.role, view.conveyancer.yearsExperience != null ? `${view.conveyancer.yearsExperience} years' experience` : null].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
          {/* Once the matter is reviewed: their work email and the office's number. */}
          {view.conveyancer.contact && (
            <div className="ns-contact-lines">
              {view.conveyancer.contact.email && <a className="ns-link" href={`mailto:${view.conveyancer.contact.email}`}>{view.conveyancer.contact.email}</a>}
              {view.conveyancer.contact.phone && <a className="ns-link" href={`tel:${view.conveyancer.contact.phone.replace(/[^\d+]/g, "")}`}>{view.conveyancer.contact.phone}</a>}
            </div>
          )}
        </>
      )}
      {viewOnlyShare && (
        <div className={view.conveyancer ? "ns-side-share" : ""}>
          <TrackerShare track={track} address={view.address} already={view.sharedWith || []} />
        </div>
      )}
    </Card>
  ) : null;
  return (
    <div className={wide ? "ns-track-grid" : ""}>
    <div className="ns-flow ns-reveal">
      <div className="ns-flow-head">
        <div>
          <p className="ns-eyebrow">{view.viewer ? "Shared with you · view only" : agent && view.proposal ? "The vendor's sale" : "Your matter"}{view.matterRef ? ` · ${view.matterRef}` : ""}</p>
          <h1 className="ns-h2">{displayAddress(view.address)}</h1>
          {view.walkthrough && onReplay && (
            <button type="button" className="ns-link ns-replay" onClick={() => onReplay({ ...view.walkthrough, payment: view.payment && !view.payment.vendorPays ? view.payment : null, agent })}>Watch the walkthrough</button>
          )}
        </div>
        {/* A button to the updates themselves, where there are any. */}
        <button type="button" className={`ns-live${failed ? " off" : ""}`} title={failed ? "Could not reach Provey — trying again" : "Updates by itself while this page is open — see what changed"}
          disabled={!view.updates.length}
          onClick={() => { const el = document.getElementById("ns-updates"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
          <span className="ns-live-dot" aria-hidden="true" />{failed ? "Reconnecting…" : (() => { const u = updatedText(view.updates, now); return u ? `Live · ${u}` : "Live"; })()}
        </button>
      </div>

      {returned && !view.viewer && (
        <p className="ns-banner-note" role="status">{returned.paid
          ? "Thank you — your payment went through. It can take a moment to show here."
          : "Payment wasn't completed — nothing was charged. You can pay below whenever you're ready."}</p>
      )}
      {opened && !returned && (
        <p className="ns-banner-note" role="status">Your matter is open{view.matterRef ? ` — reference ${view.matterRef}` : ""}. {view.payment && view.payment.canPay ? "Your invoice is below; we order the searches once it's paid." : "We'll keep this page up to date."}</p>
      )}

      {view.proposal && view.proposal.status === "expired" && agent && (
        <Card title="Proposal expired">
          <p className="ns-msg" style={{ margin: 0 }}>The vendor didn't accept it within 60 days, so it has lapsed and their link no longer works. To try again, prepare a new proposal.</p>
        </Card>
      )}

      {proposal && agent && (
        <Card title="Proposal — awaiting the vendor" className="ns-card-cta">
          <p className="ns-msg" style={{ marginTop: 0 }}>{proposal.vendorDetailsGiven
            ? "The vendor has given their details. Once they accept and pay, we order everything."
            : "Send the vendor their link. They accept, answer a few questions and pay — then we order everything."}{until ? ` Open until ${until}.` : ""}</p>
          <ProposalShare track={track} address={view.address} />
        </Card>
      )}

      {view.payment && view.payment.vendorPays && <VendorPays payment={view.payment} />}

      {/* Answering for the vendor is the agent's; someone helping them sends the link. */}
      {questionsOpen && agent && view.proposal && !answering && view.startedBy !== "other" && (
        <Card title="Answer for the vendor">
          <p className="ns-msg" style={{ marginTop: 0 }}>Optional. Answer what you know — the vendor sees it marked as from you, and can change it.</p>
          <button type="button" className="ns-btn soft" onClick={() => setAnswering(true)}>Answer for the vendor</button>
        </Card>
      )}

      {questionsOpen && (!agent || !view.proposal || answering) && (
        <SaleQuestions track={track} view={view.questions}
          intro={view.questions.who === "agent"
            ? "Answer what you know. The vendor is asked the rest — building works, services, easements and anything a buyer should know."
            : "About five minutes. \"Not sure\" is a fine answer."}
          onDone={() => { setAnswering(false); setEditingAnswers(false); setSavedAnswers(true); setView(v => ({ ...v, questions: { ...v.questions, missing: [] } })); setReread(n => n + 1); }}
          onClose={editingAnswers ? () => { setEditingAnswers(false); setReread(n => n + 1); } : null} />
      )}


      {/* **The invoice once the questions are done**, under them: the vendor
          answers, then pays, and nothing asks them for money mid-form. */}
      {view.payment && view.payment.canPay && !questionsOpen && (
        <div className="ns-reveal"><PaymentCard track={track} payment={view.payment} title="Invoice — the last step" /></div>
      )}

      {view.lease && !view.viewer && !questionsOpen && <TrackerLease track={track} lease={view.lease} />}

      <Card title="Where it's up to">
        <ol className="ns-next ns-track-steps">
          {view.steps.map((s, i) => (
            <li key={s.key} className={s.state}>
              <span className="ns-next-n">{s.state === "done"
                ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
                : i + 1}</span>
              <div><b>{s.title}</b>{s.state === "current" && <span className="ns-now">In progress</span>}
                {s.key === "form27" && s.state === "done" && questionsDone && (!agent || !view.proposal)
                  ? <>
                    <div className="ns-step-line">{savedAnswers ? "Saved — thank you." : "Answered."}</div>
                    <div className="ns-step-actions">
                      {view.questions.summary && view.questions.summary.length > 0 && (
                        <button type="button" className="ns-link ns-inline-link" aria-expanded={showAnswers} onClick={() => setShowAnswers(v => !v)}>{showAnswers ? "Hide answers" : "View answers"}</button>
                      )}
                      <button type="button" className="ns-link ns-inline-link" onClick={() => { setShowAnswers(false); setEditingAnswers(true); }}>Change answers</button></div>
                    {showAnswers && <AnswersSummary groups={view.questions.summary} />}
                  </>
                  : <div className="ns-step-line">{s.line}</div>}</div>
            </li>
          ))}
        </ol>
      </Card>

      {view.searches.length > 0 && (
        <Card title="Order queue">
          <div className="ns-queue-head">
            <span className="ns-queue-count"><b><CountUp value={counts.received} /></b> of {counts.total} in</span>
            <span className="ns-live-mini"><span className="ns-live-dot" aria-hidden="true" />Live</span>
          </div>
          <div className="ns-bar" role="img" aria-label={`${counts.received} of ${counts.total} searches received`}>
            {barSegments(view.searches).map((st, i) => <span key={i} className={`ns-seg ${st}`} />)}
          </div>
          <p className="ns-bar-text">{counts.ordered > counts.received ? `${counts.ordered - counts.received} ordered and on the way` : counts.received === counts.total && counts.total ? "Every search is in." : "Ordered once your conveyancer has reviewed the matter."}</p>
          <ul className="ns-searches ns-queue-list">
            {queueRows(view.searches).map(({ s, at: i }) => (
              <li key={`${i}:${s.name}`} className={`${fresh.has(`${i}:${s.name}`) ? "ns-fresh" : ""}${s.state === "received" ? " in" : ""}`}>
                <span className="ns-search-name"><span className="ns-badge" aria-hidden="true">{initialsOf(s.issuedBy)}</span>
                  <span>{s.name}{s.for ? ` — ${s.for}` : ""}<span className="ns-issuer-under">{s.issuedBy}{s.free ? " · free, fetched by us" : ""}</span>
                    {s.doc && !view.viewer && (
                      <button type="button" className="ns-link ns-view-doc" onClick={() => setDocOpen({ id: s.doc, title: `${s.name}${s.for ? ` — ${s.for}` : ""}` })}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>View document</button>
                    )}</span></span>
                <QueueTrack state={s.state} />
              </li>
            ))}
          </ul>
          {docOpen && <DocViewer title={docOpen.title} onClose={() => setDocOpen(null)}
            src={`${MIDDLEWARE_URL}/sale/track/${encodeURIComponent(track.ref)}/${encodeURIComponent(track.token)}/doc/${encodeURIComponent(docOpen.id)}`} />}
        </Card>
      )}

      {landed.length > 0 && (
        <div className="ns-toast" role="status">
          <span className="ns-toast-ico" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M9 15l2 2 4-4" /></svg></span>
          <span><b>{landed.length === 1 ? landed[0] : `${landed.length} searches`}</b> just arrived</span>
        </div>
      )}

      {!wide && side}

      {view.draft && view.draft.preview && (
        <TrackerDraft track={track} view={view} />
      )}

      {view.updates.length > 0 && (
        <div id="ns-updates"><Card title="Updates">
          <ul className="ns-updates">
            {view.updates.map((u, i) => (
              <li key={`${u.at}-${i}`}><span className="ns-update-at">{u.at ? new Date(u.at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : ""}</span><span>{u.line}</span></li>
            ))}
          </ul>
        </Card></div>
      )}
    </div>
    {wide && side && <aside className="ns-track-side">{side}</aside>}
    </div>
  );
}

// ── The draft ───────────────────────────────────────────────────────────────
//
// The firm's own contract template, printed by the middleware from what the
// page found and watermarked DRAFT — NOT FOR SIGNING on every page. Shown as
// a scaled page with the filled values tinted, and full size on request. The
// frame is sandboxed with nothing allowed: the draft runs no script and
// reaches nothing. See the middleware's docs/decisions/new-sale.md, "The draft".
// **Pages, as the staff draft shows them.** The HTML is loaded into a frame
// that allows same-origin access and nothing else — no script runs in it —
// and the page reaches in to break it into A4 sheets with the staff draft's
// own `paginate` and `fillPages` (contractPages.js). The sheets are then
// scaled to the frame with `zoom`, never a transform, so a phone shows a
// whole page at reading size rather than the document re-flowed at 16px.
const SHEET_PX = 595.3 * 4 / 3;
const SHEET_GAP_PX = 18;
const PUBLIC_DRAFT_CSS = `
  body { margin: 0 !important; }
  .ns-draft-note { box-sizing: border-box; margin: 0 auto 14pt !important; }
  body.ns-two .contract { display: flex; flex-wrap: wrap; justify-content: center; align-items: flex-start; gap: 0 14pt; }
  body.ns-two .sheet { margin: 0 0 14pt; flex: none; }
  body.ns-live .f[data-field] { cursor: pointer; transition: box-shadow .12s; }
  body.ns-live .f[data-field]:hover, body.ns-live .f[data-field].ns-at { box-shadow: 0 0 0 2px #5847D6 !important; }
`;

// What a value in the draft is, for the read-only popover: its label (the
// middleware's registry label, on the span), what it prints, and where on
// the screen to open. Null for anything that is not a value.
export function fieldAt(target, frameRect = { left: 0, top: 0 }) {
  const el = target && target.closest ? target.closest(".f[data-field]") : null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    el,
    label: el.getAttribute("data-label") || "Contract field",
    value: (el.textContent || "").replace(/\s+/g, " ").trim(),
    left: frameRect.left + r.left,
    top: frameRect.top + r.bottom,
  };
}

// How many sheets across, and at what size, for a frame this wide.
export function draftLayout(width, { twoUp = false } = {}) {
  const w = Math.max(0, Number(width) || 0) - SHEET_GAP_PX * 2;
  const pages = twoUp && w >= 560 ? 2 : 1;
  const zoom = Math.min(1, w / (pages * SHEET_PX + (pages - 1) * 18.7)) || 0.3;
  return { pages, zoom: Math.round(zoom * 1000) / 1000 };
}

function DraftDocument({ html, preview = false, title, showcase = false, focus = null }) {
  const frame = useRef(null);
  const [laid, setLaid] = useState(0);
  const [field, setField] = useState(null);
  const palette = useContext(Palette);

  // **The chapter's fields light up** (/sellv2): what the chapter on screen
  // is about, found in the draft and brought into view, so the contract reads
  // as built from what the visitor is reading.
  useEffect(() => {
    const doc = frame.current && frame.current.contentDocument;
    const win = frame.current && frame.current.contentWindow;
    if (!doc || !win || !laid) return undefined;
    const all = [...doc.querySelectorAll(".f[data-field]")];
    const lit = new Set(litFields(all.map(el => el.getAttribute("data-field")), focus));
    all.forEach(el => el.classList.toggle("ns-lit", lit.has(el.getAttribute("data-field"))));
    const first = all.find(el => lit.has(el.getAttribute("data-field")));
    if (!first || typeof requestAnimationFrame !== "function") return undefined;
    const from = win.scrollY;
    const to = Math.max(0, first.getBoundingClientRect().top + from - win.innerHeight * 0.3);
    if (prefersLessMotion()) { win.scrollTo(0, to); return undefined; }
    let raf = 0;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / 600);
      win.scrollTo(0, from + (to - from) * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [focus, laid]);

  // **A real document, scrolled.** When its stop comes up, the small draft
  // runs down to its third page and back, so it reads as pages of a contract
  // rather than a picture of one. Once per showing; a hand on it stops it.
  useEffect(() => {
    if (!showcase || !laid || !preview) return undefined;
    const win = frame.current && frame.current.contentWindow;
    const doc = frame.current && frame.current.contentDocument;
    if (!win || !doc || typeof requestAnimationFrame !== "function") return undefined;
    const sheets = doc.querySelectorAll(".sheet");
    const target = sheets[2] || sheets[sheets.length - 1];
    if (!target) return undefined;
    const to = Math.max(0, target.getBoundingClientRect().top + win.scrollY - 12);
    if (to < 40) return undefined;
    let raf = 0;
    let stopped = false;
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const glide = (from, dest, ms) => new Promise((resolve) => {
      const start = performance.now();
      const step = (now) => {
        if (stopped) return resolve();
        const t = Math.min(1, (now - start) / ms);
        win.scrollTo(0, from + (dest - from) * ease(t));
        if (t < 1) raf = requestAnimationFrame(step); else resolve();
      };
      raf = requestAnimationFrame(step);
    });
    const wait = (ms) => new Promise(r => setTimeout(r, ms));
    (async () => {
      await wait(700);
      if (stopped) return;
      await glide(win.scrollY, to, 2600);
      await wait(1000);
      if (stopped) return;
      await glide(win.scrollY, 0, 1600);
    })();
    return () => { stopped = true; cancelAnimationFrame(raf); };
  }, [showcase, laid, preview]);
  const lay = () => {
    const doc = frame.current && frame.current.contentDocument;
    if (!doc || !doc.body) return;
    const { pages, zoom } = draftLayout(doc.documentElement.clientWidth, { twoUp: preview });
    const contract = doc.querySelector(".contract");
    doc.body.classList.toggle("ns-two", pages === 2);
    if (contract) contract.style.zoom = String(zoom);
    else doc.body.style.zoom = String(zoom);
    // The note sits above the pages, as wide as they are; in the small card it
    // shrinks with them, and full size it stays readable.
    const note = doc.querySelector(".ns-draft-note");
    const across = pages * SHEET_PX + (pages - 1) * 18.7;
    if (note && preview) { note.style.zoom = String(zoom); note.style.maxWidth = `${across}px`; }
    else if (note) note.style.maxWidth = `${across * zoom}px`;
  };
  const wire = () => {
    const doc = frame.current && frame.current.contentDocument;
    if (!doc || !doc.head) return;
    if (!doc.getElementById("ns-pages")) {
      const style = doc.createElement("style");
      style.id = "ns-pages";
      // The small preview leaves out the diagonal marks — at that size they
      // are noise over the pages; the note above them stays, and the full
      // draft (and any print of it) keeps every mark.
      style.textContent = SHEET_CSS + PUBLIC_DRAFT_CSS + draftTint(palette) + (preview ? ".ns-draft-mark { display:none !important; }" : "");
      doc.head.appendChild(style);
    }
    // **Full size, a value opens what the office would edit there** — greyed,
    // because only staff edit it; it shows the draft is a live contract.
    if (!preview && !doc.body.classList.contains("ns-live")) {
      doc.body.classList.add("ns-live");
      doc.addEventListener("click", (e) => {
        const at = fieldAt(e.target, frame.current.getBoundingClientRect());
        doc.querySelectorAll(".ns-at").forEach(x => x.classList.remove("ns-at"));
        if (at) at.el.classList.add("ns-at");
        setField(at);
      });
      doc.addEventListener("scroll", () => setField(null), { passive: true });
    }
    paginate(doc);
    fillPages(doc);
    lay();
    setLaid(n => n + 1);
  };
  useEffect(() => {
    const el = frame.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => { fillPages(el.contentDocument); lay(); });
    ro.observe(el);
    return () => ro.disconnect();
  });
  return (
    <>
      <iframe ref={frame} title={title} sandbox="allow-same-origin" srcDoc={html} onLoad={wire}
        className={preview ? "ns-draft-frame" : "ns-viewer-frame"} scrolling={preview ? "no" : undefined} />
      {field && <FieldPeek field={field} onClose={() => { field.el.classList.remove("ns-at"); setField(null); }} />}
    </>
  );
}

// The staff popover, shown and not usable: the label, what prints, a locked
// box, and who changes it.
export function peekPlacement(field, view = { width: 1024, height: 768 }) {
  // **A phone gets a sheet along the bottom**, not a box beside the value: the
  // draft is zoomed down to fit, and Safari reports a zoomed value's place
  // unzoomed, so a box placed by it opened somewhere off the screen.
  if (view.width < 640) return { left: 8, right: 8, bottom: 12, width: "auto" };
  const width = 300;
  const left = Math.max(8, Math.min(field.left, view.width - width - 8));
  const below = field.top + 8;
  const top = below + 170 > view.height ? field.top - 200 : below;
  // Never off the screen, wherever the value is said to be.
  return { left, top: Math.max(8, Math.min(top, view.height - 210)), width };
}

function FieldPeek({ field, onClose }) {
  const style = peekPlacement(field, { width: window.innerWidth, height: window.innerHeight });
  return createPortal(
    <div className="ns-peek" role="dialog" aria-label={field.label} style={style} onClick={e => e.stopPropagation()}>
      <div className="ns-peek-head">
        <span className="ns-peek-label">{field.label}</span>
        <span className="ns-peek-lock">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>
          Staff only
        </span>
      </div>
      <input className="ns-peek-input" value={field.value} placeholder="Not filled in yet" disabled readOnly aria-label={field.label} />
      <div className="ns-peek-actions"><span className="ns-peek-btn">Cancel</span><span className="ns-peek-btn ns-peek-save">Save</span></div>
      <p className="ns-peek-note">This is a live contract. Your conveyancer checks and edits every field here before anything is sent.</p>
      <button type="button" className="ns-peek-close" onClick={onClose} aria-label="Close">×</button>
    </div>,
    document.body,
  );
}

// A returned search, read on the page: the PDF inline in the same sheet the
// draft opens in, and only there — no link out to a bare copy.
function DocViewer({ src, title, onClose }) {
  useEffect(() => {
    const key = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  return createPortal(
    <div className="ns-viewer" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="ns-viewer-sheet" onClick={e => e.stopPropagation()}>
        <div className="ns-viewer-bar">
          <span className="ns-viewer-title">{title}</span>
          <button type="button" className="ns-btn soft" onClick={onClose}>Close</button>
        </div>
        <iframe className="ns-viewer-frame" title={title} src={src} />
      </div>
    </div>,
    document.body,
  );
}

function DraftViewer({ html, onClose }) {
  useEffect(() => {
    const key = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  // At the top of the document, not inside the card: a card that has risen
  // into view keeps a transform, and a transform traps anything fixed inside
  // it to the card.
  return createPortal(
    <div className="ns-viewer" role="dialog" aria-modal="true" aria-label="Draft contract" onClick={onClose}>
      <div className="ns-viewer-sheet" onClick={e => e.stopPropagation()}>
        <div className="ns-viewer-bar">
          <span className="ns-viewer-title">Draft contract of sale <span className="ns-viewer-sub">not for signing</span></span>
          <button type="button" className="ns-btn soft" onClick={onClose}>Close</button>
        </div>
        <DraftDocument html={html} title="Draft contract, full size" />
      </div>
    </div>,
    document.body,
  );
}

// The card: two pages side by side on a wide screen, one on a phone, on the
// grey desk the staff draft uses; the whole card opens the full draft.
function DraftPanel({ html, title, intro, footer, onOpenChange, showcase = false, focus = null }) {
  const [open, setOpen] = useState(false);
  const show = (v) => { setOpen(v); if (onOpenChange) onOpenChange(v); };
  return (
    <Card title={title} className="ns-card-draft">
      <p className="ns-msg" style={{ margin: "-6px 0 16px" }}>{intro}</p>
      {html == null ? <Drafting /> : (
        <div className="ns-draft-stage" role="button" tabIndex={0} aria-label="Open the full draft"
          onClick={() => show(true)} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); show(true); } }}>
          <DraftDocument html={html} preview title="Draft contract" showcase={showcase && !open} focus={focus} />
          <div className="ns-draft-fade" />
          <span className="ns-btn soft ns-draft-open" aria-hidden="true">Open full draft</span>
        </div>
      )}
      {footer}
      {open && <DraftViewer html={html} onClose={() => show(false)} />}
    </Card>
  );
}

// While the draft is being made: a page with its clauses being typed in, and
// what it is doing said plainly. Real headings of the contract, so what is
// typed is what is coming.
export const DRAFTING_LINES = [
  "Contract of Sale of Real Estate",
  "Particulars of sale",
  "Vendor: as named on the title",
  "Land: the lot and plan you confirmed",
  "Planning: zone, overlays, bushfire prone area",
  "General conditions — Law Institute of Victoria",
];

// **What is typed is this property's cover page** — the contract's own title
// and particulars, from what the checks have found so far — rather than words
// about a contract. Anything not found yet is left out.
export function draftingLines(subject = {}) {
  const lines = ["Contract of Sale of Real Estate"];
  if (subject.address) lines.push(`Property address: ${subject.address}`);
  if (subject.lots && subject.lots.length) lines.push(`Land: ${subject.lots.join(", ")}`);
  if (subject.council) lines.push(`Council: ${subject.council}`);
  if (subject.zones && subject.zones.length) lines.push(`Zone: ${subject.zones.join(", ")}`);
  lines.push("Vendor: as named on the title");
  lines.push("General conditions — Law Institute of Victoria");
  return lines.length > 3 ? lines : DRAFTING_LINES;
}
const DraftSubject = createContext({});

function Drafting() {
  const subject = useContext(DraftSubject);
  const LINES = draftingLines(subject);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 45);
    return () => clearInterval(t);
  }, []);
  // Characters typed so far across the lines, each line finishing before the
  // next starts; the whole set loops once it has all been typed.
  const total = LINES.reduce((n, l) => n + l.length + 8, 0);
  let left = tick % total;
  const typed = LINES.map((line) => {
    const take = Math.max(0, Math.min(line.length, left));
    left -= line.length + 8;
    return line.slice(0, take);
  });
  const current = typed.findIndex((t, i) => t.length < LINES[i].length);
  return (
    <div className="ns-drafting" aria-live="polite">
      <div className="ns-drafting-head"><span className="ns-spark" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.8L20 11l-6.1 2.2L12 19l-1.9-5.8L4 11l6.1-2.2z" /></svg>
      </span>Contract is being drafted…</div>
      <div className="ns-drafting-page" aria-hidden="true">
        {typed.map((t, i) => (
          <p key={i} className={i === 0 ? "ns-drafting-title" : i === 1 && subject.address ? "ns-drafting-address" : ""}>{t}{i === current && <span className="ns-caret" />}</p>
        ))}
      </div>
    </div>
  );
}

// The draft's HTML, fetched rather than framed by URL: the frame has to be
// same-origin for the page to lay it out.
function useDraftHtml(load, deps) {
  const [html, setHtml] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const res = await load();
        if (!res.ok) throw new Error(String(res.status));
        const text = await res.text();
        if (!gone) setHtml(text);
      } catch (e) {
        if (!gone) setFailed(true);
      }
    })();
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { html, failed };
}

function DraftCard({ draft, onOpenChange, showcase, ready = true, eyebrow = null, focus = null }) {
  if (draft && !draft.ok) return null;
  const html = ready && draft ? draft.html : null;
  return (
    <DraftPanel html={html} title={<>{eyebrow}Your contract, taking shape</>} onOpenChange={onOpenChange} showcase={showcase} focus={focus}
      intro={<>Your contract of sale, drafted from what we've found so far. The <span className="ns-tint">tinted</span> details are already filled in;
        your conveyancer completes the rest from the title and your answers.</>} />
  );
}

// ── /sellv2: the brand's colours, and the story told by scrolling ──────────

// Which palette fills the page: T's sell* on /sell, the brand's on /sellv2.
// The stylesheet reads them as custom properties, so one sheet serves both.
const Palette = createContext(T);
export const paletteCss = (P) => `:root{${Object.keys(P).filter(k => k.startsWith("sell")).map(k => `--${k}:${P[k]};`).join("")}}`;

// The draft's own tints in the page's palette (the middleware tints its
// values violet; /sellv2 is coral), and a chapter's fields lit.
const draftTint = (P) => `
  .ns-draft-mark span { color:${P.sellMark} !important; }
${P !== T ? `  html, body { background:${P.sellCream} !important; }
` : ""}${P !== T ? `  .ns-draft-note { border-color:${P.sellLineStrong} !important; background:${P.sellWash} !important; color:${P.sellInk} !important; }` : ""}
  @media screen { [data-field] { background:${P.sellFieldTint} !important; box-shadow:0 0 0 2px ${P.sellFieldTint} !important; } }
  .f.ns-lit { background:${P === T ? P.sellRedSoft : P.sellLavenderDeep} !important; box-shadow:0 0 0 2px ${P === T ? P.sellGlow : P.sellFaint} !important; border-radius:3px; transition:background .4s, box-shadow .4s; }
`;

// The fields each chapter is about, by registry name.
export const CHAPTER_FIELDS = {
  land: /^(property\.|s32\.(title|planning|bushfire|responsibleAuthority))/,
  searches: /^s32\./,
  conveyancer: /^firm\./,
  next: /^sale\./,
  start: /^vendor\./,
};
export const litFields = (names = [], key) => {
  const re = key && CHAPTER_FIELDS[key];
  return re ? names.filter(n => re.test(n)) : [];
};

// How far each chapter is from the middle of the screen (`--p`, -1 above to
// 1 below) and how clear it should be (`--o`), set every frame something
// scrolls; the stylesheet moves layers by them at different speeds. The
// pointer's place (`--px`, `--py`) tilts the hero's layers. `onActive` hears
// which chapter is nearest the middle.
function useDepth(active, onActive, deps = []) {
  const heard = useRef(onActive);
  heard.current = onActive;
  useEffect(() => {
    if (!active || typeof window === "undefined") return undefined;
    const root = document.documentElement;
    const still = prefersLessMotion();
    let raf = 0;
    let last = -1;
    const frame = () => {
      raf = 0;
      const h = window.innerHeight || 1;
      if (!still) root.style.setProperty("--sy", String(Math.round(window.scrollY)));
      let best = -1;
      let near = Infinity;
      document.querySelectorAll("[data-depth]").forEach((el) => {
        const r = el.getBoundingClientRect();
        const p = Math.max(-1, Math.min(1, (r.top + r.height / 2 - h / 2) / h));
        if (!still) {
          el.style.setProperty("--p", p.toFixed(3));
          el.style.setProperty("--o", Math.max(0.25, Math.min(1, 1 - (Math.abs(p) - 0.45) * 1.6)).toFixed(3));
        }
        if (Math.abs(p) < 0.5) el.classList.add("seen");
        const ch = el.getAttribute("data-ch");
        if (ch != null && Math.abs(p) < near) { near = Math.abs(p); best = Number(ch); }
      });
      if (best !== last && best >= 0) { last = best; if (heard.current) heard.current(best); }
    };
    const ask = () => { if (!raf) raf = requestAnimationFrame(frame); };
    const point = (e) => {
      root.style.setProperty("--px", ((e.clientX / window.innerWidth) * 2 - 1).toFixed(3));
      root.style.setProperty("--py", ((e.clientY / window.innerHeight) * 2 - 1).toFixed(3));
    };
    frame();
    window.addEventListener("scroll", ask, { passive: true });
    window.addEventListener("resize", ask);
    if (!still) window.addEventListener("pointermove", point, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", ask);
      window.removeEventListener("resize", ask);
      window.removeEventListener("pointermove", point);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ...deps]);
}

// The hero's depth: what the page makes, as paper floating at different
// distances behind the address box — the title, the planning map, the
// contract, a conveyancer — drifting with the scroll and the pointer.
// Stylised, never a real record or a real person.
function HeroDepth() {
  const lines = (n, w = []) => Array.from({ length: n }, (_, i) => <i key={i} style={{ width: `${w[i % w.length] || 100}%` }} />);
  return (
    <div className="ns-depth" aria-hidden="true">
      <div className="ns-float f1" style={{ "--d": 1.1 }}><div className="ns-float-in">
        <div className="ns-paper">
          <span className="ns-paper-eyebrow">Register search statement</span>
          <b className="ns-paper-title">Volume 10234 Folio 118</b>
          <span className="ns-paper-lines">{lines(4, [92, 70, 84, 56])}</span>
        </div>
      </div></div>
      <div className="ns-float f2" style={{ "--d": 0.6 }}><div className="ns-float-in">
        <div className="ns-paper ns-map">
          <svg viewBox="0 0 160 110" width="100%" height="100%">
            <path d="M0 28h160M0 78h160M46 0v110M118 0v110" stroke="currentColor" strokeOpacity=".14" strokeWidth="7" />
            <path d="M58 38l42-6 8 34-44 8z" className="ns-map-lot" />
          </svg>
          <span className="ns-chip">GRZ1 · General Residential</span>
        </div>
      </div></div>
      <div className="ns-float f3" style={{ "--d": 1.5 }}><div className="ns-float-in">
        <div className="ns-paper ns-contract">
          <b className="ns-contract-title">Contract of Sale of Real Estate</b>
          <span className="ns-paper-lines">{lines(2, [88, 64])}</span>
          <span className="ns-contract-row"><em>Vendor</em><mark /></span>
          <span className="ns-contract-row"><em>Land</em><mark style={{ width: "58%" }} /></span>
          <span className="ns-paper-lines">{lines(3, [96, 78, 60])}</span>
        </div>
      </div></div>
    </div>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

// **The property manager's page** (`#lease=`): the address, and a place to
// put the lease. Nothing else about the sale is on it, or reachable from it.
function LeaseUpload({ lease }) {
  const [info, setInfo] = useState(null);
  const [value, setValue] = useState(null);
  useEffect(() => {
    let gone = false;
    ask(`/sale/lease/${encodeURIComponent(lease.ref)}/${encodeURIComponent(lease.token)}`).then(({ body }) => { if (!gone) setInfo(body); });
    return () => { gone = true; };
  }, [lease.ref, lease.token]);
  if (!info) return <Card><p className="ns-msg">Loading…</p></Card>;
  if (!info.ok) return <Card><h1 className="ns-h2">This link is not recognised</h1><p className="ns-msg">{info.error || "Please reply to the email with the lease attached instead."}</p></Card>;
  const sent = Boolean(value) || info.received > 0;
  return (
    <Card>
      <h1 className="ns-h2">The lease for {info.address || "the property"}</h1>
      <p className="ns-msg">{info.firmName || "We"} {info.firmName ? "is" : "are"} preparing the contract of sale and Section 32 for the owner. Please upload the current lease, with any renewals or variations — a PDF, or a photo of each page.</p>
      <UploadButton url={`${MIDDLEWARE_URL}/sale/lease/${encodeURIComponent(lease.ref)}/${encodeURIComponent(lease.token)}`}
        label="The lease" value={value} onChange={setValue} first="Upload the lease" again="Add another file or page" />
      {sent && <p className="ns-msg">Thank you — {value ? "it's with the conveyancer now" : "we have the lease"}. Add anything else that belongs with it the same way.</p>}
      <p className="ns-hint" style={{ marginTop: 14 }}>Rather email it? Reply to our email with the lease attached.</p>
    </Card>
  );
}

export default function NewSale({ v2 = false } = {}) {
  const [query, setQuery] = useState("");
  const [lookup, setLookup] = useState(null);
  const [looking, setLooking] = useState(false);
  const [pick, setPick] = useState(null);
  const [answers, setAnswers] = useState({});
  const [live, setLive] = useState({});
  const [shown, setShown] = useState(0);
  const [form, setForm] = useState({ role: "vendor", consent: false });
  // **The button is never dead**: pressed without the box ticked, the box is
  // pointed at and says why, where a greyed button only looked broken.
  const [consentNudge, setConsentNudge] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [questionsDone, setQuestionsDone] = useState(false);
  const [startPromo, setStartPromo] = useState(null);
  // A matter the vendor just opened, for the tracker to say so.
  const [opened, setOpened] = useState(null);
  const [back] = useState(() => (typeof window === "undefined" ? null : returnedFromStripe(window.location.search, readTracked(), readPaying())));
  const [track, setTrack] = useState(() => {
    if (typeof window === "undefined") return null;
    if (back && back.track) {
      try { window.history.replaceState(null, "", `${window.location.pathname}#t=${back.track.ref}.${back.track.token}`); } catch (e) { /* the link still works */ }
      return back.track;
    }
    return trackFromHash(window.location.hash);
  });
  const [tracked] = useState(readTracked);
  const [leaseLink, setLeaseLink] = useState(() => (typeof window === "undefined" ? null : leaseFromHash(window.location.hash)));
  // **The opening walkthrough, replayed** from a proposal's tracker: the page
  // runs its own checks and chapters for the matter's property, and ends on
  // "See your proposal" instead of the start form.
  const [replay, setReplay] = useState(null);
  const seq = useRef(0);
  const typed = useTypewriter(!pick && !query);

  useEffect(() => {
    const onHash = () => { setTrack(trackFromHash(window.location.hash)); setLeaseLink(leaseFromHash(window.location.hash)); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // The address, asked as the person types — after a pause, and only once it
  // could be an address. The latest answer wins.
  useEffect(() => {
    if (pick) return undefined;
    const q = query.trim();
    if (q.length < 6) { setLookup(null); return undefined; }
    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      setLooking(true);
      const { body } = await ask(`/sale/lookup?address=${encodeURIComponent(q)}`);
      if (mine !== seq.current) return;
      setLooking(false);
      setLookup(body);
    }, 400);
    return () => clearTimeout(timer);
  }, [query, pick]);

  // Once a property is picked, every question is asked at once and each step
  // ticks when its own answer lands.
  useEffect(() => {
    if (!pick) return;
    const place = { address: pick.address, propertyPfi: pick.propertyPfi };
    const store = (key) => ({ body }) => setAnswers(a => ({ ...a, [key]: body }));
    // **Not on the map**: the estimate and the conveyancer; the land's own
    // checks and the draft wait for the conveyancer to confirm the property.
    if (pick.unlisted) {
      setAnswers(a => ({ ...a, planning: { ok: false, unlisted: true } }));
      ask("/sale/property", { method: "POST", body: JSON.stringify({ address: pick.address, unlisted: true }) }).then(store("property"));
      ask("/sale/conveyancer").then(store("conveyancer"));
      return;
    }
    const where = JSON.stringify(place);
    ask("/sale/property", { method: "POST", body: where }).then(store("property"));
    askPlanningLive(place, (line) => setLive(l => (
      line.type === "shape" ? { ...l, shape: line.shape }
        : line.type === "overlays" ? { ...l, overlays: line }
          : line.type === "zones" ? { ...l, zones: line.zones }
            : line.type === "bushfire" ? { ...l, bushfire: line }
              : l
    ))).then(store("planning"));
    ask("/sale/conveyancer").then(store("conveyancer"));
  }, [pick]);

  // The first draft, once the property and the planning answer are in — the
  // draft prints the planning answer the middleware has just remembered. It
  // is the last check, and the draft panel shows what it fetched.
  // Waits for the conveyancer too: the draft prints them beside the reference.
  const [chosenId, setChosenId] = useState(null);
  const draftConveyancer = chosenConveyancer(answers.conveyancer, chosenId);
  const draftConveyancerId = draftConveyancer ? draftConveyancer.id : null;
  const readyToDraft = Boolean(pick && answers.property && answers.planning && answers.conveyancer);
  useEffect(() => {
    if (!readyToDraft) return undefined;
    let gone = false;
    (async () => {
      try {
        const res = await saleFetch("/sale/draft", { method: "POST", body: JSON.stringify({ address: pick.address, propertyPfi: pick.propertyPfi, ...(pick.unlisted ? { unlisted: true } : {}), ...(draftConveyancerId ? { conveyancerId: draftConveyancerId } : {}) }) });
        if (!res.ok) throw new Error(String(res.status));
        const html = await res.text();
        if (!gone) setAnswers(a => ({ ...a, draft: { ok: true, html } }));
      } catch (e) {
        if (!gone) setAnswers(a => ({ ...a, draft: { ok: false } }));
      }
    })();
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToDraft, draftConveyancerId]);

  // Steps are revealed one after another, so the person sees each being
  // worked on; a step still only ticks when its answer is in.
  useEffect(() => {
    if (!pick || shown >= STEPS.length) return undefined;
    const current = STEPS[shown];
    if (!stepResult(current.key, answers)) return undefined;
    const timer = setTimeout(() => setShown(n => n + 1), STEP_MS);
    return () => clearTimeout(timer);
  }, [pick, shown, answers]);

  const finished = pick && shown >= STEPS.length;
  const wide = useWide();
  const stops = tourFor({ conveyancer: answers.conveyancer && answers.conveyancer.ok && answers.conveyancer.conveyancer, wide });
  // **The page opens out once drafting starts**: on a wide screen, the draft
  // takes its own column from the moment its check begins, typing until the
  // draft lands and the check ticks.
  const draftAt = STEPS.findIndex(st => st.key === "draft");
  const split = wide && pick && !done && shown >= draftAt && answers.property && answers.property.ok;
  const draftShown = shown > draftAt;
  const [tourAt, setTourAt] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const flowRef = useRef(null);
  const lastStop = stops.length - 1;
  const touring = tourAt != null && tourAt < lastStop;
  const [paused, setPaused] = useState(false);
  const [leaving, setLeaving] = useState(null);
  const lastTour = useRef(null);
  const go = (n) => setTourAt(Math.max(0, Math.min(lastStop, n)));

  // The panel being left slides out while the next slides in over it.
  useEffect(() => {
    const before = lastTour.current;
    lastTour.current = tourAt;
    if (before == null || tourAt == null || before === tourAt || !stops[before]) return undefined;
    setLeaving(stops[before].key);
    const timer = setTimeout(() => setLeaving(null), LEAVE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourAt]);

  // While walking: the one panel on show (and the one leaving) stacked in one
  // place under the checks. At the end every panel unstacks into a column, one
  // after another, ending on the form.
  const stopClass = (key) => {
    if (!touring) return "ns-unstack";
    if (stops[tourAt].key === key) return "ns-tour-step";
    if (leaving === key) return "ns-tour-leave";
    return null;
  };
  const unstackAt = (key) => (touring ? undefined : { animationDelay: `${Math.max(0, stops.findIndex(t => t.key === key)) * 60}ms` });

  // Arrow keys move through it; swiping the panel does too.
  useEffect(() => {
    if (!touring) return undefined;
    const onKey = (e) => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test((e.target && e.target.tagName) || "")) return;
      if (e.key === "ArrowRight") go(tourAt + 1);
      else if (e.key === "ArrowLeft") go(tourAt - 1);
      else if (e.key === "Escape") go(lastStop);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touring, tourAt, lastStop]);
  const swipe = useRef(null);
  const onTouchStart = (e) => { swipe.current = e.touches[0] ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null; };
  const onTouchEnd = (e) => {
    const from = swipe.current; swipe.current = null;
    const t = e.changedTouches && e.changedTouches[0];
    if (!touring || !from || !t) return;
    const dx = t.clientX - from.x;
    if (Math.abs(dx) > 60 && Math.abs(dx) > 2 * Math.abs(t.clientY - from.y)) go(tourAt + (dx < 0 ? 1 : -1));
  };

  // The walk starts a beat after the last check ticks; less motion skips it.
  useEffect(() => {
    if (v2 || !finished || tourAt != null) return undefined;
    // **Walked through even for somebody who asked for less motion** — it is
    // how the page shows what it found, not decoration. Their panels fade
    // rather than slide (the stylesheet's reduced-motion rules).
    const timer = setTimeout(() => setTourAt(0), 700);
    return () => clearTimeout(timer);
  }, [v2, finished, tourAt, lastStop]);

  // Each stop moves on by itself, unless the reader paused it or has the
  // draft open.
  useEffect(() => {
    if (!touring || draftOpen || paused) return undefined;
    const timer = setTimeout(() => setTourAt(n => Math.min(lastStop, n + 1)), stops[tourAt].ms);
    return () => clearTimeout(timer);
  }, [touring, tourAt, draftOpen, paused, lastStop, stops]);

  // The first stop brings the panels into view under the checks, which stay
  // where they are; the last scrolls on down to the form once the panels have
  // unstacked.
  useEffect(() => {
    if (tourAt == null) return;
    const smooth = "smooth";
    if (tourAt >= lastStop) {
      const timer = setTimeout(() => {
        const form = document.getElementById("ns-start-card");
        if (form && typeof form.scrollIntoView === "function") form.scrollIntoView({ behavior: smooth, block: wide ? "center" : "end" });
      }, stops.length * 60 + 350);
      return () => clearTimeout(timer);
    }
    if (tourAt === 0) {
      const stage = document.getElementById("ns-stage");
      if (stage && typeof window.scrollTo === "function") {
        window.scrollTo({ top: Math.max(0, stage.getBoundingClientRect().top + window.scrollY - 140), behavior: smooth });
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourAt, lastStop]);

  // The floating bar points at the form, so it steps aside once the form is
  // on screen.
  const [atForm, setAtForm] = useState(false);
  useEffect(() => {
    if (!finished || typeof IntersectionObserver === "undefined") return undefined;
    const el = document.getElementById("ns-start-card");
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setAtForm(e.isIntersecting), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
    // The form only exists once the walk through ends, so look again then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, tourAt]);
  const property = answers.property && answers.property.ok ? answers.property : null;
  const planning = answers.planning && answers.planning.ok ? answers.planning.planning : null;
  const conveyancer = chosenConveyancer(answers.conveyancer, chosenId);
  const conveyancerOptions = answers.conveyancer && answers.conveyancer.ok ? answers.conveyancer.options || [] : [];
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // An agent gives their work email; the directory says who they are.
  const [agentKnown, setAgentKnown] = useState(null);
  const lookUpAgent = async (email) => {
    const e = String(email || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { setAgentKnown(null); return; }
    const { body } = await ask(`/sale/agent?email=${encodeURIComponent(e)}`);
    setAgentKnown(body && body.found ? { firstName: body.firstName, agency: body.agency } : null);
  };

  const startReplay = useCallback((w) => {
    setReplay(w);
    setPick({ address: w.address, propertyPfi: w.propertyPfi, parcels: w.parcels || [] });
    try { window.scrollTo(0, 0); } catch (e) { /* the page still plays */ }
  }, []);
  const endReplay = () => { restart(); setReplay(null); try { window.scrollTo(0, 0); } catch (e) { /* fine */ } };
  const restart = () => {
    setPick(null); setAnswers({}); setLive({}); setShown(0); setLookup(null); setError(""); setDone(null); setTourAt(null); setQuestionsDone(false); setStartPromo(null); setChosenId(null);
  };

  // **The vendor's flow: open, the questions, then their tracker**, where the
  // invoice is. An agent's start is a proposal and stays on its own page.
  useEffect(() => {
    if (!done || done.proposal || !done.track || (done.questions && !questionsDone)) return;
    setOpened({ ref: done.track.ref });
    window.location.hash = `t=${done.track.ref}.${done.track.token}`;
  }, [done, questionsDone]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.role === "vendor" && form.consent !== true) {
      setConsentNudge(true);
      const box = document.querySelector(".ns-consent input");
      if (box) { box.focus({ preventScroll: true }); if (typeof box.scrollIntoView === "function") box.scrollIntoView({ behavior: "smooth", block: "center" }); }
      return;
    }
    setSending(true);
    const payload = startPayload(form, pick, conveyancer && conveyancer.id);
    if (startPromo) payload.promoCode = startPromo.promo.code;
    const { status, body } = await ask("/sale/start", { method: "POST", body: JSON.stringify(payload) });
    setSending(false);
    if (status === 429) { setError("Too many requests from here — please try again in a little while."); return; }
    if (!body.ok) { setError(body.error || "Something went wrong. Please try again."); return; }
    if (body.track) rememberTracked({ ...body.track, address: pick.address });
    setDone(body);
  };


  // **/sellv2 tells what was found as chapters the reader scrolls through**,
  // beside the draft, which lights each chapter's fields. It plays itself —
  // a chapter every few seconds — until the reader scrolls, swipes, clicks
  // or presses a key, and from then on the scroll is theirs.
  const [chapter, setChapter] = useState(0);
  // The chapter bar shows once the checks card has scrolled away.
  const [pastChecks, setPastChecks] = useState(false);
  useEffect(() => {
    if (!v2 || !finished) { setPastChecks(false); return undefined; }
    const look = () => {
      const card = document.querySelector(".ns-checks-card");
      setPastChecks(Boolean(card && card.getBoundingClientRect().bottom < 90));
    };
    look();
    window.addEventListener("scroll", look, { passive: true });
    window.addEventListener("resize", look);
    return () => { window.removeEventListener("scroll", look); window.removeEventListener("resize", look); };
  }, [v2, finished]);
  // **Until the checks card has scrolled away, the draft sits level with it
  // and as tall**: the side's centred offset would push it below the card,
  // so it starts at the card's top, its stage sized so the two end together.
  const [startStage, setStartStage] = useState(null);
  useEffect(() => {
    if (!v2 || pastChecks || typeof ResizeObserver !== "function") { setStartStage(null); return undefined; }
    const fit = () => {
      const card = document.querySelector(".ns-checks-card");
      const side = document.querySelector(".ns-split-side .ns-card-draft");
      const stage = side && side.querySelector(".ns-draft-stage, .ns-drafting");
      if (!card || !side || !stage) return;
      const chrome = side.offsetHeight - stage.offsetHeight;
      setStartStage(Math.max(280, card.offsetHeight - chrome));
    };
    fit();
    const ro = new ResizeObserver(fit);
    [".ns-checks-card", ".ns-split-side .ns-card-draft"].forEach((q) => { const el = document.querySelector(q); if (el) ro.observe(el); });
    return () => ro.disconnect();
  }, [v2, pastChecks, split, draftShown]);
  const [autoplay, setAutoplay] = useState(true);
  useDepth(v2, setChapter, [finished, pick, done]);
  // **Our own glide, not the browser's smooth scroll**: slower and eased, so
  // a chapter arrives rather than jumps — and it stops the instant the reader
  // touches the wheel, the trackpad or a key, where the browser's would carry
  // on and fight them for a second.
  const glide = useRef(null);
  const stopGlide = () => { if (glide.current) glide.current(); };
  const glideTo = (el) => {
    stopGlide();
    if (!el) return;
    let to = Math.max(0, el.getBoundingClientRect().top + window.scrollY - (window.innerHeight - el.offsetHeight) / 2);
    // Never past the chapter's own heading: centring a tall card on a phone
    // put its "06" and title above the screen.
    const head = el.closest && el.closest(".ns-ch") ? el.closest(".ns-ch").querySelector(".ns-ch-eyebrow") : (el.querySelector && el.querySelector(".ns-ch-eyebrow"));
    if (head) to = Math.max(0, Math.min(to, head.getBoundingClientRect().top + window.scrollY - (window.innerWidth < 720 ? 96 : 140)));
    if (typeof requestAnimationFrame !== "function") { window.scrollTo(0, to); return; }
    // **Glided either way.** A jump reads as the page breaking — and less
    // motion is a common default on office PCs (Windows' "Show animations"
    // off) — so somebody who asked for less gets a shorter glide, not none.
    const from = window.scrollY;
    const ms = prefersLessMotion() ? 450 : Math.min(1400, Math.max(700, Math.abs(to - from) * 0.9));
    const start = performance.now();
    let raf = 0;
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const hands = ["wheel", "touchstart", "keydown", "pointerdown"];
    const off = () => { cancelAnimationFrame(raf); hands.forEach(h => window.removeEventListener(h, stopGlide)); glide.current = null; };
    const step = (now) => {
      const t = Math.min(1, (now - start) / ms);
      window.scrollTo(0, from + (to - from) * ease(t));
      if (t < 1) raf = requestAnimationFrame(step); else off();
    };
    raf = requestAnimationFrame(step);
    glide.current = off;
    hands.forEach(h => window.addEventListener(h, stopGlide, { passive: true }));
  };
  useEffect(() => stopGlide, []);
  useEffect(() => {
    if (!v2 || !finished || !autoplay) return undefined;
    const stop = () => { stopGlide(); setAutoplay(false); };
    // Only a person's own scroll stops it: the wheel, a touch, a key.
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("keydown", stop);
    window.addEventListener("pointerdown", stop);
    return () => {
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", stop);
      window.removeEventListener("pointerdown", stop);
    };
  }, [v2, finished, autoplay]);
  const [playAt, setPlayAt] = useState(-1);
  useEffect(() => {
    if (!v2 || !finished || !autoplay) return undefined;
    const count = document.querySelectorAll("[data-ch]").length;
    if (playAt >= count - 1) { setAutoplay(false); return undefined; }
    const timer = setTimeout(() => {
      const next = playAt + 1;
      glideTo(document.querySelector(`[data-ch="${next}"]`));
      setPlayAt(next);
    }, playAt < 0 ? 900 : CHAPTER_MS);
    return () => clearTimeout(timer);
    // glideTo reads only refs and the page, so it need not be a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2, finished, autoplay, playAt]);
  const goChapter = (i) => {
    setAutoplay(false);
    glideTo(document.querySelector(`[data-ch="${i}"]`));
  };
  // The open button leans towards the pointer (/sellv2, a desktop).
  const magnet = useCallback((el) => {
    if (!el || !v2 || prefersLessMotion()) return;
    el.addEventListener("mousemove", (e) => {
      const r = el.getBoundingClientRect();
      el.style.transform = `translate(${((e.clientX - r.left - r.width / 2) * 0.16).toFixed(1)}px, ${((e.clientY - r.top - r.height / 2) * 0.28).toFixed(1)}px)`;
    });
    el.addEventListener("mouseleave", () => { el.style.transform = ""; });
  }, [v2]);

  // What was found, as cards — walked through on /sell, told as chapters on
  // /sellv2. Only drawn once the property answered.
  const propertyCards = property && (
<div className="ns-grid">
                  <Card title="The property">
                    <dl className="ns-dl">
                      <dt>Title</dt><dd>{property.property.lots.join(", ") || "From the title search"}</dd>
                      <dt>Council</dt><dd>{property.property.council || "To be confirmed"}</dd>
                      <dt>Water</dt><dd>{property.property.waterAuthority || "To be confirmed"}</dd>
                      <dt>Owners corporation</dt><dd>{property.ownersCorporation.line}</dd>
                      {property.ownersCorporation.status !== "none" && !property.unlisted && <><dt>Area</dt><dd>On the plan of subdivision — ordered with the searches</dd></>}
                    </dl>
                    {property.property.proposed && <p className="ns-msg">This lot is on a plan not yet registered — an off-the-plan sale. Your conveyancer will take it from there.</p>}
                  </Card>
                  <Card title="Planning">
                    <dl className="ns-dl">
                      <dt>Zone</dt><dd>{planning && planning.zones.length ? planning.zones.join(", ") : planning && planning.stillSearching ? "Still searching" : "To be confirmed"}</dd>
                      <dt>Overlays</dt><dd>{planning && planning.stillSearching ? "Still searching" : planning ? (planning.overlays.length ? planning.overlays.join(", ") : planning.overlaysComplete ? "None affecting the land" : "To be confirmed") : "To be confirmed"}</dd>
                      <dt>Bushfire</dt><dd>{planning && planning.stillSearching ? "Still searching" : planning ? planning.bushfireLine : property.unlisted ? "Checked once your conveyancer confirms the land" : "Could not be checked just now — your conveyancer will confirm it."}</dd>
                    </dl>
                  </Card>
                </div>
  );
  const searchesCard = property && (
<Card title="Searches we'll order once reviewed">
                  <ul className="ns-searches ns-cascade">
                    {property.searches.map((s, i) => (
                      <li key={`${s.name}-${i}`} style={{ animationDelay: `${i * 70}ms` }}>
                        <span className="ns-search-name"><span className="ns-badge" aria-hidden="true">{initialsOf(s.issuedBy)}</span><span>{s.name}{s.for ? ` — ${s.for}` : ""}</span></span>
                        <span className="ns-issuer">{s.issuedBy}{s.free ? " · free" : ""}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
  );
  const conveyancerPanel = conveyancer && (
<TiltCard title={v2 ? undefined : "Your conveyancer"}>
                    <div className="ns-person">
                      <Portrait person={conveyancer} size={88} />
                      <div>
                        <div className="ns-person-name">{conveyancer.name}{conveyancer.role && <span className="ns-role">{conveyancer.role}</span>}</div>
                        <div className="ns-stats">
                          {conveyancer.yearsExperience != null && <span className="ns-stat"><b><CountUp value={conveyancer.yearsExperience} /></b> years' experience</span>}
                          {conveyancer.contractsPrepared != null && <span className="ns-stat"><b><CountUp value={conveyancer.contractsPrepared} ms={1200} /></b> contracts in the last 3 months</span>}
                        </div>
                        {conveyancer.bio && <p className="ns-bio">{conveyancer.bio}</p>}
                      </div>
                    </div>
                    {conveyancerOptions.length > 1 && <ConveyancerChooser options={conveyancerOptions} chosen={conveyancer} onChoose={setChosenId} />}
                  </TiltCard>
  );
  const nextCard = property && (
<Card title={v2 ? undefined : "What happens next"}>
                  {/* Under a headline of "as soon as a few hours", the line that agrees with it. */}
                  <p className="ns-timeframe">{v2 && property.timeframe.detail ? property.timeframe.detail : property.timeframe.line}</p>
                  <ol className="ns-next">
                    {property.nextSteps.map((s, i) => (
                      <li key={s.key}><span className="ns-next-n">{i + 1}</span><div><b>{s.title}</b><div className="ns-step-line">{s.line}</div></div></li>
                    ))}
                  </ol>
                </Card>
  );

  const chapters = v2 && property ? [
    { key: "land", eyebrow: "The land", title: "What the title and the maps say", body: propertyCards },
    !split && { key: "draft", eyebrow: "Your contract", title: "Already taking shape", body: <DraftCard draft={answers.draft} onOpenChange={setDraftOpen} /> },
    { key: "searches", eyebrow: "The searches", title: `${property.searches.length} searches, lined up`, body: searchesCard },
    conveyancer && { key: "conveyancer", eyebrow: "Your conveyancer", title: `Meet ${conveyancer.firstName}`, body: conveyancerPanel },
    { key: "next", eyebrow: "What happens next", title: (property.timeframe && property.timeframe.headline) || "What happens next", body: nextCard },
  ].filter(Boolean) : [];

  // The form that opens the matter: at the end of the column on a phone, and
  // at the top of the draft's column on a wide screen, beside the contract.
  const startCard = replay ? (
<div className="ns-unstack" style={unstackAt("start")}><div id="ns-start" /><div id="ns-start-card"><Card title={replay.agent ? "What the vendor sees" : "Your quote"} className="ns-card-cta ns-attn">
                  <p className="ns-msg" style={{ marginTop: 0 }}>{replay.agent
                    ? "That's the walk through the vendor gets when they open the proposal. Their link ends here, on the questions and the invoice."
                    : "Accept it, answer a few questions and pay — then we order everything and the contract comes together in days."}</p>
                  {/* The matter's own invoice — its promotion included — not a fresh estimate. */}
                  {!replay.agent && (replay.payment || (property && property.price)) && <PriceSummary price={replay.payment || property.price} agent />}
                  {!replay.agent && <TrustLine />}
                  <button type="button" id="ns-open" className="ns-btn ns-replay-go" onClick={endReplay}>{replay.agent ? "Back to the sale" : "Get started"}<span className="ns-btn-arrow" aria-hidden="true">→</span></button>
                </Card></div></div>
  ) : (
<div className="ns-unstack" style={unstackAt("start")}><div id="ns-start" /><div id="ns-start-card"><Card title={form.role !== "vendor" ? "Prepare a proposal" : "Start your matter"} className={`ns-card-cta${tourAt != null || (v2 && finished) ? " ns-attn" : ""}`}>
                  <form onSubmit={submit} className="ns-form">
                    <div className="ns-toggle" role="radiogroup" aria-label="Who are you?">
                      {[["vendor", "I'm the vendor"], ["agent", "I'm the agent"], ["other", "Someone else"]].map(([v, label]) => (
                        <button type="button" key={v} role="radio" aria-checked={form.role === v}
                          className={`ns-toggle-btn${form.role === v ? " on" : ""}`} onClick={() => setForm(f => ({ ...f, role: v }))}>{label}</button>
                      ))}
                    </div>
                    <div className="ns-form-grid">
                      {form.role !== "agent" && <Field label="Your name"><input className="ns-input" value={form.name || ""} onChange={set("name")} required autoComplete="name" /></Field>}
                      <Field label={form.role === "agent" ? "Your work email" : "Your email"} hint={form.role === "agent" && !agentKnown ? "That's all we need — we'll find your details." : undefined}>
                        <input className="ns-input" type="email" value={form.email || ""} onChange={set("email")} onBlur={() => form.role === "agent" && lookUpAgent(form.email)} required autoComplete="email" />
                        {form.role === "agent" && agentKnown && <span className="ns-agent-hi"><Portraitless name={agentKnown.firstName} />Hi {agentKnown.firstName}{agentKnown.agency ? ` — ${agentKnown.agency}` : ""}</span>}
                      </Field>
                      {form.role === "vendor" && <Field label="Mobile"><input className="ns-input" type="tel" value={form.phone || ""} onChange={set("phone")} autoComplete="tel" inputMode="tel" required minLength={8} /></Field>}
                      {form.role !== "vendor" && <div className="ns-hint ns-form-note">We'll prepare the proposal for you to send the vendor. They accept it, answer the questions and pay.</div>}
                    </div>
                    {/* Left empty by people; a filled one is a bot. */}
                    <input className="ns-trap" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website || ""} onChange={set("website")} />
                    {property && property.price && <PriceSummary price={property.price} promo={startPromo} agent={form.role !== "vendor"} />}
                    {property && property.price && <StartPromo promo={startPromo} onChange={setStartPromo} />}
                    {/* An agent asked for this themselves, from their work address:
                        no box to tick, just what they will be sent. */}
                    {form.role !== "vendor" ? (
                      <p className="ns-hint" style={{ margin: 0 }}>We'll email you the proposal to send on, and keep you posted on this sale.</p>
                    ) : (
                      <label className={`ns-consent${form.consent === true ? " on" : ""}${consentNudge && form.consent !== true ? " ns-consent-attn" : ""}`}>
                        <input type="checkbox" checked={form.consent === true} onChange={(e) => { set("consent")(e); setConsentNudge(false); }} />
                        <span>{property && property.price
                          ? "I agree to Provey contacting me about this sale, and to paying the invoice at the end."
                          : "I agree to Provey contacting me about this sale. Nothing is ordered or charged until a conveyancer has reviewed it."}</span>
                      </label>
                    )}
                    {form.role === "vendor" && consentNudge && form.consent !== true && <p className="ns-consent-why" role="alert">Please tick the box to continue.</p>}
                    {error && <p className="ns-error" role="alert">{error}</p>}
                    {form.role !== "agent" && <TrustLine />}
                    {conveyancer && <ConveyancerLine person={conveyancer} agent={form.role !== "vendor"} />}
                    <button type="submit" id="ns-open" ref={magnet} className="ns-btn" disabled={sending}>{form.role !== "vendor" ? (sending ? "Preparing…" : "Prepare proposal") : (sending ? "Opening your matter…" : "Open my matter")}<span className="ns-btn-arrow" aria-hidden="true">→</span></button>
                  </form>
                </Card></div></div>
  );

  const P = v2 ? SELL_BRAND : T;
  // Back from Stripe on a device with no saved link: the email has it.
  if (!track && back && !pick) {
    return (
      <div className={`ns-page${v2 ? " ns-v2" : ""}`}>
        <link rel="stylesheet" href={SELL_FONTS} />
        <style>{paletteCss(v2 ? SELL_BRAND : T) + SHEET + (v2 ? SHEET_V2 : "")}</style>
        <main className="ns-main"><div className="ns-flow"><Card>
          <h1 className="ns-h2">{back.paid ? "Thank you — your matter is open" : "Your matter is open"}</h1>
          <p className="ns-msg">{back.paid ? "Thank you — your invoice is paid." : "Payment wasn't completed."} Open the link we emailed you to follow your matter{back.paid ? "" : " and pay from there"}.</p>
        </Card></div></main>
      </div>
    );
  }

  if (leaseLink) {
    return (
      <Palette.Provider value={P}>
      <div className={`ns-page${v2 ? " ns-v2" : ""}`}>
        <link rel="stylesheet" href={SELL_FONTS} />
        <style>{paletteCss(P) + SHEET + (v2 ? SHEET_V2 : "")}</style>
        <header className="ns-top ns-main-track"><img src={LOGO_BLACK_SRC} alt="Provey" className="ns-logo" /></header>
        <main className="ns-main"><div className="ns-flow"><LeaseUpload lease={leaseLink} /></div></main>
      </div>
      </Palette.Provider>
    );
  }

  if (track && !replay) {
    return (
      <Palette.Provider value={P}>
      <div className={`ns-page${v2 ? " ns-v2" : ""}`}>
        <link rel="stylesheet" href={SELL_FONTS} />
        <style>{paletteCss(P) + SHEET + (v2 ? SHEET_V2 : "")}</style>
        <Atmosphere />
        <header className="ns-top ns-main-track"><a href={salePath()}><img src={LOGO_BLACK_SRC} alt="Provey" className="ns-logo" /></a></header>
        <main className="ns-main ns-main-track"><Tracker track={track} returned={back && back.track ? back : null} opened={opened && opened.ref === track.ref ? opened : null} onReplay={startReplay} /></main>
      </div>
      </Palette.Provider>
    );
  }

  return (
    <Palette.Provider value={P}>
    <DraftSubject.Provider value={pick ? {
      address: displayAddress(pick.address),
      lots: (pick.parcels || []).map(p => (p.lot && p.plan ? `Lot ${p.lot} on ${p.plan}` : p.spi)),
      council: property && property.property.council,
      zones: planning ? planning.zones : live.zones,
    } : {}}>
    <div className={`ns-page${v2 ? " ns-v2" : ""}`}>
      <link rel="stylesheet" href={SELL_FONTS} />
      <style>{paletteCss(P) + SHEET + (v2 ? SHEET_V2 : "")}</style>
      <Atmosphere />
      {v2 && pick && !done && <div className="ns-prog" aria-hidden="true"><span style={{ width: `${finished ? ((chapter + 1) / Math.max(1, chapters.length + 1)) * 100 : (Math.min(shown, STEPS.length) / STEPS.length) * 40}%` }} /></div>}
                {v2 && split && finished && chapters.length > 0 && (
        <nav className={`ns-chapters ns-chapters-float${pastChecks ? " on" : ""}`} aria-label="What we found" aria-hidden={!pastChecks}>
          {[...chapters, { key: "start", eyebrow: "Start" }].map((c, i) => (
            <button type="button" key={c.key} className={`ns-chapter${chapter === i ? " on" : chapter > i ? " seen" : ""}`} onClick={() => goChapter(i)}>
              <span className="ns-chapter-n">{String(i + 1).padStart(2, "0")}</span>{c.eyebrow}
            </button>
          ))}
        </nav>
      )}
      <header className={`ns-top${split ? " wide" : ""}`}>
        <img src={LOGO_BLACK_SRC} alt="Provey" className="ns-logo" />
      </header>

      <main className={`ns-main${split ? " wide" : ""}`}>
        {!pick && (
          <div className="ns-hero">
            {v2 && <HeroDepth />}
            <div className="ns-banner">
            <p className="ns-kicker"><span className="ns-live-dot" aria-hidden="true" />Section 32 &amp; contract of sale</p>
            <h1 className="ns-h1">
              {"Selling a property?".split(" ").map((w, i) => <span key={i}><span className="ns-word" style={{ animationDelay: `${i * 90}ms` }}>{w}</span>{" "}</span>)}
              <br />
              <em className="ns-word ns-accent" style={{ animationDelay: "300ms" }}>Start in a minute.</em>
            </h1>
            <p className="ns-lede ns-fade" style={{ animationDelay: "450ms" }}>Type the address. We'll check the land, line up every search and introduce your conveyancer — while you watch.</p>
            <div className="ns-search ns-fade" style={{ animationDelay: "600ms", textAlign: "left" }}>
              <span className="ns-ring" aria-hidden="true" />
              <svg className="ns-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
              <input
                className="ns-address" autoFocus value={query} onChange={e => setQuery(e.target.value)}
                placeholder={typed} aria-label="Property address" autoComplete="street-address"
              />
              {looking && <span className="ns-spin ns-search-spin" aria-hidden="true" />}
            </div>
            </div>
            {!query && (
              <ul className="ns-promises ns-fade" style={{ animationDelay: "750ms" }}>
                <li><span className="ns-promise-ico" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z" /><path d="M8 2v16M16 6v16" /></svg></span>Live checks against the state's maps</li>
                <li><span className="ns-promise-ico" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg></span>A real conveyancer reviews everything</li>
                <li><span className="ns-promise-ico" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg></span>Nothing ordered or charged until then</li>
              </ul>
            )}
            {!query && tracked.length > 0 && (
              <div className="ns-tracked">
                <span className="ns-label">Your matters</span>
                {tracked.map(t => (
                  <a key={t.ref} className="ns-tracked-link" href={trackHref(t)}><span className="ns-live-dot" aria-hidden="true" />{displayAddress(t.address) || t.ref}</a>
                ))}
              </div>
            )}
            {lookup && !lookup.ok && <p className="ns-msg">{lookup.error || "We could not look that up just now."}</p>}
            {lookup && lookup.ok && (
              <div className="ns-candidates">
                {(lookup.unitsAtAddress || []).map(u => (
                  <p key={u.address} className="ns-msg">{displayAddress(u.address)} has {u.count} units — add your unit number, like 4/{displayAddress(u.address)}.</p>
                ))}
                {lookup.candidates.length === 0 && !(lookup.unitsAtAddress || []).length && (
                  <p className="ns-msg">No property found yet — keep typing the street, suburb and postcode.</p>
                )}
                {lookup.candidates.map((c, i) => (
                  <button type="button" key={c.propertyPfi} className="ns-candidate" style={{ animationDelay: `${i * 60}ms` }} onClick={() => setPick(c)}>
                    <span>
                      <span className="ns-candidate-address">{displayAddress(c.address)}</span>
                      <span className="ns-candidate-lots">{c.parcels.map(p => (p.lot && p.plan ? `Lot ${p.lot} on ${p.plan}` : p.spi)).join(", ")}</span>
                    </span>
                    <span className="ns-candidate-go" aria-hidden="true">→</span>
                  </button>
                ))}
                {/* Nobody is stopped at the address: the property is confirmed from
                    the rates notice instead. */}
                {query.trim().length >= 10 && (
                  <button type="button" className="ns-link ns-unlisted" onClick={() => setPick({ address: query.trim(), propertyPfi: "", parcels: [], unlisted: true })}>
                    Can't find your property? Continue with "{query.trim()}"
                  </button>
                )}
                {lookup.attribution && <p className="ns-fine">{lookup.attribution}</p>}
              </div>
            )}
          </div>
        )}

        {pick && !done && (
          <div className={`ns-flow${split ? " ns-split" : ""}`} ref={flowRef}>
            <div className="ns-flow-head">
              <div>
                <p className="ns-eyebrow">Your property</p>
                <h1 className="ns-h2">{displayAddress(pick.address)}</h1>
              </div>
              {replay
                ? <button type="button" className="ns-link" onClick={() => {
                    // To the quote card at the walk's end; before the checks
                    // have finished there is none yet, so on to the matter.
                    const card = !replay.agent && document.getElementById("ns-start-card");
                    if (card) { setAutoplay(false); glideTo(card); } else endReplay();
                  }}>Skip to {replay.agent ? "the sale" : "your quote"}</button>
                : <button type="button" className="ns-link" onClick={restart}>Change</button>}
            </div>

            <div className="ns-split-main">
            <Card className="ns-checks-card">
              <div className="ns-progress">
                <span>{shown >= STEPS.length ? "All checks done" : "Checking your property"}</span>
                <span className="ns-progress-count"><b>{Math.min(shown, STEPS.length)}</b> of {STEPS.length}</span>
              </div>
              <div className="ns-progress-bar" aria-hidden="true"><span style={{ width: `${(Math.min(shown, STEPS.length) / STEPS.length) * 100}%` }} /></div>
              <div className={`ns-steps-wrap${live.shape ? " with-parcel" : ""}`}>
              <ol className="ns-steps">
                {STEPS.map((step, i) => {
                  const result = stepResult(step.key, answers);
                  const state = i < shown ? "done" : i === shown ? "running" : "waiting";
                  const tone = result ? result.tone : "ok";
                  return (
                    <li key={step.key} className={`ns-step ns-step-${state}`}>
                      <StepIcon state={state} tone={tone} icon={step.key} portrait={step.key === "conveyancer" && conveyancer ? conveyancer.portrait : null} />
                      <div>
                        <div className="ns-step-title">{step.title}</div>
                        {state === "done" && result && <div className="ns-step-line" style={{ color: TONE[tone].ink }}>{result.line}</div>}
                        {state === "running" && runningLine(step.key, live) && <div className="ns-step-line ns-step-live" aria-live="polite">{runningLine(step.key, live)}</div>}
                        {step.key === "property" && state === "done" && property && localLine(property.local) && (
                          <div className="ns-local"><span className="ns-local-ico" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1z" /></svg>
                          </span>{localLine(property.local)}</div>
                        )}
                        {step.key === "conveyancer" && state === "done" && conveyancer && (
                          <div className="ns-meet">
                            <Portrait person={conveyancer} size={52} />
                            <div>
                              <div className="ns-meet-hi">Meet {conveyancer.firstName}, your conveyancer</div>
                              <div className="ns-meet-sub">{[conveyancer.role, conveyancer.yearsExperience != null ? `${conveyancer.yearsExperience} years' experience` : null, conveyancer.contractsPrepared != null ? `${conveyancer.contractsPrepared} contracts in the last 3 months` : null].filter(Boolean).join(" · ")}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
              {live.shape && <ParcelOutline shape={live.shape} lots={(pick.parcels || []).map(p => (p.lot && p.plan ? `Lot ${p.lot} on ${p.plan}` : p.spi)).join(", ")} />}
              </div>
            </Card>

            {finished && property && !split && property.timeframe && <TimeEstimate timeframe={property.timeframe} />}

            {answers.property && !answers.property.ok && (
              <Card><p className="ns-msg">{answers.property.error || "We could not look that property up."} <button type="button" className="ns-link" onClick={restart}>Try another address</button></p></Card>
            )}

            {finished && property && touring && (
              <div className={`ns-dock ns-dock-tour${paused ? " paused" : ""}`} role="region" aria-label="What we found"
                onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
                <button type="button" className="ns-dock-arrow" aria-label="Back" disabled={tourAt === 0} onClick={() => go(tourAt - 1)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <span className="ns-segs" role="tablist">
                  {stops.map((t, i) => (
                    <button type="button" key={t.key} role="tab" aria-selected={i === tourAt} aria-label={t.label}
                      className={`ns-seg-btn${i === tourAt ? " on" : i < tourAt ? " seen" : ""}`} onClick={() => go(i)}>
                      {i === tourAt && t.ms > 0 && <span className="ns-seg-fill" style={{ animationDuration: `${t.ms}ms`, animationPlayState: draftOpen || paused ? "paused" : "running" }} />}
                      <span className="ns-seg-label">{t.label}</span>
                    </button>
                  ))}
                </span>
                <button type="button" className="ns-dock-arrow" aria-label={paused ? "Play" : "Pause"} onClick={() => setPaused(p => !p)}>
                  {paused
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>
                    : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>}
                </button>
                <button type="button" className="ns-btn" onClick={() => go(tourAt + 1)}>Next<span className="ns-btn-arrow" aria-hidden="true">→</span></button>
              </div>
            )}

            {finished && property && !touring && tourAt != null && !atForm && (
              <div className="ns-dock" role="region" aria-label="Your progress">
                <span><b>{STEPS.length} of {STEPS.length}</b> checks done<span className="ns-dock-extra"> · {property.searches.length} searches ready</span></span>
                <button type="button" className="ns-btn" onClick={() => {
                  const form = document.getElementById("ns-start");
                  if (form) form.scrollIntoView({ behavior: "smooth", block: "start" });
                }}>{replay ? (replay.agent ? "Back to the sale" : "Get started") : "Start my matter"}</button>
              </div>
            )}

            {v2 && finished && property && (
              <div className="ns-story">
                {chapters.map((c, i) => (
                  <section key={c.key} className={`ns-ch${chapter === i ? " on" : ""}`} data-depth="" data-ch={i}>
                    <p className="ns-ch-eyebrow"><span className="ns-ch-num" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>{c.eyebrow}</p>
                    <h2 className="ns-ch-title">{c.title.split(" ").map((w, j) => <span key={j}><span className="ns-ch-word" style={{ transitionDelay: `${j * 60}ms` }}>{w}</span>{" "}</span>)}</h2>
                    <div className="ns-ch-body">{c.body}</div>
                  </section>
                ))}
                <section className={`ns-ch ns-ch-start${chapter === chapters.length ? " on" : ""}`} data-depth="" data-ch={chapters.length}>
                  <p className="ns-ch-eyebrow"><span className="ns-ch-num" aria-hidden="true">{String(chapters.length + 1).padStart(2, "0")}</span>Ready when you are</p>
                  <h2 className="ns-ch-title">{"Open your matter".split(" ").map((w, j) => <span key={j}><span className="ns-ch-word" style={{ transitionDelay: `${j * 60}ms` }}>{w}</span>{" "}</span>)}</h2>
                  <div className="ns-ch-body">{startCard}</div>
                </section>
                {autoplay && (
                  <div className="ns-autoplay" role="status">
                    <span className="ns-live-dot" aria-hidden="true" />Showing you around · scroll to take over
                    <button type="button" className="ns-link" onClick={() => setAutoplay(false)}>Stop</button>
                  </div>
                )}
              </div>
            )}

            {!v2 && finished && property && (
              <div className={touring ? "ns-stage" : "ns-reveal"} id="ns-stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                {stopClass("property") && <div className={stopClass("property")} style={unstackAt("property")}><Reveal>{propertyCards}</Reveal></div>}

                {/* Mounted throughout, so the draft is ready by its stop. */}
                {!split && <div className={stopClass("draft") || "ns-tour-hidden"} style={unstackAt("draft")}>
                  <DraftCard draft={answers.draft} onOpenChange={setDraftOpen} showcase={touring && stops[tourAt].key === "draft"} />
                </div>}

                {stopClass("searches") && <div className={stopClass("searches")} style={unstackAt("searches")}><Reveal delay={60}>{searchesCard}</Reveal></div>}

                {conveyancer && stopClass("conveyancer") && (
                  <div className={stopClass("conveyancer")} style={unstackAt("conveyancer")}><Reveal delay={60}>{conveyancerPanel}</Reveal></div>
                )}

                {stopClass("next") && <div className={stopClass("next")} style={unstackAt("next")}><Reveal delay={60}>{nextCard}</Reveal></div>}

                {!touring && !split && startCard}
              </div>
            )}
            </div>

            {split && (
              <aside className={`ns-split-side${!v2 && finished && !touring && tourAt != null ? " with-form" : ""}${v2 && !pastChecks ? " at-start" : ""}`}
                style={v2 && !pastChecks && startStage ? { "--ns-start-stage": `${startStage}px` } : undefined}>

                {!v2 && finished && !touring && tourAt != null && startCard}
                {!v2 && property && property.timeframe && <TimeEstimate timeframe={property.timeframe} />}
                <DraftCard draft={answers.draft} ready={draftShown} onOpenChange={setDraftOpen}
                  focus={v2 && finished ? ([...chapters.map(c => c.key), "start"][chapter] || null) : null}
                  eyebrow={<span className="ns-drafting-eyebrow"><span className="ns-live-dot" aria-hidden="true" />{draftShown ? "Drafting has started" : "Drafting now"}</span>}
                  showcase={draftShown && touring && tourAt === 0} />
              </aside>
            )}
          </div>
        )}

        {done && !done.proposal && !questionsDone && done.track && done.questions && (
          <div className="ns-flow ns-reveal">
            <div className="ns-flow-head">
              <div>
                <p className="ns-eyebrow">Your matter is open{done.matterRef ? ` · ${done.matterRef}` : ""}</p>
                <h1 className="ns-h2">A few questions about {displayAddress(pick.address)}</h1>
              </div>
              <button type="button" className="ns-link" onClick={() => setQuestionsDone(true)}>Later</button>
            </div>
            <SaleQuestions track={done.track}
              view={{ ...done.questions, who: form.role, answers: {}, answeredBy: {} }}
              intro={form.role === "agent"
                ? `Answer what you know.${done.vendorLinkSent ? " We've emailed the vendor a link to answer the rest — building works, services, easements and anything a buyer should know." : " The vendor will be asked the rest."}`
                : "These are the questions our Section 32 form would ask you later — answer them now and there's no form to fill in. \"Not sure\" is a fine answer."}
              onDone={() => setQuestionsDone(true)} />
          </div>
        )}

        {done && done.proposal && done.track && (
          <div className="ns-flow ns-reveal">
            <Card className="ns-card-done">
              <div className="ns-done-head">
                <span className="ns-done ns-done-big ns-done-celebrate" aria-hidden="true">
                  <Confetti />
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" pathLength="1" className="ns-draw" /></svg>
                </span>
                <div>
                  <h1 className="ns-h2">Proposal ready for the vendor</h1>
                  {done.matterRef && <p className="ns-lede" style={{ margin: "4px 0 0" }}>Reference <b>{done.matterRef}</b> · {displayAddress(pick.address)}</p>}
                </div>
              </div>
              <p className="ns-msg" style={{ marginTop: 0 }}>We've set up the matter and lined up every search. Once the vendor accepts, answers a few questions and pays, we order everything and the contract comes together in days.</p>
              <div className="ns-form-sub">Send it to the vendor</div>
              <ProposalShare track={done.track} address={pick.address} initialLink={done.proposal.vendorLink} />
              <p className="ns-hint">We've emailed you the proposal too, to forward.{done.proposal.expiresAt ? ` It's open until ${new Date(done.proposal.expiresAt).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}.` : ""}</p>
              {/* A quiet link, and a whole load of the page: the walkthrough's
                  state is still in memory, and only the hash would change. */}
              <p className="ns-hint ns-follow-line">
                <a className="ns-link" href={trackHref(done.track)} onClick={(e) => { e.preventDefault(); window.location.assign(trackHref(done.track)); window.location.reload(); }}>Follow the sale live</a>
                {" "}— your private link, also emailed to you. You'll see when the vendor accepts, and every search as it comes back.
              </p>
            </Card>
          </div>
        )}

        {done && !done.track && (
          <div className="ns-flow ns-reveal">
            <Card className="ns-card-done">
              <h1 className="ns-h2">Your matter is open</h1>
              {done.matterRef && <p className="ns-lede" style={{ margin: "4px 0 0" }}>Reference <b>{done.matterRef}</b> · {displayAddress(pick.address)}</p>}
              {done.conveyancer && <p className="ns-bio">{done.conveyancer.firstName} will review it and be in touch.</p>}
            </Card>
          </div>
        )}
      </main>
    </div>
    </DraftSubject.Provider>
    </Palette.Provider>
  );
}

// Figtree for text, EB Garamond for headings — the pairing the page's look
// follows. Loaded by this page only.
// /sellv2's own rules, after the page's: the hero's depth, the chapters, the
// rail beside the draft. Colours only from the palette's custom properties.
const SHEET_V2 = `
.ns-v2 { overflow-x:clip; }
.ns-v2 .ns-kicker, .ns-v2 .ns-eyebrow, .ns-ch-eyebrow { text-transform:uppercase; letter-spacing:.2em; font-size:11.5px; font-weight:600; }
.ns-v2 .ns-eyebrow, .ns-ch-eyebrow { color:var(--sellAccent); }
.ns-v2 .ns-aurora .b1 { opacity:.22; } .ns-v2 .ns-aurora .b2 { opacity:.07; } .ns-v2 .ns-aurora .b3 { opacity:.12; } .ns-v2 .ns-aurora .ns-spot { opacity:.12; }
.ns-v2 .ns-btn:hover:not(:disabled) { background:var(--sellAccent); }
/* Monochrome. Red is for buttons and nothing else; mist is the address
   banner and nothing else; everything else is ink, greys and white — and
   the darkest ink is kept for words, so marks and outlines are a soft
   grey rather than black. Nothing is filled dark. */
.ns-v2 .ns-btn { font-weight:500; }
.ns-v2 .ns-btn:disabled { background:var(--sellCard); color:var(--sellFaint); box-shadow:inset 0 0 0 1px var(--sellLineStrong); }
.ns-v2 .ns-btn.soft, .ns-v2 .ns-draft-open { background:var(--sellCard); color:var(--sellInk); border:1px solid var(--sellLineStrong); border-radius:999px; }
.ns-v2 .ns-btn.soft:hover:not(:disabled) { background:var(--sellAccentTint); }
.ns-v2 .ns-toggle-btn.on, .ns-v2 .ns-choice.on, .ns-v2 .ns-seg-btn.on, .ns-v2 .ns-chapter.on {
  background:var(--sellLavender); color:var(--sellInk); border-color:var(--sellLavender); box-shadow:none; }
.ns-v2 .ns-chapter.on .ns-chapter-n { color:var(--sellSoft); }
.ns-v2 .ns-autoplay { background:var(--sellCard); color:var(--sellSoft); border:1px solid var(--sellLine); box-shadow:0 10px 30px rgba(43,33,41,.10); }
.ns-v2 .ns-autoplay .ns-link { color:var(--sellSoft); }
.ns-v2 .ns-kicker { background:var(--sellCard); border:1px solid var(--sellLine); color:var(--sellSoft); }
.ns-v2 .ns-viewer-sub, .ns-v2 .ns-now { background:var(--sellAccentTint); color:var(--sellSoft); }
.ns-v2 .ns-local, .ns-v2 .ns-meet, .ns-v2 .ns-agent-hi { background:var(--sellAccentTint); color:var(--sellInk); }
.ns-v2 .ns-local-ico { background:var(--sellCard); color:var(--sellFaint); }
.ns-v2 .ns-agent-dot { background:var(--sellLavender); color:var(--sellSoft); }
.ns-v2 .ns-role { color:var(--sellSoft); background:var(--sellAccentTint); border-color:var(--sellLine); }
.ns-v2 .ns-badge { color:var(--sellSoft); background:var(--sellAccentTint); }
.ns-v2 .ns-live-dot { background:var(--sellFaint); box-shadow:none; }
/* Icon tiles: off-white, a grey icon, a grey tick. */
.ns-v2 .ns-promise-ico, .ns-v2 .ns-eta-ico { border-radius:12px; }
.ns-v2 .ns-promise-ico, .ns-v2 .ns-eta-ico { background:var(--sellAccentTint); border-color:transparent; color:var(--sellSoft); }
.ns-v2 .ns-step-ico.done { background:var(--sellAccentTint); border-color:var(--sellAccentTint) !important; color:var(--sellSoft) !important; }
.ns-v2 .ns-step-ico.running { color:var(--sellSoft); border-color:var(--sellAccentTint); }
.ns-v2 .ns-step-ico.running::after { border-top-color:var(--sellFaint); }
.ns-v2 .ns-step-mark { background:var(--sellFaint) !important; color:var(--sellCard); }
.ns-v2 .ns-done { border-color:var(--sellAccentTint) !important; color:var(--sellSoft) !important; background:var(--sellAccentTint); }
.ns-v2 .ns-spark { background:var(--sellAccentTint); color:var(--sellSoft); }
.ns-v2 .ns-spin { border-top-color:var(--sellFaint); }
/* Mist for the big surfaces only: the banner… */
.ns-v2 .ns-banner { background:var(--sellCream); box-shadow:none; }
.ns-v2 .ns-banner .ns-lede { color:var(--sellPartnerInk); }
/* …and two more big surfaces: the start card and the desk behind the
   draft's pages (painted inside the frame, draftTint). */
.ns-v2 .ns-card-cta { background:var(--sellCream); border-color:transparent; }
.ns-v2 .ns-card-cta .ns-input, .ns-v2 .ns-card-cta .ns-toggle { background:var(--sellCard); }
.ns-v2 .ns-draft-stage { background:var(--sellCream); border-color:transparent; }
.ns-v2 .ns-parcel-fill, .ns-v2 .ns-map-lot { fill:var(--sellAccentTint); }
.ns-v2 .ns-parcel-line, .ns-v2 .ns-map-lot { stroke:var(--sellFaint); }
.ns-v2 .ns-tint { background:var(--sellFieldTint); }
.ns-v2 .ns-contract-row mark { background:var(--sellFieldTint); box-shadow:none; }
.ns-v2 .ns-paper-eyebrow, .ns-v2 .ns-chip { color:var(--sellFaint); }
.ns-v2 .ns-aurora { display:none; }
/* Rings and bars, in grey. */
.ns-v2 .ns-ring { display:none; }
.ns-v2 .ns-address:focus { border-color:var(--sellLineStrong); box-shadow:0 0 0 4px var(--sellAccentTint); }
.ns-v2 .ns-input:focus { border-color:var(--sellLineStrong); box-shadow:0 0 0 3px var(--sellAccentTint); }
.ns-v2 .ns-progress-bar span, .ns-v2 .ns-prog span, .ns-v2 .ns-seg-fill { background:var(--sellSoft); }
.ns-v2 .ns-attn::before { display:none; }
.ns-v2 .ns-attn { animation:none; }
/* **Red as a mark, never a surface** — besides the buttons, it marks what is
   live, current or the client's own: the kicker, live dots, the step in
   progress, the chapter you are on, searches that are back, and the land. */
.ns-v2 .ns-kicker { background:var(--sellPop); border-color:var(--sellPop); color:#FFFFFF; }
.ns-v2 .ns-kicker .ns-live-dot { display:none; }
.ns-v2 .ns-live-dot { background:var(--sellPop); box-shadow:0 0 0 3px var(--sellPopTint); }
.ns-v2 .ns-live.off .ns-live-dot { background:var(--sellFaint); box-shadow:none; }
.ns-v2 .ns-now { background:var(--sellPopTint); color:var(--sellPop); }
/* Done is light: a grey tick on an off-white tile, as the checks show it. */
.ns-v2 .ns-track-steps li.done .ns-next-n { background:var(--sellAccentTint); border-color:var(--sellAccentTint); color:var(--sellSoft); }
.ns-v2 .ns-queue-fill { background:var(--sellFaint); }
.ns-v2 .ns-queue-stop.past, .ns-v2 .ns-queue.at-2 .ns-queue-stop.now { background:var(--sellFaint); border-color:var(--sellFaint); color:var(--sellCard); }
.ns-v2 .ns-queue-stop.now { border-color:var(--sellFaint); }
.ns-v2 .ns-queue-list li.in .ns-badge { background:var(--sellLavender); color:var(--sellSoft); }
.ns-v2 .ns-chapter.on .ns-chapter-n { color:var(--sellPop); }
.ns-v2 .ns-seg.received { background:var(--sellPop); }
.ns-v2 .ns-parcel-fill { fill:var(--sellPopTint); }
.ns-v2 .ns-parcel-line { stroke:var(--sellPop); }
.ns-v2 .ns-step-ico.running::after { border-top-color:var(--sellPop); }
/* **Readable small print, and a choice you can see.** Helper text in the
   page's mid-grey (5.8:1 on the mist card; the faint grey was 2.7:1). A picked
   option keeps its soft fill but gains an ink border and a tick, so the state
   reads from shape as well as colour; keyboard focus is a soft halo of its
   own, never mistaken for a pick. */
.ns-v2 .ns-hint, .ns-v2 .ns-choice-sub { color:var(--sellSoft); font-size:12.5px; }
.ns-v2 .ns-choice.on { background:var(--sellLavender); border-color:var(--sellInk); box-shadow:inset 0 0 0 .5px var(--sellInk); color:var(--sellInk); font-weight:500; }
.ns-v2 .ns-choice.on::before { content:"✓"; margin-right:6px; font-weight:600; }
.ns-v2 .ns-choice:focus { outline:none; }
.ns-v2 .ns-choice:focus-visible { outline:none; box-shadow:0 0 0 3px rgba(43,33,41,.16); }
.ns-v2 .ns-choice.on:focus-visible { box-shadow:inset 0 0 0 .5px var(--sellInk), 0 0 0 3px rgba(43,33,41,.16); }
.ns-v2 .ns-choose-card.ns-on { border-color:var(--sellLineStrong); background:var(--sellAccentTint); box-shadow:none; }
.ns-v2 .ns-choose-card.ns-on .ns-choose-line:last-child { color:var(--sellSoft); }
.ns-v2 .ns-card { border:1px solid var(--sellLine); }
.ns-v2 .ns-btn { transition:background .2s, transform .25s cubic-bezier(.2,.8,.2,1); }
.ns-v2 .ns-card { transition:box-shadow .35s, transform .35s cubic-bezier(.2,.8,.2,1); }
.ns-v2 .ns-card:hover { box-shadow:0 18px 40px -22px rgba(54,47,42,.35); }

.ns-prog { position:fixed; top:0; left:0; right:0; height:3px; z-index:30; pointer-events:none; }
.ns-prog span { display:block; height:100%; background:var(--sellGlow); transition:width .5s cubic-bezier(.2,.8,.2,1); }

/* The hero's paper, at depth. */
.ns-v2 .ns-hero { position:relative; }
.ns-v2 .ns-banner { position:relative; z-index:1; }
.ns-depth { position:absolute; top:0; left:50%; width:min(1380px, 100vw); height:620px; margin-left:calc(min(1380px, 100vw) / -2); pointer-events:none; z-index:0; }
.ns-float { position:absolute; will-change:transform;
  transform:translate3d(calc(var(--px, 0) * var(--d) * -16px), calc(var(--sy, 0) * var(--d) * -0.32px + var(--py, 0) * var(--d) * -10px), 0); }
.ns-float-in { animation:ns-float-in .9s cubic-bezier(.2,.8,.2,1) both, ns-bob 7s ease-in-out infinite alternate; }
.ns-float.f1 { left:36px; top:70px; } .ns-float.f1 .ns-float-in { animation-delay:.5s, 1.4s; --r:-5deg; }
.ns-float.f2 { right:44px; top:36px; } .ns-float.f2 .ns-float-in { animation-delay:.65s, 1.6s; --r:4deg; }
.ns-float.f3 { left:20px; top:330px; } .ns-float.f3 .ns-float-in { animation-delay:.8s, 1.2s; --r:3deg; }
.ns-float.f4 { right:40px; top:410px; } .ns-float.f4 .ns-float-in { animation-delay:.95s, 1.8s; --r:-3deg; }
.ns-float.f5 { right:14px; top:236px; } .ns-float.f5 .ns-float-in { animation-delay:1.1s, 1s; --r:-2deg; }
@keyframes ns-float-in { from { opacity:0; transform:translateY(40px) rotate(var(--r, 0deg)) scale(.96); } to { opacity:1; transform:rotate(var(--r, 0deg)); } }
@keyframes ns-bob { from { translate:0 0; } to { translate:0 -10px; } }
.ns-paper { background:var(--sellCard); border:1px solid var(--sellLine); border-radius:12px; box-shadow:0 22px 44px -26px rgba(54,47,42,.35); padding:14px 16px; width:210px; box-sizing:border-box; display:flex; flex-direction:column; gap:6px; color:var(--sellInk); font-size:12px; }
.ns-paper-eyebrow { font-size:9.5px; letter-spacing:.18em; text-transform:uppercase; color:var(--sellAccent); font-weight:600; }
.ns-paper-title { font-family:"EB Garamond", Georgia, serif; font-weight:500; font-size:17px; }
.ns-paper-lines { display:flex; flex-direction:column; gap:6px; margin-top:2px; }
.ns-paper-lines i { display:block; height:5px; border-radius:3px; background:var(--sellTrack); }
.ns-map { width:190px; height:140px; padding:0; position:relative; overflow:hidden; color:var(--sellInk); background:var(--sellWash); }
.ns-map-lot { fill:var(--sellLavender); stroke:var(--sellGlow); stroke-width:2; }
.ns-chip { position:absolute; left:10px; bottom:10px; font-size:10.5px; font-weight:600; background:var(--sellCard); border:1px solid var(--sellLine); border-radius:999px; padding:3px 9px; }
.ns-contract { width:230px; }
.ns-contract-title { font-family:"EB Garamond", Georgia, serif; font-weight:500; font-size:16px; }
.ns-contract-row { display:flex; align-items:center; gap:8px; font-size:11px; color:var(--sellSoft); }
.ns-contract-row em { font-style:normal; width:44px; }
.ns-contract-row mark { display:block; height:12px; width:70%; border-radius:3px; background:var(--sellAccentTint); box-shadow:0 0 0 1.5px var(--sellLavenderDeep); }
.ns-pill-paper { flex-direction:row; align-items:center; gap:10px; width:auto; border-radius:999px; padding:9px 16px 9px 9px; line-height:1.3; color:var(--sellSoft); }
.ns-pill-paper b { color:var(--sellInk); font-weight:600; }
.ns-pill-face { width:34px; height:34px; border-radius:50%; background:linear-gradient(140deg, var(--sellLavender), var(--sellGlow)); flex:none; }
.ns-tick { padding:9px 16px; color:var(--sellInk); font-weight:500; }
.ns-tick-mark { width:20px; height:20px; border-radius:50%; background:var(--sellGlow); color:var(--sellOnDark); display:inline-flex; align-items:center; justify-content:center; font-size:11px; }
@media (max-width: 1260px) { .ns-depth { display:none; } }

/* The chapters. */
.ns-story { display:flex; flex-direction:column; padding-bottom:12vh; }
.ns-ch { position:relative; min-height:76vh; display:flex; flex-direction:column; justify-content:center; padding:7vh 0; }
.ns-ch-num { position:absolute; left:-10px; bottom:-34px; font-family:"EB Garamond", Georgia, serif; font-size:150px; font-weight:400; letter-spacing:0; line-height:1; color:var(--sellTrack); z-index:-1; pointer-events:none;
  transform:translateY(calc(var(--p, 0) * 70px)); transition:color .6s; }
.ns-ch.on .ns-ch-num { color:var(--sellAccentTint); }
.ns-ch-eyebrow { position:relative; z-index:0; margin:0 0 8px; }
.ns-ch-title { position:relative; z-index:1; font-family:"EB Garamond", Georgia, serif; font-weight:400; font-size:44px; line-height:1.05; letter-spacing:-0.015em; margin:0 0 22px; color:var(--sellInk); }
.ns-ch-word { display:inline-block; opacity:0; transform:translateY(18px); transition:opacity .6s cubic-bezier(.2,.8,.2,1), transform .6s cubic-bezier(.2,.8,.2,1); }
.ns-ch.seen .ns-ch-word { opacity:1; transform:none; }
.ns-ch-body { position:relative; z-index:1; transform:translateY(calc(var(--p, 0) * 36px)); opacity:var(--o, 1); will-change:transform, opacity; }
.ns-ch-start { min-height:90vh; }
.ns-autoplay { position:fixed; left:50%; bottom:22px; translate:-50% 0; z-index:25; display:inline-flex; align-items:center; gap:10px; background:var(--sellCharcoal); color:var(--sellOnDark); border-radius:999px; padding:10px 12px 10px 16px; font-size:13.5px; box-shadow:0 10px 30px rgba(0,0,0,.2); animation:ns-up .4s ease both; white-space:nowrap; }
.ns-autoplay .ns-link { color:var(--sellOnDark); opacity:.8; }

/* The chapter rail above the draft. */
.ns-chapters { display:flex; flex-wrap:wrap; gap:6px; }
.ns-chapter { font:inherit; font-size:12.5px; font-weight:500; color:var(--sellSoft); background:var(--sellCard); border:1px solid var(--sellLine); border-radius:999px; padding:6px 12px 6px 8px; display:inline-flex; align-items:center; gap:7px; cursor:pointer; transition:background .3s, color .3s, border-color .3s; }
.ns-chapter-n { font-size:10.5px; font-weight:600; color:var(--sellFaint); font-variant-numeric:tabular-nums; }
.ns-chapter.seen { color:var(--sellInk); }
.ns-chapter.on { background:var(--sellCharcoal); color:var(--sellOnDark); border-color:var(--sellCharcoal); }
.ns-chapter.on .ns-chapter-n { color:var(--sellGlow); }

@media (max-width: 720px) {
  .ns-ch { min-height:0; padding:8vh 0 4vh; }
  .ns-ch-num { font-size:96px; bottom:-20px; }
  .ns-ch-title { font-size:34px; }
  .ns-autoplay { bottom:14px; font-size:12.5px; max-width:calc(100vw - 32px); white-space:normal; }
}
@media (prefers-reduced-motion: reduce) {
  .ns-float, .ns-ch-num, .ns-ch-body { transform:none !important; }
  .ns-float-in { animation:ns-float-in .01s both; }
  .ns-ch-word { transition:opacity .3s; transform:none; }
}
`;

const CHAPTER_MS = 2800;

const SELL_FONTS = "https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500&family=Figtree:wght@400;500;600&display=swap";

const SHEET = `
.ns-page { min-height:100vh; background:var(--sellBg); color:var(--sellInk); font-family:Figtree, system-ui, sans-serif; font-weight:400; -webkit-font-smoothing:antialiased; }
.ns-top { max-width:760px; margin:0 auto; padding:24px 16px 4px; box-sizing:content-box; }
.ns-logo { height:28px; }
.ns-main { max-width:760px; margin:0 auto; padding:0 16px 80px; transition:max-width .6s cubic-bezier(.2,.8,.2,1); }
.ns-main.wide, .ns-top.wide { max-width:1320px; }
.ns-top { transition:max-width .6s cubic-bezier(.2,.8,.2,1); }
.ns-flow.ns-split { display:grid; grid-template-columns:minmax(0, 1fr) minmax(0, 1.12fr); gap:16px 22px; align-items:start; }
/* v2: the two halves read as two halves, and the draft rides at the middle of the screen. */
.ns-v2 .ns-flow.ns-split { grid-template-columns:minmax(0, 1fr) minmax(0, 1.05fr); column-gap:56px; }
.ns-v2 .ns-split-side { top:max(72px, calc(50vh - (min(68vh, 760px) + 120px) / 2)); }
.ns-chapters-float { position:fixed; top:14px; left:50%; z-index:30; transform:translate(-50%, -12px); opacity:0; pointer-events:none;
  padding:6px; border-radius:999px; background:rgba(255,255,255,.82); backdrop-filter:blur(10px); -webkit-backdrop-filter:blur(10px);
  box-shadow:0 8px 28px rgba(43,33,41,.10); border:1px solid var(--sellLine); flex-wrap:nowrap; transition:opacity .25s ease, transform .25s ease; }
.ns-chapters-float.on { opacity:1; transform:translate(-50%, 0); pointer-events:auto; }
.ns-flow.ns-split > .ns-flow-head { grid-column:1 / -1; }
.ns-split-main { display:flex; flex-direction:column; gap:16px; min-width:0; }
.ns-flow:not(.ns-split) > .ns-split-main { display:contents; }
.ns-split-side { position:sticky; top:16px; display:flex; flex-direction:column; gap:16px; min-width:0; animation:ns-side .6s cubic-bezier(.2,.8,.2,1) both; }
@keyframes ns-side { from { opacity:0; transform:translateX(40px); } to { opacity:1; transform:none; } }
.ns-split-side .ns-draft-stage { height:min(68vh, 760px); }
.ns-split-side.with-form { position:static; }
.ns-v2 .ns-split-side.at-start { top:16px; }
.ns-v2 .ns-split-side { transition:top .5s cubic-bezier(.2,.8,.2,1); }
.ns-v2 .ns-split-side .ns-draft-stage { transition:height .5s cubic-bezier(.2,.8,.2,1); }
.ns-v2 .ns-split-side.at-start .ns-draft-stage { height:var(--ns-start-stage, min(68vh, 760px)); }
.ns-v2 .ns-split-side.at-start .ns-drafting { box-sizing:border-box; height:var(--ns-start-stage, auto); display:flex; flex-direction:column; overflow:hidden; }
.ns-v2 .ns-split-side.at-start .ns-drafting-page { flex:1; }
.ns-split-side.with-form .ns-draft-stage { height:min(52vh, 560px); }
.ns-drafting-eyebrow { display:flex; align-items:center; gap:8px; font-size:12px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--sellAccent); margin-bottom:6px; }
.ns-eta { display:flex; gap:14px; align-items:center; }
.ns-eta-ico { width:44px; height:44px; flex:none; border-radius:50%; background:var(--sellAccentTint); color:var(--sellAccent); display:inline-flex; align-items:center; justify-content:center; }
.ns-eta-label { font-size:12px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--sellSoft); }
.ns-eta-head { font-family:'EB Garamond', Georgia, serif; font-size:28px; line-height:1.15; color:var(--sellInk); }
.ns-eta-detail { font-size:14px; color:var(--sellSoft); margin-top:2px; }
.ns-card-eta { background:linear-gradient(120deg, var(--sellCard) 0%, var(--sellCard) 60%, var(--sellLavender) 180%); }
.ns-drafting { border:1px solid var(--sellLine); border-radius:14px; background:var(--sellWash); padding:18px; }
.ns-drafting-head { display:flex; align-items:center; gap:10px; font-weight:600; color:var(--sellInk); margin-bottom:14px; }
.ns-spark { width:30px; height:30px; border-radius:50%; background:var(--sellAccent); color:var(--sellOnDark); display:inline-flex; align-items:center; justify-content:center; animation:ns-pulse 1.4s ease-in-out infinite; }
.ns-drafting-page { background:var(--sellCard); border-radius:6px; box-shadow:0 6px 24px rgba(0,0,0,.06); padding:28px 30px; min-height:240px; font-family:Georgia, 'Times New Roman', serif; font-size:13px; color:var(--sellInk); }
.ns-drafting-page p { margin:0 0 12px; min-height:1em; }
.ns-drafting-title { font-size:20px; font-weight:600; text-align:center; letter-spacing:.01em; margin-bottom:18px !important; }
.ns-drafting-address { font-weight:600; }
.ns-caret { display:inline-block; width:2px; height:1.05em; margin-left:2px; vertical-align:-2px; background:var(--sellAccent); animation:ns-blink 1s steps(1) infinite; }
@keyframes ns-blink { 50% { opacity:0; } }
.ns-form-sub { grid-column:1 / -1; font-size:12px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--sellSoft); margin-top:6px; padding-top:12px; border-top:1px solid var(--sellLine); }
.ns-card-done .ns-form-sub { margin-bottom:12px; }
.ns-agent-hi { display:inline-flex; align-items:center; gap:8px; line-height:1.3; margin-top:8px; padding:6px 12px 6px 6px; border-radius:999px; background:var(--sellAccentTint); color:var(--sellInk); font-size:14px; font-weight:500; animation:ns-meet .4s cubic-bezier(.2,1.2,.4,1) both; width:fit-content; }
.ns-agent-dot { width:24px; height:24px; min-width:24px; flex:none; box-sizing:border-box; border-radius:50%; background:var(--sellAccent); color:var(--sellOnDark); display:inline-flex; align-items:center; justify-content:center; font-size:12px; font-weight:600; }
.ns-hero { padding-top:7vh; }
.ns-banner { background:var(--sellCream); border-radius:28px; padding:48px 40px 40px; text-align:center; box-shadow:var(--sellShadow); animation:ns-up .5s ease both; }
.ns-banner .ns-lede { margin-left:auto; margin-right:auto; max-width:520px; }
.ns-banner .ns-kicker { margin-bottom:18px; }
.ns-h1 { font-family:"EB Garamond", Georgia, serif; font-size:58px; font-weight:400; line-height:1.04; letter-spacing:-0.015em; margin:0 0 14px; color:var(--sellInk); }
.ns-h2 { font-family:"EB Garamond", Georgia, serif; font-size:34px; font-weight:400; line-height:1.1; letter-spacing:-0.01em; margin:0; }
.ns-lede { font-size:18px; font-weight:500; color:var(--sellInk); margin:0 0 28px; line-height:1.45; }
.ns-eyebrow { font-size:12px; font-weight:500; text-transform:uppercase; letter-spacing:.1em; color:var(--sellFaint); margin:0 0 4px; }
.ns-search { position:relative; }
.ns-search-icon { position:absolute; left:18px; top:50%; transform:translateY(-50%); color:var(--sellFaint); }
.ns-search-spin { position:absolute; right:18px; top:50%; margin-top:-9px; }
.ns-address { width:100%; box-sizing:border-box; font:inherit; font-size:19px; box-shadow:var(--sellShadow); padding:18px 48px 18px 50px; border:1px solid var(--sellLineStrong); border-radius:999px; outline:none; background:var(--sellCard); transition:border-color .15s, box-shadow .15s; }
.ns-address:focus { border-color:var(--sellAccent); box-shadow:0 0 0 4px var(--sellLavender); }
.ns-candidates { margin-top:12px; display:flex; flex-direction:column; gap:8px; animation:ns-up .25s ease both; }
.ns-candidate { text-align:left; font:inherit; background:var(--sellCard); border:1px solid var(--sellLine); border-radius:18px; padding:14px 18px; cursor:pointer; display:flex; flex-direction:column; gap:2px; transition:border-color .15s, transform .15s; }
.ns-candidate:hover { border-color:var(--sellInk); transform:translateY(-1px); }
.ns-candidate-address { font-weight:500; }
.ns-candidate-lots { font-size:13px; color:var(--sellSoft); }
.ns-msg { font-size:14px; color:var(--sellSoft); margin:8px 0; }
.ns-fine { font-size:12px; color:var(--sellFaint); margin:12px 0 0; }
.ns-flow { padding-top:24px; display:flex; flex-direction:column; gap:16px; }
.ns-flow-head { display:flex; justify-content:space-between; align-items:flex-end; gap:16px; }
.ns-link { font:inherit; font-size:14px; color:var(--sellAccent); background:none; border:none; cursor:pointer; padding:0; }
.ns-card { background:var(--sellCard); border:1px solid var(--sellLine); border-radius:28px; padding:26px 28px; box-shadow:var(--sellShadow); }
.ns-card-title { font-size:19px; font-weight:600; letter-spacing:-0.01em; color:var(--sellInk); margin:0 0 16px; }
.ns-steps { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:14px; }
.ns-step { display:flex; gap:14px; align-items:flex-start; transition:opacity .3s; }
.ns-step-waiting { opacity:.35; }
.ns-step-title { font-weight:500; font-size:15px; line-height:24px; }
.ns-step-line { font-size:14px; color:var(--sellSoft); line-height:1.45; animation:ns-up .3s ease both; }
.ns-dot { width:24px; height:24px; flex:none; border-radius:50%; border:2px solid var(--sellLine); box-sizing:border-box; }
.ns-spin { width:24px; height:24px; flex:none; border-radius:50%; border:2px solid var(--sellLine); border-top-color:var(--sellGlow); box-sizing:border-box; animation:ns-rot .8s linear infinite; display:inline-block; }
.ns-search .ns-spin { width:18px; height:18px; }
.ns-step-ico { position:relative; width:36px; height:36px; flex:none; border-radius:50%; border:2px solid var(--sellLine); box-sizing:border-box; display:inline-flex; align-items:center; justify-content:center; color:var(--sellFaint); transition:color .3s, border-color .3s, background .3s; }
.ns-step-ico.running { color:var(--sellAccent); border-color:var(--sellLavender); }
.ns-step-ico.running::after { content:""; position:absolute; inset:-2px; border-radius:50%; border:2px solid transparent; border-top-color:var(--sellGlow); animation:ns-rot .8s linear infinite; }
.ns-step-ico.running svg { animation:ns-pulse 1.2s ease-in-out infinite; }
.ns-step-ico.done { background:var(--sellAccentTint); animation:ns-pop .3s ease both; }
.ns-step-mark { position:absolute; right:-4px; bottom:-4px; width:16px; height:16px; border-radius:50%; color:var(--sellOnDark); display:inline-flex; align-items:center; justify-content:center; box-shadow:0 0 0 2px var(--sellCard); animation:ns-pop .35s .1s cubic-bezier(.2,1.4,.4,1) both; }
.ns-done { width:24px; height:24px; flex:none; border-radius:50%; border:2px solid; box-sizing:border-box; display:inline-flex; align-items:center; justify-content:center; animation:ns-pop .3s ease both; }
.ns-done-big { width:44px; height:44px; }
.ns-reveal { display:flex; flex-direction:column; gap:16px; animation:ns-up .4s ease both; }
.ns-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.ns-dl { display:grid; grid-template-columns:auto 1fr; gap:8px 16px; margin:0; font-size:14px; }
.ns-dl dt { color:var(--sellFaint); }
.ns-dl dd { margin:0; }
.ns-person { display:flex; gap:16px; align-items:flex-start; }
.ns-conv-line { display:flex; align-items:center; gap:12px; font-size:14.5px; color:var(--sellInk); }
.ns-conv-line .ns-avatar { font-size:15px; }
.ns-conv-role { color:var(--sellSoft); }
.ns-avatar { width:64px; height:64px; flex:none; border-radius:50%; object-fit:cover; background:var(--sellSlate); color:var(--sellOnDark); display:inline-flex; align-items:center; justify-content:center; font-size:26px; font-weight:500; }
.ns-person-name { font-size:26px; font-weight:600; letter-spacing:-0.01em; display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.ns-role { font-size:13px; font-weight:500; color:var(--sellAccent); border:1.5px solid var(--sellLavenderDeep); border-radius:7px; padding:1px 8px; letter-spacing:0; }
.ns-stats { display:flex; flex-wrap:wrap; gap:6px 16px; font-size:14px; color:var(--sellSoft); margin-top:4px; }
.ns-stats b { color:var(--sellInk); }
.ns-bio { font-size:14px; color:var(--sellSoft); margin:10px 0 0; line-height:1.5; }
.ns-peek { position:fixed; max-width:calc(100vw - 16px); z-index:2147483647; box-sizing:border-box; background:var(--sellCard); border:1px solid var(--sellLineStrong); border-radius:10px; padding:12px 12px 10px; box-shadow:0 10px 30px rgba(0,0,0,.16); text-align:left; animation:nsPeek .14s ease-out; }
@keyframes nsPeek { from { opacity:0; transform:translateY(-4px); } }
.ns-peek-head { display:flex; justify-content:space-between; align-items:center; gap:8px; margin:0 22px 8px 0; }
.ns-peek-label { font-size:12px; font-weight:600; color:var(--sellInk); }
.ns-peek-lock { display:inline-flex; align-items:center; gap:4px; flex:none; font-size:11px; color:var(--sellFaint); border:1px solid var(--sellLine); border-radius:999px; padding:1px 7px; }
.ns-peek-input { width:100%; box-sizing:border-box; font:inherit; font-size:13px; padding:7px 9px; border:1px solid var(--sellLine); border-radius:6px; background:color-mix(in srgb, var(--sellSlate) 6%, transparent); color:var(--sellSoft); cursor:not-allowed; }
.ns-peek-actions { display:flex; justify-content:flex-end; gap:6px; margin-top:8px; opacity:.45; }
.ns-peek-btn { font-size:12px; padding:4px 12px; border-radius:999px; border:1px solid var(--sellLine); color:var(--sellSoft); cursor:not-allowed; }
.ns-peek-save { background:var(--sellLine); }
.ns-peek-note { font-size:12px; color:var(--sellFaint); margin:8px 0 0; line-height:1.4; }
.ns-questions-closable { position:relative; }
.ns-questions-close { position:absolute; top:10px; right:10px; z-index:1; width:32px; height:32px; border:0; background:none; font-size:22px; line-height:1; color:var(--sellFaint); cursor:pointer; border-radius:50%; }
.ns-questions-close:hover { color:var(--sellInk); }
.ns-peek-close { position:absolute; top:6px; right:6px; width:24px; height:24px; border:0; background:none; font-size:18px; line-height:1; color:var(--sellFaint); cursor:pointer; border-radius:50%; }
.ns-choose { margin-top:18px; padding-top:14px; border-top:1px solid var(--sellLine); text-align:left; }
.ns-choose-head { font-size:13px; font-weight:600; color:var(--sellSoft); margin-bottom:8px; }
.ns-choose-wrap { position:relative; }
.ns-choose-strip { display:flex; gap:10px; overflow-x:auto; scroll-snap-type:x mandatory; padding:2px 2px 6px; scrollbar-width:none; }
.ns-choose-strip::-webkit-scrollbar { display:none; }
.ns-choose-card { flex:0 0 132px; scroll-snap-align:start; display:flex; flex-direction:column; align-items:center; gap:3px; padding:12px 8px; border:1.5px solid var(--sellLine); border-radius:12px; background:var(--sellCard); font:inherit; color:var(--sellInk); cursor:pointer; text-align:center; transition:border-color .15s, box-shadow .15s; }
.ns-choose-card:hover:not(:disabled) { border-color:var(--sellLavenderDeep); }
.ns-choose-card.ns-on { border-color:var(--sellAccent); box-shadow:0 0 0 3px var(--sellLavender); }
.ns-choose-card.ns-full { cursor:default; opacity:.55; }
.ns-choose-card.ns-full .ns-avatar { filter:grayscale(1); }
.ns-choose-name { font-size:14px; font-weight:600; margin-top:4px; }
.ns-choose-line { font-size:12px; color:var(--sellFaint); line-height:1.3; }
.ns-choose-card.ns-on .ns-choose-line:last-child { color:var(--sellAccent); font-weight:500; }
.ns-choose-more { position:absolute; right:-6px; top:50%; transform:translateY(-50%); width:32px; height:32px; border-radius:50%; border:1px solid var(--sellLine); background:var(--sellCard); color:var(--sellInk); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; box-shadow:0 2px 8px rgba(0,0,0,.12); }
.ns-choose-more.ns-back { right:auto; left:-6px; }
.ns-choose-note { font-size:12px; color:var(--sellFaint); margin:8px 0 0; line-height:1.45; }
.ns-searches { list-style:none; margin:0; padding:0; font-size:14px; text-align:left; }
.ns-searches li { display:flex; justify-content:space-between; gap:16px; padding:9px 0; border-top:1px solid var(--sellLine); }
.ns-searches li:first-child { border-top:none; }
.ns-issuer { color:var(--sellFaint); text-align:right; }
.ns-timeframe { font-size:15px; margin:0 0 14px; }
.ns-next { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:12px; font-size:15px; }
.ns-next li { display:flex; gap:12px; }
.ns-next-n { width:24px; height:24px; flex:none; border-radius:50%; border:1px solid var(--sellLineStrong); font-size:12px; display:inline-flex; align-items:center; justify-content:center; color:var(--sellSoft); }
.ns-next li.now .ns-next-n { border-color:var(--sellAccent); color:var(--sellAccent); }
.ns-now { white-space:nowrap; margin-left:8px; font-size:11px; font-weight:600; color:var(--sellAccent); background:var(--sellAccentTint); border:1px solid var(--sellAccentTint); border-radius:999px; padding:1px 8px; vertical-align:2px; }
.ns-form { display:flex; flex-direction:column; gap:16px; }
.ns-toggle { display:inline-flex; border:1px solid var(--sellLineStrong); border-radius:999px; padding:3px; align-self:flex-start; }
.ns-toggle-btn { font:inherit; font-size:14px; border:none; background:none; border-radius:999px; padding:8px 16px; cursor:pointer; color:var(--sellSoft); }
.ns-toggle-btn.on { background:var(--sellCharcoal); color:var(--sellOnDark); }
.ns-toggle-btn { white-space:nowrap; }
@media (max-width: 420px) { .ns-toggle { display:flex; align-self:stretch; } .ns-toggle-btn { flex:1; padding:8px 6px; font-size:13px; } }
.ns-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px 16px; }
.ns-field { display:flex; flex-direction:column; gap:6px; }
.ns-label { font-size:13px; font-weight:500; }
.ns-hint { font-size:12px; color:var(--sellFaint); }
.ns-form-note { grid-column:1 / -1; margin-top:-6px; }
.ns-terms { display:flex; flex-direction:column; gap:8px; padding:14px 16px; border:1px solid var(--sellLine); border-radius:12px; background:var(--sellCard); }
.ns-terms-head { font-size:13px; font-weight:600; color:var(--sellInk); }
.ns-terms-list { margin:0; padding-left:18px; font-size:13px; line-height:1.5; color:var(--sellSoft); display:flex; flex-direction:column; gap:4px; }
.ns-price { display:flex; flex-direction:column; gap:6px; padding:14px 16px; border:1px solid var(--sellLine); border-radius:12px; background:var(--sellCard); font-size:14px; }
.ns-price-row { display:flex; justify-content:space-between; gap:12px; }
.ns-price-was { color:var(--sellFaint); margin-right:8px; font-weight:400; }
.ns-price-total { font-weight:600; padding-top:6px; border-top:1px solid var(--sellLine); }
.ns-bill-sub { font-size:12px; color:var(--sellFaint); text-transform:uppercase; letter-spacing:.08em; padding-top:6px; }
.ns-bill-line { font-size:13.5px; color:var(--sellSoft); }
.ns-pay { display:flex; flex-direction:column; gap:14px; }
.ns-promo { display:flex; gap:8px; flex-wrap:wrap; }
/* The button takes the field's height, and the field keeps the height every
   other field on the card has. */
.ns-promo .ns-btn { align-self:stretch; padding:0 22px; font-size:15px; justify-content:center; }
.ns-promo .ns-input { flex:1; min-width:0; text-transform:uppercase; }
.ns-promo-open { align-self:flex-start; font-size:13px; }
.ns-promo-hide { align-self:center; margin-left:4px; font-size:14px; }
.ns-promo-tag { white-space:nowrap; display:inline-block; margin-left:8px; font-size:11.5px; border:1px solid var(--sellLineStrong); border-radius:999px; padding:1px 8px; color:var(--sellSoft); }
.ns-picker { position:relative; }
.ns-picker-list { position:absolute; left:0; right:0; top:100%; z-index:5; list-style:none; margin:4px 0 0; padding:4px; background:var(--sellCard); border:1px solid var(--sellLineStrong); border-radius:12px; box-shadow:var(--sellShadow); max-height:260px; overflow:auto; }
.ns-picker-list button { display:flex; flex-direction:column; align-items:flex-start; gap:1px; width:100%; text-align:left; font:inherit; font-size:14px; background:none; border:0; border-radius:8px; padding:8px 10px; cursor:pointer; color:var(--sellInk); }
.ns-picker-list button:hover, .ns-picker-list li[aria-selected="true"] button { background:var(--sellWash); }
.ns-picker-list button span { font-size:12px; color:var(--sellFaint); }
.ns-choice-sub { color:var(--sellFaint); font-weight:400; }
.ns-input { font:inherit; font-size:15px; padding:12px 16px; border:1px solid var(--sellLineStrong); border-radius:12px; outline:none; background:var(--sellCard); color:var(--sellInk); }
.ns-input:focus { border-color:var(--sellAccent); box-shadow:0 0 0 3px var(--sellLavender); }
.ns-trap { position:absolute; left:-10000px; width:1px; height:1px; opacity:0; }
.ns-questions { display:flex; flex-direction:column; gap:22px; margin:18px 0 6px; }
.ns-question + .ns-question { border-top:1px solid var(--sellLine); padding-top:22px; }
.ns-question { display:flex; flex-direction:column; gap:8px; }
.ns-question.ns-question-attached { border-top:0; padding-top:0; }
.ns-question-row { display:flex; flex-wrap:wrap; gap:16px; }
.ns-question-cell { flex:1 1 240px; min-width:0; display:flex; flex-direction:column; gap:8px; }
.ns-question-missing .ns-label { color:var(--sellAccent); }
.ns-optional { color:var(--sellSoft); font-weight:400; }
.ns-choices { display:flex; flex-wrap:wrap; gap:8px; }
.ns-choice { font:inherit; font-size:14px; border:1px solid var(--sellLineStrong); background:var(--sellCard); color:var(--sellInk); border-radius:999px; padding:8px 16px; cursor:pointer; transition:background .15s, color .15s, border-color .15s; }
.ns-choice:hover { border-color:var(--sellAccent); }
.ns-choice.on { background:var(--sellCharcoal); border-color:var(--sellCharcoal); color:var(--sellOnDark); }
.ns-works { display:flex; flex-direction:column; gap:8px; align-items:flex-start; }
.ns-works-row { display:grid; grid-template-columns:2fr 1.4fr 1fr; gap:8px; width:100%; }
@media (max-width:640px) { .ns-works-row { grid-template-columns:1fr; } }
.ns-question-nav { display:flex; justify-content:flex-end; align-items:center; gap:16px; margin-top:14px; flex-wrap:wrap; }
.ns-saved-note { margin-right:auto; font-size:12.5px; color:var(--sellSoft); }
@media (max-width: 480px) { .ns-saved-note { flex-basis:100%; order:3; text-align:right; } }
.ns-consent { display:flex; gap:12px; font-size:14px; color:var(--sellInk); line-height:1.45; align-items:flex-start; padding:12px 14px; border:1.5px solid var(--sellLineStrong); border-radius:10px; background:var(--sellCard); cursor:pointer; transition:border-color .2s, background .2s; }
.ns-consent input { width:20px; height:20px; margin:0; flex:none; accent-color:var(--sellInk); cursor:pointer; }
.ns-consent.on { border-color:var(--sellInk); background:var(--sellWash); }
.ns-consent.ns-consent-attn { border-color:var(--sellPop, var(--sellAccent)); box-shadow:0 0 0 3px var(--sellPopTint, var(--sellAccentTint)); }
.ns-consent-why { margin:-4px 0 0; font-size:13px; color:var(--sellPop, var(--sellAccent)); }
/* Actions inside a line of text read as links: ink, underlined, a hover. */
.ns-view-doc, .ns-inline-link { color:var(--sellInk); font-weight:500; text-decoration:underline; text-decoration-color:var(--sellLineStrong); text-underline-offset:3px; text-decoration-thickness:1px; }
.ns-view-doc:hover, .ns-inline-link:hover { text-decoration-color:currentColor; }
.ns-view-doc { display:inline-flex; align-items:center; gap:5px; margin-top:4px; padding:0; font-size:13px; }
.ns-banner-note { margin:0 0 4px; padding:12px 16px; border-radius:12px; border:1px solid var(--sellLineStrong); background:var(--sellCard); color:var(--sellInk); font-size:14px; }
.ns-pay-step { display:flex; align-items:center; gap:10px; font-size:15px; font-weight:600; color:var(--sellInk); margin-top:6px; }
.ns-pay-n { width:24px; height:24px; flex:none; border-radius:50%; border:1px solid var(--sellLineStrong); display:inline-flex; align-items:center; justify-content:center; font-size:12px; font-weight:600; color:var(--sellSoft); }
.ns-accept { display:flex; gap:12px; align-items:center; margin-top:6px; padding:12px 14px; border:1.5px solid var(--sellLineStrong); border-radius:10px; background:var(--sellCard); cursor:pointer; font-size:15px; color:var(--sellInk); transition:border-color .2s, box-shadow .2s, background .2s; }
.ns-accept input { width:20px; height:20px; margin:0; flex:none; accent-color:var(--sellInk); cursor:pointer; }
.ns-accept b { font-weight:600; }
.ns-accept.on { border-color:var(--sellInk); background:var(--sellWash); }
.ns-accept-attn { border-color:var(--sellPop, #FE5442); box-shadow:0 0 0 3px var(--sellPopTint, rgba(254,84,66,.12)); animation:ns-nudge .4s ease; }
.ns-accept-why { display:block; font-size:12.5px; font-weight:500; color:var(--sellPop, #FE5442); margin-top:2px; }
@keyframes ns-nudge { 0%,100% { transform:none; } 25% { transform:translateX(-4px); } 75% { transform:translateX(4px); } }
.ns-error { color:var(--sellAccent); font-size:14px; margin:0; }
.ns-btn { font:inherit; font-size:17px; font-weight:500; background:var(--sellCharcoal); color:var(--sellOnDark); border:none; border-radius:999px; padding:16px 30px; cursor:pointer; align-self:flex-start; transition:background .15s, transform .15s; }
.ns-btn.soft { background:var(--sellLavender); color:var(--sellInk); border-radius:18px; }
.ns-btn.soft:hover:not(:disabled) { background:var(--sellLavenderDeep); }
.ns-btn:hover:not(:disabled) { background:var(--sellInk); transform:translateY(-1px); }
.ns-btn:disabled { opacity:.45; cursor:default; }
.ns-done-head { display:flex; gap:16px; align-items:center; margin-bottom:16px; }
.ns-steps-wrap { display:flex; gap:24px; align-items:flex-start; }
.ns-steps-wrap .ns-steps { flex:1; min-width:0; }
.ns-step-live { color:var(--sellSoft); font-variant-numeric:tabular-nums; }
.ns-parcel { margin:0; width:200px; flex:none; text-align:center; animation:ns-up .4s ease both; }
.ns-parcel svg { width:100%; height:180px; overflow:visible; }
.ns-parcel-line { fill:none; stroke:var(--sellAccent); stroke-width:1.2; stroke-linejoin:round; stroke-dasharray:1; stroke-dashoffset:1; animation:ns-trace 1.8s ease-out forwards; }
.ns-parcel-fill { fill:var(--sellLavender); opacity:0; animation:ns-fill .6s ease 1.5s forwards; }
.ns-parcel figcaption { display:flex; flex-direction:column; gap:2px; font-size:12px; color:var(--sellFaint); margin-top:8px; }
.ns-parcel figcaption b { font-family:"EB Garamond", Georgia, serif; font-size:22px; color:var(--sellInk); font-weight:400; }
.ns-tracked { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin:16px 4px 0; }
.ns-tracked-link { display:inline-flex; align-items:center; gap:8px; font-size:13px; color:var(--sellInk); text-decoration:none; border:1px solid var(--sellLine); border-radius:999px; padding:6px 12px; }
.ns-tracked-link:hover { border-color:var(--sellInk); }
.ns-follow { display:flex; flex-wrap:wrap; gap:12px; align-items:center; margin-top:20px; }
.ns-follow .ns-btn { text-decoration:none; }
.ns-follow-line { margin:20px 0 0; }
button.ns-live { font:inherit; font-size:12px; background:none; cursor:pointer; }
button.ns-live:hover:not(:disabled) { border-color:var(--sellLineStrong); color:var(--sellInk); }
button.ns-live:disabled { cursor:default; }
.ns-live { display:inline-flex; align-items:center; gap:8px; font-size:12px; color:var(--sellSoft); border:1px solid var(--sellLine); border-radius:999px; padding:5px 12px; white-space:nowrap; font-variant-numeric:tabular-nums; }
.ns-live-dot { width:8px; height:8px; border-radius:50%; background:var(--sellGlow); box-shadow:0 0 0 3px rgba(255,169,70,.25); flex:none; animation:ns-pulse 2s ease-in-out infinite; }
.ns-live.off .ns-live-dot { background:var(--sellFaint); animation:none; }
.ns-track-steps li.done .ns-next-n { background:var(--sellAccent); border-color:var(--sellAccent); color:var(--sellOnDark); }
.ns-track-steps li.current .ns-next-n { border-color:var(--sellAccent); color:var(--sellAccent); animation:ns-ring 2s ease-out infinite; }
.ns-track-steps li.upcoming { opacity:.55; }
.ns-bar { display:flex; gap:3px; height:8px; margin-bottom:8px; }
.ns-seg { flex:1; border-radius:999px; background:var(--sellTrack); transition:background .6s; }
.ns-seg.ordered { background:var(--sellLavender); background-image:linear-gradient(90deg, var(--sellLavender), var(--sellLavenderDeep), var(--sellLavender)); background-size:200% 100%; animation:ns-shimmer 2.4s linear infinite; }
.ns-seg.received { background:var(--sellAccent); }
.ns-bar-text { font-size:14px; color:var(--sellSoft); margin:0 0 10px; }
.ns-bar-text b { color:var(--sellInk); }
.ns-issuer-under { display:block; font-size:12px; color:var(--sellFaint); }
.ns-chip { font-size:11px; font-weight:600; border-radius:999px; padding:2px 9px; border:1px solid var(--sellLineStrong); color:var(--sellSoft); white-space:nowrap; align-self:center; }
.ns-chip.ordered { background:var(--sellLavender); border-color:var(--sellLavenderDeep); color:var(--sellInk); }
.ns-chip.received { background:var(--sellAccent); border-color:var(--sellAccent); color:var(--sellOnDark); }
.ns-chip.not_required { color:var(--sellFaint); border-style:dashed; }
.ns-fresh { animation:ns-flash 4s ease; }
.ns-queue-head { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:10px; }
.ns-queue-count { font-size:15px; color:var(--sellSoft); }
.ns-queue-count b { font:inherit; color:inherit; }
.ns-live-mini { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:var(--sellSoft); }
.ns-queue-list li { align-items:center; transition:background .4s; }
.ns-queue-list li.in .ns-badge { background:var(--sellAccent); color:var(--sellOnDark); transition:background .4s, color .4s; }
.ns-queue { position:relative; flex:none; width:120px; height:30px; display:inline-block; }
.ns-queue-line { position:absolute; left:7px; right:7px; top:8px; height:3px; border-radius:999px; background:var(--sellTrack); }
.ns-queue-fill { position:absolute; left:0; top:0; bottom:0; border-radius:999px; background:var(--sellAccent); transition:width .9s cubic-bezier(.2,.8,.2,1); }
.ns-queue-stop { position:absolute; top:2px; width:15px; height:15px; margin-left:-1px; border-radius:50%; background:var(--sellCard); border:2px solid var(--sellLineStrong); box-sizing:border-box; display:inline-flex; align-items:center; justify-content:center; color:var(--sellOnDark); }
.ns-queue-stop.past { background:var(--sellAccent); border-color:var(--sellAccent); }
.ns-queue-stop.now { border-color:var(--sellAccent); animation:ns-ring 2s ease-out infinite; }
.ns-queue.at-2 .ns-queue-stop.now { background:var(--sellAccent); animation:ns-pop .5s cubic-bezier(.2,1.4,.4,1) both; }
.ns-queue-label { position:absolute; left:0; right:0; top:19px; font-size:11px; font-weight:600; color:var(--sellSoft); text-align:right; }
.ns-queue.at-2 .ns-queue-label { color:var(--sellAccent); }
.ns-toast { position:fixed; left:50%; bottom:24px; transform:translateX(-50%); z-index:6; display:flex; align-items:center; gap:10px; padding:12px 18px 12px 12px; border-radius:999px; background:var(--sellCharcoal); color:var(--sellOnDark); box-shadow:var(--sellShadow); font-size:14px; animation:ns-dock .45s cubic-bezier(.2,1.2,.4,1) both; max-width:calc(100% - 32px); }
.ns-toast-ico { width:30px; height:30px; border-radius:50%; background:var(--sellAccent); display:inline-flex; align-items:center; justify-content:center; flex:none; }
.ns-share { display:flex; flex-direction:column; gap:10px; align-items:flex-start; }
.ns-share .ns-btn svg { margin-right:8px; vertical-align:-2px; }
.ns-share-form { display:flex; flex-wrap:wrap; gap:8px; align-items:center; width:100%; }
.ns-share-form .ns-input { flex:1 1 160px; }
.ns-replay-go { margin-top:16px; }
.ns-inline-link { font-size:inherit; padding:0; margin-left:4px; }
.ns-step-actions { display:flex; flex-wrap:wrap; gap:4px 16px; margin-top:2px; }
.ns-step-actions .ns-inline-link { margin-left:0; }
.ns-answers { margin:10px 0 2px; padding:14px 16px; border:1px solid var(--sellLine); border-radius:12px; background:var(--sellWash); display:flex; flex-direction:column; gap:14px; animation:ns-up .25s ease both; }
.ns-answers-title { font-size:11.5px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--sellSoft); margin-bottom:6px; }
.ns-answers-list { display:grid; grid-template-columns:minmax(0, 1.3fr) minmax(0, 1fr); gap:6px 16px; margin:0; font-size:13.5px; }
.ns-answers-list dt { color:var(--sellSoft); }
.ns-answers-list dd { margin:0; color:var(--sellInk); font-weight:500; overflow-wrap:anywhere; }
.ns-answers-by { color:var(--sellSoft); font-weight:400; }
@media (max-width: 560px) { .ns-answers-list { grid-template-columns:1fr; gap:2px; } .ns-answers-list dd { margin-bottom:8px; } }
.ns-share-hide { align-self:center; margin-left:6px; font-size:14px; }
.ns-trust { display:flex; align-items:center; gap:8px; margin:4px 0 0; font-size:13.5px; color:var(--sellSoft); }
.ns-trust b { color:var(--sellInk); font-weight:600; }
.ns-trust-stars { color:var(--sellStar); letter-spacing:1px; font-size:14px; }
.ns-contact-name { grid-column:1 / -1; }
.ns-field-error { font-size:12.5px; color:var(--sellAccent); }
.ns-saved { display:flex; align-items:center; gap:10px; flex-wrap:wrap; font-size:15px; }
.ns-saved .ns-done { width:24px; height:24px; display:inline-flex; align-items:center; justify-content:center; border-radius:999px; }
.ns-saved .ns-link { margin-left:auto; }
/* One height for the boxes and the button beside them. */
.ns-share-form .ns-input, .ns-share-form .ns-btn { height:48px; box-sizing:border-box; }
.ns-share-form .ns-btn { padding:0 22px; font-size:15px; }
.ns-share-row .ns-btn { padding:0 18px; height:48px; box-sizing:border-box; font-size:15px; flex:1 1 96px; justify-content:center; white-space:nowrap; }
.ns-share-row .ns-btn.ns-share-more { flex:none; width:48px; padding:0; }
.ns-share-or { margin:4px 0 -4px; }
.ns-share-row .ns-btn.ns-share-more svg { margin-right:0; }
@media (min-width:1024px) {
  .ns-main-track { max-width:1120px; }
  .ns-track-grid { display:grid; grid-template-columns:minmax(0,1fr) 340px; gap:24px; align-items:start; }
  .ns-track-side { position:sticky; top:24px; padding-top:24px; }
  .ns-track-side .ns-share-form { flex-direction:column; align-items:stretch; }
  .ns-track-side .ns-share-form .ns-input { flex:none; width:100%; }
  .ns-track-side .ns-share-form .ns-btn { align-self:flex-start; }
}
.ns-side-card .ns-label { display:block; margin-bottom:8px; }
.ns-person-side { align-items:center; margin:0; }
.ns-contact-lines { display:flex; flex-direction:column; gap:4px; margin-top:12px; font-size:14px; word-break:break-all; }
.ns-side-share { margin-top:18px; padding-top:16px; border-top:1px solid var(--sellLine); }
.ns-share-row { display:flex; flex-wrap:wrap; gap:8px; width:100%; max-width:560px; align-items:center; }
.ns-share-row .ns-btn svg { margin-right:8px; vertical-align:-2px; }
.ns-upload { display:flex; flex-direction:column; gap:8px; align-items:flex-start; }
.ns-upload-btn { position:relative; overflow:hidden; cursor:pointer; }
/* The size of the answer pills beside it, not a call to action. */
.ns-btn.ns-upload-btn { font-size:14px; padding:9px 18px; border-radius:999px; }
.ns-choice-note { margin:10px 0 0; font-size:14px; line-height:1.5; color:var(--sellInk); }
.ns-upload-btn input { position:absolute; inset:0; opacity:0; cursor:pointer; }
.ns-unlisted { display:block; margin:12px 0 0; text-align:left; }
.ns-replay { display:inline-block; margin-top:6px; font-size:13px; }
.ns-share-url { width:100%; box-sizing:border-box; font-size:13px; color:var(--sellSoft); }
.ns-from-agent { color:var(--sellSoft); font-weight:400; }
.ns-from-agent-note { font-size:13px; color:var(--sellSoft); margin:14px 0 0; }
.ns-choice-more { font-size:14px; align-self:center; margin-left:4px; }
.ns-share-note { flex-basis:100%; }
.ns-share-sent { display:flex; align-items:center; gap:6px; margin:0; font-size:14px; color:var(--sellAccent); }
.ns-done-share { margin-top:16px; padding-top:16px; border-top:1px solid var(--sellLine); }
.ns-local { display:inline-flex; align-items:center; gap:8px; margin-top:8px; padding:6px 14px 6px 6px; border-radius:999px; background:var(--sellCream); border:1px solid var(--sellLine); font-size:13px; font-weight:500; color:var(--sellInk); animation:ns-meet .5s cubic-bezier(.2,1.2,.4,1) both; }
.ns-local-ico { width:22px; height:22px; border-radius:50%; background:var(--sellAccent); color:var(--sellOnDark); display:inline-flex; align-items:center; justify-content:center; }
.ns-meet { display:flex; align-items:center; gap:12px; margin-top:10px; padding:10px 16px 10px 10px; border-radius:18px; background:var(--sellAccentTint); width:fit-content; max-width:100%; box-sizing:border-box; animation:ns-meet .5s cubic-bezier(.2,1.2,.4,1) both; }
.ns-meet .ns-avatar { box-shadow:0 0 0 3px var(--sellCard); }
.ns-meet-hi { font-weight:600; color:var(--sellInk); font-size:15px; }
.ns-meet-sub { font-size:13px; color:var(--sellSoft); }
@keyframes ns-meet { from { opacity:0; transform:translateY(8px) scale(.96); } to { opacity:1; transform:none; } }
.ns-step-face { padding:0; border-color:var(--sellAccent); background:var(--sellCard); }
.ns-step-face img { width:100%; height:100%; border-radius:50%; object-fit:cover; }
.ns-updates { list-style:none; margin:0; padding:0; font-size:14px; }
.ns-updates li { display:flex; gap:14px; padding:8px 0; border-top:1px solid var(--sellLine); }
.ns-updates li:first-child { border-top:none; }
.ns-update-at { color:var(--sellFaint); min-width:124px; flex:none; font-variant-numeric:tabular-nums; }
@keyframes ns-trace { to { stroke-dashoffset:0; } }
@keyframes ns-fill { to { opacity:.9; } }
@keyframes ns-pulse { 0%,100% { opacity:1; } 50% { opacity:.35; } }
@keyframes ns-ring { 0% { box-shadow:0 0 0 0 rgba(3,79,70,.3); } 100% { box-shadow:0 0 0 8px rgba(3,79,70,0); } }
@keyframes ns-shimmer { to { background-position:-200% 0; } }
@keyframes ns-flash { 0%,30% { background:var(--sellLavender); } 100% { background:transparent; } }
.ns-page { position:relative; isolation:isolate; overflow-x:hidden; }
.ns-top, .ns-main { position:relative; z-index:1; }
.ns-logo { height:26px; display:block; }
.ns-aurora { position:fixed; inset:0; z-index:0; pointer-events:none; overflow:hidden; }
.ns-aurora span { position:absolute; border-radius:50%; filter:blur(70px); opacity:.55; will-change:transform; }
.ns-aurora .b1 { width:520px; height:520px; background:var(--sellLavender); opacity:.35; top:-140px; right:-120px; animation:ns-drift1 26s ease-in-out infinite alternate; }
.ns-aurora .b2 { width:420px; height:420px; background:var(--sellGlow); opacity:.12; bottom:-160px; left:-120px; animation:ns-drift2 30s ease-in-out infinite alternate; }
.ns-aurora .b3 { width:360px; height:360px; background:var(--sellLavenderDeep); opacity:.2; top:40%; left:55%; animation:ns-drift3 34s ease-in-out infinite alternate; }
.ns-aurora .ns-spot { width:600px; height:600px; left:calc(var(--mx, -999px) - 300px); top:calc(var(--my, -999px) - 300px); background:var(--sellLavender); opacity:.18; filter:blur(90px); transition:left .25s ease-out, top .25s ease-out; }
.ns-kicker { display:inline-flex; align-items:center; gap:8px; font-size:13px; font-weight:500; color:var(--sellAccent); background:var(--sellAccentTint); border-radius:999px; padding:6px 14px; margin:0 0 22px; animation:ns-up .5s ease both; }
.ns-word { display:inline-block; animation:ns-word .7s cubic-bezier(.2,.7,.2,1) both; }
.ns-accent { font-style:italic; color:var(--sellSoft); }
.ns-fade { animation:ns-up .6s ease both; }
.ns-search { border-radius:999px; }
.ns-ring { position:absolute; inset:-3px; border-radius:999px; background:conic-gradient(from var(--ang, 0deg), var(--sellLavender), var(--sellGlow), var(--sellLavenderDeep), var(--sellLavender)); opacity:0; transition:opacity .3s; z-index:-1; animation:ns-spin-ring 4s linear infinite; }
.ns-search:focus-within .ns-ring { opacity:1; }
.ns-search .ns-address:focus { box-shadow:var(--sellShadow); border-color:transparent; }
.ns-promises { list-style:none; display:flex; flex-wrap:wrap; gap:10px 22px; margin:22px 4px 0; padding:0; font-size:14px; color:var(--sellSoft); }
.ns-promises li { display:inline-flex; align-items:center; gap:8px; }
.ns-promise-ico { width:26px; height:26px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; background:var(--sellCard); border:1px solid var(--sellLine); color:var(--sellAccent); }
.ns-candidate { flex-direction:row !important; justify-content:space-between; align-items:center; animation:ns-up .35s ease both; }
.ns-candidate > span:first-child { display:flex; flex-direction:column; gap:2px; }
.ns-candidate-go { font-size:20px; color:var(--sellAccent); transition:transform .2s; }
.ns-candidate:hover .ns-candidate-go { transform:translateX(4px); }
.ns-candidate:hover { box-shadow:var(--sellShadow); }
.ns-progress { display:flex; justify-content:space-between; align-items:baseline; font-size:13px; font-weight:500; color:var(--sellSoft); margin-bottom:8px; }
.ns-progress-count b { font:inherit; color:inherit; }
.ns-progress-bar { height:4px; border-radius:999px; background:var(--sellTrack); overflow:hidden; margin-bottom:20px; }
.ns-progress-bar span { display:block; height:100%; border-radius:999px; background:linear-gradient(90deg, var(--sellAccent), var(--sellLavenderDeep)); transition:width .6s cubic-bezier(.2,.7,.2,1); }
.ns-step-done .ns-step-title { color:var(--sellInk); }
.ns-step-running { background:linear-gradient(90deg, transparent, var(--sellWash), transparent); background-size:200% 100%; animation:ns-shimmer 2s linear infinite; border-radius:12px; margin:-6px -10px; padding:6px 10px; }
.ns-rise { opacity:0; transform:translateY(18px); transition:opacity .7s ease, transform .7s cubic-bezier(.2,.7,.2,1); }
.ns-rise.in { opacity:1; transform:none; }
.ns-tilt { transition:transform .25s ease; transform-style:preserve-3d; background-image:radial-gradient(circle at var(--gx, 50%) var(--gy, 0%), color-mix(in srgb, var(--sellLavender) 33%, transparent), transparent 55%); }
.ns-stat b { font-size:22px; font-weight:600; color:var(--sellInk); font-variant-numeric:tabular-nums; }
.ns-cascade li { animation:ns-up .45s ease both; align-items:center; }
.ns-search-name { display:inline-flex; align-items:center; gap:12px; }
.ns-badge { width:30px; height:30px; flex:none; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; font-size:11px; font-weight:600; letter-spacing:.02em; color:var(--sellAccent); background:var(--sellAccentTint); }
.ns-card-cta { background:linear-gradient(160deg, var(--sellCard) 0%, var(--sellCard) 55%, var(--sellLavender) 160%); }
.ns-btn { position:relative; overflow:hidden; display:inline-flex; align-items:center; gap:10px; }
.ns-btn::after { content:""; position:absolute; top:0; left:-60%; width:40%; height:100%; background:linear-gradient(100deg, transparent, rgba(255,255,255,.22), transparent); transform:skewX(-20deg); transition:left .6s ease; }
.ns-btn:hover:not(:disabled)::after { left:120%; }
.ns-btn-arrow { transition:transform .2s; }
.ns-btn:hover:not(:disabled) .ns-btn-arrow { transform:translateX(4px); }
.ns-done-celebrate { position:relative; width:64px !important; height:64px !important; border:none !important; background:var(--sellAccent); color:var(--sellOnDark); animation:ns-pop .5s cubic-bezier(.2,1.4,.4,1) both; }
.ns-draw { stroke-dasharray:1; stroke-dashoffset:1; animation:ns-trace .6s ease .35s forwards; }
.ns-confetti { position:absolute; left:50%; top:50%; width:0; height:0; }
.ns-confetti i { position:absolute; width:8px; height:12px; border-radius:2px; opacity:0; animation:ns-burst 1.4s cubic-bezier(.15,.7,.3,1) forwards; }
.ns-card-done { overflow:visible; }
.ns-dock { position:fixed; left:50%; bottom:18px; transform:translateX(-50%); z-index:5; display:flex; align-items:center; gap:14px; padding:8px 8px 8px 20px; border-radius:999px; background:var(--sellFrost); backdrop-filter:blur(18px) saturate(1.4); -webkit-backdrop-filter:blur(18px) saturate(1.4); border:1px solid var(--sellLine); box-shadow:var(--sellShadow); font-size:14px; color:var(--sellSoft); animation:ns-dock .5s cubic-bezier(.2,.7,.2,1) both; white-space:nowrap; }
.ns-dock b { color:var(--sellInk); font-weight:600; }
.ns-dock .ns-btn { padding:10px 18px; font-size:15px; }
@keyframes ns-dock { from { opacity:0; transform:translate(-50%, 24px); } to { opacity:1; transform:translate(-50%, 0); } }
.ns-tint { background:var(--sellAccentTint); border-radius:4px; padding:0 4px; color:var(--sellInk); }
.ns-draft-stage { cursor:zoom-in; position:relative; border-radius:18px; overflow:hidden; border:1px solid var(--sellLine); background:var(--sellWash); height:460px; }
.ns-draft-frame { pointer-events:none; border:0; width:100%; height:100%; display:block; background:var(--sellWash); }
.ns-draft-fade { position:absolute; left:0; right:0; bottom:0; height:180px; background:linear-gradient(transparent, var(--sellCard)); pointer-events:none; }
.ns-draft-open { white-space:nowrap; position:absolute; left:50%; bottom:22px; transform:translateX(-50%); box-shadow:var(--sellShadow); }
.ns-draft-stage:hover .ns-draft-open { transform:translate(-50%, -2px); background:var(--sellLavenderDeep); }
.ns-draft-stage:focus-visible { outline:3px solid var(--sellLavenderDeep); outline-offset:2px; }
.ns-draft-skeleton { display:flex; flex-direction:column; gap:12px; padding:22px; border-radius:18px; background:var(--sellWash); }
.ns-draft-skeleton span { height:12px; border-radius:6px; background:linear-gradient(90deg, var(--sellTrack), var(--sellAccentTint), var(--sellTrack)); background-size:200% 100%; animation:ns-shimmer 1.6s linear infinite; }
.ns-viewer { position:fixed; inset:0; z-index:20; background:rgba(31,30,36,.42); backdrop-filter:blur(6px); -webkit-backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center; padding:24px; animation:ns-up .25s ease both; }
.ns-viewer-sheet { width:min(900px, 100%); height:min(92vh, 1200px); background:var(--sellCard); border-radius:24px; overflow:hidden; display:flex; flex-direction:column; box-shadow:var(--sellShadow); }
.ns-viewer-bar { flex-shrink:0; display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-bottom:1px solid var(--sellLine); }
.ns-viewer-bar .ns-btn { padding:8px 16px; font-size:14px; }
.ns-viewer-frame { flex:1; border:0; width:100%; background:var(--sellWash); }
.ns-stage { display:grid; }
.ns-stage > * { grid-area:1 / 1; min-width:0; }
.ns-tour-step { animation:ns-tour .22s cubic-bezier(.2,.8,.2,1) both; z-index:1; }
.ns-tour-leave { animation:ns-leave .18s ease-in both; pointer-events:none; }
.ns-tour-hidden { display:none; }
.ns-unstack { animation:ns-unstack .3s cubic-bezier(.2,.8,.2,1) both; }
@keyframes ns-fadein { from { opacity:0; } to { opacity:1; } }
@keyframes ns-fadeout { to { opacity:0; } }
@keyframes ns-tour { from { opacity:0; transform:translateY(56px); } to { opacity:1; transform:none; } }
@keyframes ns-leave { to { opacity:0; transform:translateY(-28px) scale(.97); } }
@keyframes ns-unstack { from { opacity:0; transform:translateY(-36px) scale(.97); } to { opacity:1; transform:none; } }
.ns-dock-tour { gap:8px; padding:6px; }
.ns-dock-arrow { width:36px; height:36px; flex:none; border-radius:50%; border:1px solid var(--sellLine); background:var(--sellCard); color:var(--sellInk); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:transform .15s, background .15s; }
.ns-dock-arrow:hover:not(:disabled) { transform:scale(1.08); background:var(--sellLavender); }
.ns-dock-arrow:disabled { opacity:.35; cursor:default; }
.ns-segs { display:inline-flex; gap:4px; align-items:center; padding:3px; border-radius:999px; background:var(--sellTrack); }
.ns-seg-btn { position:relative; overflow:hidden; border:0; border-radius:999px; padding:8px 12px; font:inherit; font-size:13px; font-weight:500; background:transparent; color:var(--sellSoft); cursor:pointer; transition:background .2s, color .2s, padding .25s; }
.ns-seg-btn:hover { background:var(--sellCard); color:var(--sellInk); }
.ns-seg-btn.seen { color:var(--sellInk); }
.ns-seg-btn.seen::before { content:"✓ "; color:var(--sellAccent); }
.ns-seg-btn.on { background:var(--sellCharcoal); color:var(--sellOnDark); padding:8px 16px; box-shadow:0 4px 14px rgba(0,0,0,.18); }
.ns-seg-label { position:relative; z-index:1; }
.ns-seg-fill { position:absolute; left:0; bottom:0; height:3px; width:100%; background:var(--sellGlow); transform-origin:0 50%; animation:ns-dotfill linear forwards; }
@keyframes ns-dotfill { from { transform:scaleX(0); } to { transform:scaleX(1); } }
.ns-dock-tour.paused .ns-seg-btn.on { background:var(--sellAccent); }
.ns-attn { position:relative; isolation:isolate; animation:ns-attn-glow 2.4s ease-in-out infinite; }
.ns-attn::before { content:""; position:absolute; inset:-3px; border-radius:inherit; padding:3px; background:conic-gradient(from var(--ang, 0deg), transparent 0 40%, var(--sellLavenderDeep) 55%, var(--sellAccent) 75%, var(--sellGlow) 88%, transparent 100%); -webkit-mask:linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite:xor; mask-composite:exclude; animation:ns-spin-ring 2.6s linear infinite; pointer-events:none; z-index:2; filter:drop-shadow(0 0 6px var(--sellGlow)); }
@keyframes ns-attn-glow { 0%,100% { box-shadow:var(--sellShadow); } 50% { box-shadow:0 0 0 6px var(--sellLavender), var(--sellShadow); } }
.ns-viewer-title { font-weight:600; font-size:15px; color:var(--sellInk); }
.ns-viewer-sub { font-weight:500; font-size:12px; color:var(--sellAccent); background:var(--sellAccentTint); border-radius:6px; padding:2px 7px; margin-left:6px; }
@keyframes ns-word { from { opacity:0; transform:translateY(.45em); filter:blur(4px); } to { opacity:1; transform:none; filter:none; } }
@keyframes ns-drift1 { to { transform:translate(-160px, 120px) scale(1.15); } }
@keyframes ns-drift2 { to { transform:translate(180px, -140px) scale(1.2); } }
@keyframes ns-drift3 { to { transform:translate(-220px, -90px) scale(.85); } }
@keyframes ns-spin-ring { to { --ang:360deg; } }
@keyframes ns-burst { 0% { opacity:1; transform:translate(0,0) rotate(0); } 70% { opacity:1; } 100% { opacity:0; transform:translate(var(--dx), calc(var(--dy) + 80px)) rotate(var(--rot)); } }
@property --ang { syntax:"<angle>"; inherits:false; initial-value:0deg; }
@keyframes ns-rot { to { transform:rotate(360deg); } }
@keyframes ns-pop { from { transform:scale(.6); opacity:0; } to { transform:scale(1); opacity:1; } }
@keyframes ns-up { from { transform:translateY(6px); opacity:0; } to { transform:none; opacity:1; } }
@media (max-width: 640px) {
  .ns-h1 { font-size:40px; }
  .ns-banner { padding:32px 20px 24px; border-radius:24px; }
  .ns-dock { left:16px; right:16px; transform:none; justify-content:space-between; animation:none; padding:6px 6px 6px 16px; font-size:13px; }
  .ns-dock-extra { display:none; }
  .ns-viewer { padding:0; }
  .ns-viewer-sheet { height:100%; border-radius:0; padding-top:env(safe-area-inset-top); }
  .ns-draft-stage { height:400px; }
  .ns-seg-btn:not(.on) .ns-seg-label { font-size:0; }
  .ns-seg-btn:not(.on) { width:10px; height:10px; padding:0; margin:0 2px; background:var(--sellLineStrong); }
  .ns-segs { gap:2px; }
  .ns-dock-tour .ns-btn { padding:9px 12px; }
  .ns-dock-tour .ns-btn-arrow { display:none; }
  .ns-seg-btn.seen::before { content:""; }
  .ns-seg-btn.seen:not(.on) { background:var(--sellAccent); }
  .ns-dock-tour { justify-content:space-between; }
  .ns-dock .ns-btn { padding:9px 14px; font-size:14px; }
  .ns-main { padding-bottom:110px; }
  .ns-aurora span { filter:blur(50px); }
  .ns-aurora .ns-spot { display:none; }
  .ns-promises { flex-direction:column; gap:10px; }
  .ns-search-name { gap:10px; }
  .ns-hero { padding-top:6vh; }
  .ns-grid, .ns-form-grid { grid-template-columns:1fr; }
  .ns-searches li { flex-direction:column; align-items:flex-start; gap:2px; text-align:left; }
  .ns-queue-list .ns-queue { margin:6px 0 0 40px; width:150px; }
  .ns-queue-list .ns-queue .ns-queue-label { text-align:left; }
  .ns-toast { left:16px; right:16px; transform:none; bottom:16px; border-radius:18px; animation:none; }
  .ns-issuer { text-align:left; padding-left:40px; }
  .ns-steps-wrap.with-parcel { flex-direction:column-reverse; }
  .ns-parcel { width:100%; }
  .ns-parcel svg { height:140px; }
  .ns-chip { align-self:flex-start; }
  .ns-update-at { min-width:90px; }
}
@media (prefers-reduced-motion: reduce) {
  .ns-spin { animation-duration:2s; }
  .ns-candidates, .ns-step-line, .ns-done, .ns-reveal, .ns-fresh { animation:none; }
  .ns-live-dot, .ns-track-steps li.current .ns-next-n, .ns-seg.ordered { animation:none; }
  .ns-accept-attn, .ns-queue-stop, .ns-queue-fill, .ns-toast, .ns-step-ico svg, .ns-draft-skeleton span, .ns-viewer, .ns-dock, .ns-banner, .ns-aurora span, .ns-word, .ns-fade, .ns-kicker, .ns-ring, .ns-step-running, .ns-cascade li, .ns-candidate, .ns-done-celebrate { animation:none; }
  .ns-draw { animation:none; stroke-dashoffset:0; }
  .ns-tour-step, .ns-unstack { animation-name:ns-fadein; }
  .ns-tour-leave { animation-name:ns-fadeout; }
  .ns-rise { opacity:1; transform:none; transition:none; }
  .ns-tilt { transform:none !important; }
}
`;
